import {
    IDualMeet,
    IExistingFencer,
    ITeam,
    IOrganization,
    IUser,
    Strip,
    Weapon,
    DualMeetType,
    IDualMeetBout,
    BoutType,
    IDualMeetBoutFencer,
    IBoutEvent,
    RosterCSV,
    IPublicationApplication,
    IDualMeet_DB,
    IDualMeetTeam,
    ActionQueue,
    BoutSide,
    Listener,
    SchoolType,
    ICollegeEventRound,
    ICollegeEvent,
    ICollegeEventTeam,
    IDualMeetBoutFencerInfo,
    Lineup,
    ICollegeEventRoundMeet,
    ICoachInvite,
    DualMeetSignatureKey,
    CollegeProgramType,
    IFencerRecord,
    IBoutCore,
    RosterRecords,
    IOrganizationEdit,
    IDualMeet_Base,
    ID,
    IFencerRanking,
    IOverallRankings,
    ITournament,
    ITournamentEvent,
    ITournamentRound,
    ITournamentEventBase,
    ITournamentEventTeam,
    ITournamentEventCBA,
    ITournamentPoolCBA,
    ITournamentPool,
    ITournamentEventIndividual,
    ITournamentRoundPoolCBA,
    ITournamentEventTeamResult,
    ITournamentEventFencerResult,
    ITournamentEventFencer,
    SeedStatus,
    RosterWeapon,
    IDirectoryDualMeet,
    UserFlag,
    IFencerInvite,
    BoutEventType,
    IDualMeetDispute,
    IDualMeetDisputeStatus,
    IDirectoryTeam,
    PoolResults,
    IPowerPointsStats,
    ISPIResult,
    ITeamSPIResult,
    ISquadSPIResult,
    ICollegeEventResults,
    ICollegeEventFencerResult,
    ICollegeEventResult,
    LinkedTeamType,
    FencerCalculationData,
    UsfaRatingsByWeapon,
    IHighSchoolBoutReportData
} from "../types";
import fire, { firebase } from "./firebaseSetup";
import {
    genRandomColor,
    getRandomInt,
    boutWinner,
    genAbbreviationFromName,
    genRandomStr,
    incrementString,
    getMeetsFromEvent,
    computePoolResults,
    GenPoolOrder,
    sortPoolResults,
    normalizeApostrophes
} from "./helpers";
import { compress, decompress } from "lz-string";
import { meetsRawEqual } from "./equality";
import CacheService from "./cache/cacheService";
import ListenerManager from "./cache/listenerManager";
import VersionService from "./cache/versionService";
import {
    ERROR_ACCOUNT_ISSUE,
    ERROR_ADDING_LINEUPS,
    ERROR_BOUT_DOES_NOT_EXIST,
    ERROR_BOUT_EVENT_DOES_NOT_EXIST,
    ERROR_CANNOT_MOVE_MEET,
    ERROR_DUAL_MEET_DOES_NOT_EXIST,
    ERROR_EVENT_DOES_NOT_EXIST,
    ERROR_FENCER_DOES_NOT_EXIST,
    ERROR_FENCER_RECORDS_DOES_NOT_EXIST,
    ERROR_INVALID_ID,
    ERROR_INVALID_INVITE,
    ERROR_INVITE_CLAIMED,
    ERROR_MEET_NOT_IN_SCHEDULE,
    ERROR_NOT_LOGGED_IN,
    ERROR_PROCESSING_MEET,
    ERROR_SCHOOL_DOES_NOT_EXIST,
    ERROR_TEAM_DOES_NOT_EXIST,
    ERROR_UNKNOWN,
    ERROR_USER_DOES_NOT_EXIST,
    GROWN_UP_STR
} from "./constants";
import { GetTeamOptions, ManualBoutUpdateRequest } from "./database_types";
import { CURRENT_SEASON_STR } from "./season";
import { INotification } from "../types";
import { Conference } from "./ncaaConference";

// Fallback static conferences (used if Firebase fetch fails or hasn't completed)
const FALLBACK_HIGH_SCHOOL_CONFERENCES: Conference[] = [
    { name: "Burlington County Scholastic League", id: "BCSL" },
    { name: "Big North Conference", id: "BNC" },
    { name: "Cape Atlantic League", id: "CAL" },
    { name: "Colonial Conference", id: "CC" },
    { name: "Colonial Valley Conference", id: "CVC" },
    { name: "Greater Middlesex Conference", id: "GMC" },
    { name: "Hudson County Interscholastic Athletic League", id: "HCIAL" },
    { name: "Mid-Atlantic Prep League", id: "MAPL" },
    { name: "Northwest Jersey Athletic Conference", id: "NJAC" },
    { name: "New Jersey Association of Independent Schools", id: "NJAIS" },
    { name: "North Jersey Interscholastic Conference", id: "NJIC" },
    { name: "Olympic Conference", id: "OC" },
    { name: "Shore Conference", id: "SC" },
    { name: "Super Essex Conference", id: "SEC" },
    { name: "Skyland Conference", id: "SKY" },
    { name: "Tri County Conference", id: "TCC" },
    { name: "Union County Conference", id: "UCC" }
];

const MIDDLE_SCHOOL_CONFERENCES: Conference[] = [
    { name: "NJ Middle School Winter League", id: "NJMSWL" },
    { name: "The Chimera League of Youth Fencing Teams", id: "CLYFT" }
];

// Global array to store conferences (initialized with fallback)
export const HighSchoolConferences: Conference[] = [...FALLBACK_HIGH_SCHOOL_CONFERENCES];

// Global variable to store conferences as a record for quick lookup (initialized with fallback)
export let HighSchoolConferencesRecord: Record<string, Conference> = {};
FALLBACK_HIGH_SCHOOL_CONFERENCES.forEach(conf => {
    HighSchoolConferencesRecord[conf.id] = conf;
});

type Database = firebase.database.Database;
type Authentication = firebase.auth.Auth;
type Functions = firebase.functions.Functions;

export class DBSuccess<T> {
    public readonly status = "success";
    constructor(public data: T) {}
}
export class DBError<R> {
    public readonly status = "fail";
    constructor(public data: R) {}
}
export type DBResult<T = void, R = string> = DBSuccess<T> | DBError<R>;
// We need this to prevent TS from whining
const AsyncDBResult = Promise;
export type AsyncDBResult<T = void, R = string> = Promise<DBResult<T, R>>;

export const isSuccess = <T = void, R = string>(result: DBResult<T, R>): result is DBSuccess<T> => {
    return result.status === "success";
};
export const isFailure = <T = void, R = string>(result: DBResult<T, R>): result is DBError<R> => {
    return result.status === "fail";
};

/**
 * Helper function for calling a database ref
 */
export function refOnce<T>(ref: string): Promise<T> {
    return new Promise(res =>
        fire
            .database()
            .ref(ref)
            .once("value", v => {
                res(v.val());
            })
    );
}

export function refMultiple<T>(ref: string, listener: (val: T) => void): Promise<T> {
    return new Promise(res =>
        fire
            .database()
            .ref(ref)
            .on("value", v => {
                const val = v.val();
                listener(val);
                res(val);
            })
    );
}

export function removeRefListener(ref: string, listener?: (val: unknown) => void) {
    fire.database().ref(ref).off("value", listener);
}

export const DEFAULT_USER: IUser = {
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    createdAt: 0,
    updatedAt: 0,
    verificationPin: 99999,
    flags: 0,
    teams: [],
    managingTeams: [],
    linkedFencerIds: []
};

export const DEFAULT_TEAM: ITeam = {
    id: "",
    administrators: [],
    managers: [],
    linkedUsers: {},
    boysAndGirlsTeam: "both",
    countryCode: "US",
    region: "",
    color: "#FFF",
    createdBy: "",
    createdAt: 0,
    dualMeets: {},
    fencers: [],
    published: false,
    name: "",
    pin: 99999,
    roster: {},
    seasons: [],
    lastAccessedSeason: CURRENT_SEASON_STR,
    type: SchoolType.HS
};

export const DEFAULT_ORGANIZATION: IOrganization = {
    id: "",
    name: "",
    administrators: [],
    countryCode: "US",
    region: "",
    color: "#FFF",
    createdBy: "",
    createdAt: 0,
    published: false,
    type: SchoolType.HS
};

export const DEFAULT_BOUT: Omit<IDualMeetBout, "fencer1" | "fencer2"> = {
    id: "",
    dualMeetId: "",
    color: "",
    currentSpectatorsNum: 0,
    log: [],
    order: 0,
    pin: 0,
    weapon: "Sabre",
    type: BoutType.DualMeet,
    switchedSides: false,
    priority: null,
    boutTime: 180000,
    startedAt: 0,
    endedAt: 0,
    updatedAt: 0
};

export const DEFAULT_EVENT: ICollegeEvent = {
    address: "",
    createdAt: new Date(),
    createdBy: "",
    hostName: "",
    id: "",
    location: "",
    mensRounds: [],
    mensTeams: [],
    name: "",
    published: false,
    refereePin: 12345,
    season: "",
    startedAt: new Date(),
    womensRounds: [],
    womensTeams: []
};

export const DEFAULT_TOURNAMENT: ITournament = {
    id: "",
    name: "",
    createdAt: 0,
    createdBy: "",
    startedAt: 0,
    season: CURRENT_SEASON_STR,
    events: {},
    location: {
        name: "",
        city: "",
        state: ""
    }
};

export const DEFAULT_TOURNAMENT_EVENT: ITournamentEvent = {
    id: "",
    name: "",
    gender: "both",
    weapon: "Sabre",
    startedAt: Date.now(),
    updatedAt: Date.now(),
    type: "cba",
    teams: [],
    rounds: [],
    tournamentID: ""
};

/**
 * Extends `DB_BASE` with caching features. Fully public
 */
export class DB_V2 {
    public PREFIX = "/test";
    public COMMON_PREFIX = "/common";

    protected database: Database;
    protected auth: Authentication;
    protected functions: Functions;

    public version: VersionService;

    private cache: CacheService;
    get teamDirectory() {
        return this.cache.teamDirectory;
    }
    get teams() {
        return this.cache.teams;
    }
    get fencers() {
        return this.cache.fencers;
    }
    get organizations() {
        return this.cache.organizations;
    }
    get dualMeets() {
        return this.cache.dualMeets;
    }
    get dualMeetBouts() {
        return this.cache.dualMeetBouts;
    }
    get boutEvents() {
        return this.cache.boutEvents;
    }
    get tournaments() {
        return this.cache.tournaments;
    }
    get tournamentEvents() {
        return this.cache.tournamentEvents;
    }
    get tournamentRounds() {
        return this.cache.tournamentRounds;
    }
    get tournamentPools() {
        return this.cache.tournamentPools;
    }
    get collegeEventResults() {
        return this.cache.collegeEventResults;
    }

    private listeners: ListenerManager;

    public constructor(prefix: string = "/test", db: Database, auth: Authentication, functions: Functions) {
        this.PREFIX = prefix;
        this.database = db;
        this.auth = auth;
        this.functions = functions;

        this.cache = new CacheService(prefix);

        this.listeners = new ListenerManager();

        this.version = new VersionService(db, prefix);
    }

    private getWeaponRatingFromFencer(fencer: IExistingFencer, weapon: Weapon): string | undefined {
        const direct =
            weapon === "Sabre"
                ? fencer.ratingSabre
                : weapon === "Foil"
                ? fencer.ratingFoil
                : weapon === "Epee"
                ? fencer.ratingEpee
                : undefined;
        if (direct && direct.trim()) return direct.trim();
        const allEmpty = !fencer.ratingSabre && !fencer.ratingFoil && !fencer.ratingEpee;
        if (allEmpty && typeof fencer.usfaRating === "string" && fencer.usfaRating.trim()) {
            return fencer.usfaRating.trim();
        }
        return undefined;
    }

    private setWeaponRatingOnFencer(
        fencer: IExistingFencer,
        weapon: Weapon,
        rating?: string | null
    ): IExistingFencer {
        const trimmed = rating?.trim();
        if (weapon === "Sabre") {
            if (trimmed) fencer.ratingSabre = trimmed;
            else delete fencer.ratingSabre;
        } else if (weapon === "Foil") {
            if (trimmed) fencer.ratingFoil = trimmed;
            else delete fencer.ratingFoil;
        } else if (weapon === "Epee") {
            if (trimmed) fencer.ratingEpee = trimmed;
            else delete fencer.ratingEpee;
        }
        return fencer;
    }

    /**
     * Fetches high school conferences from Firebase and returns them as a record of id to Conference.
     * Stores data at /v2/conferences in Firebase.
     */
    public async fetchHighSchoolConferences(): AsyncDBResult<Record<string, Conference>> {
        try {
            const conferencesRef = this.database.ref("/v2/conferences");
            const snapshot = await conferencesRef.once("value");
            const conferences = snapshot.val();

            if (conferences && typeof conferences === "object") {
                return new DBSuccess(conferences);
            }
            // If Firebase doesn't have data yet, return empty object
            return new DBSuccess({} as Record<string, Conference>);
        } catch (error) {
            return new DBError((error as Error).message);
        }
    }

    /**
     * Initializes conferences from Firebase and stores them in the global cache.
     * On the middle school site, uses the static MS conferences instead.
     * Falls back to static conferences if Firebase fetch fails.
     */
    public async initConferences(): Promise<void> {
        if (this.PREFIX === "/ms") {
            HighSchoolConferences.length = 0;
            HighSchoolConferences.push(...MIDDLE_SCHOOL_CONFERENCES);
            HighSchoolConferencesRecord = {};
            MIDDLE_SCHOOL_CONFERENCES.forEach(conf => {
                HighSchoolConferencesRecord[conf.id] = conf;
            });
            return;
        }

        const result = await this.fetchHighSchoolConferences();

        if (isSuccess(result) && result.data && Object.keys(result.data).length > 0) {
            HighSchoolConferencesRecord = result.data;
            // Update the array by clearing and repopulating
            HighSchoolConferences.length = 0;
            HighSchoolConferences.push(...Object.values(HighSchoolConferencesRecord));
        }
        // If Firebase doesn't have data yet, keep using the fallback that was initialized
    }

    public async updateDatabase(updates: Record<string, unknown>): AsyncDBResult<void> {
        try {
            await this.database.ref().update(updates);
            return new DBSuccess(void 0);
        } catch (e) {
            return new DBError((e as Error).message);
        }
    }

    // #region Users

    /**
     * Add newly created user to db
     */
    public createUser(uid: string, firstName: string, lastName: string, email: string): AsyncDBResult<IUser> {
        return COMMON_DB.createUser(uid, firstName, lastName, email);
    }

    /**
     * Takes a filled out `IUser` and adds to `/v2` (migration purposes only)
     */
    public async migrateAccount(user: IUser): Promise<boolean> {
        await this.database.ref(`${this.PREFIX}/users/${user.id}`).set(user);
        return true;
    }

    /**
     * Get info of the current logged in user
     */
    public getCurrentUserInfo(listener?: Listener<DBResult<IUser>>): AsyncDBResult<IUser> {
        return COMMON_DB.getCurrentUserInfo(listener);
    }

    public getUserInfo(userId: string): AsyncDBResult<IUser> {
        return COMMON_DB.getUserInfo(userId);
    }

    /**
     * Links a fencer to a user in the database. Put `null` as `fencerID` to unlink.
     */
    public async linkUserToFencer(uid: string, fencerID: string): AsyncDBResult<null> {
        const linkUserToFencer = this.functions.httpsCallable("linkUserToFencer");
        // Map /test to /v2 for backend compatibility (frontend uses /test, backend uses /v2)
        const backendPrefix = this.PREFIX === "/test" ? "/v2" : this.PREFIX;
        const result = await linkUserToFencer({ fencerID, userId: uid, prefix: backendPrefix });
        return result.data;
    }

    public getUserList(listener?: Listener<DBResult<Record<string, IUser>>>): AsyncDBResult<Record<string, IUser>> {
        return COMMON_DB.getUserList(listener);
    }

    public stopListeningUserList() {
        return COMMON_DB.stopListeningUserList();
    }

    /**
     * Get all users with their linked teams and fencers resolved
     * Returns users with team names and fencer names populated
     */
    public async getAllUsersWithLinkedInfo(): AsyncDBResult<
        Array<{
            user: IUser;
            teams: Array<{ id: ID; name: string; type: "team" | "managing" | "linked"; linkedType?: LinkedTeamType }>;
            fencers: Array<{ id: ID; name: string }>;
        }>
    > {
        try {
            // Get all users, teams, and fencers
            const [usersResult, teamsResult, fencersResult] = await Promise.all([
                this.getUserList(),
                this.getTeamList(),
                this.getAllFencers()
            ]);

            if (usersResult.status === "fail") {
                return new DBError(usersResult.data);
            }
            if (teamsResult.status === "fail") {
                return new DBError(teamsResult.data);
            }
            if (fencersResult.status === "fail") {
                return new DBError(fencersResult.data);
            }

            const users = usersResult.data;
            const teams = teamsResult.data;
            const fencers = fencersResult.data;

            // Build result array
            const result = Object.values(users).map(user => {
                const userTeams: Array<{ id: ID; name: string; type: "team" | "managing" | "linked"; linkedType?: LinkedTeamType }> = [];

                // Add teams from teams array
                user.teams.forEach(teamId => {
                    const team = teams[teamId];
                    if (team) {
                        userTeams.push({
                            id: teamId,
                            name: team.name,
                            type: "team"
                        });
                    }
                });

                // Add managing teams
                user.managingTeams.forEach(teamId => {
                    const team = teams[teamId];
                    if (team) {
                        userTeams.push({
                            id: teamId,
                            name: team.name,
                            type: "managing"
                        });
                    }
                });

                // Add linked teams
                if (user.linkedTeams) {
                    Object.keys(user.linkedTeams).forEach(teamId => {
                        const team = teams[teamId];
                        if (team) {
                            userTeams.push({
                                id: teamId,
                                name: team.name,
                                type: "linked",
                                linkedType: user.linkedTeams![teamId]
                            });
                        }
                    });
                }

                // Get fencers
                const userFencers = user.linkedFencerIds
                    .map(fencerId => {
                        const fencer = fencers[fencerId];
                        if (fencer) {
                            return {
                                id: fencerId,
                                name: `${fencer.firstName} ${fencer.lastName}`
                            };
                        }
                        return null;
                    })
                    .filter((f): f is { id: ID; name: string } => f !== null);

                return {
                    user,
                    teams: userTeams,
                    fencers: userFencers
                };
            });

            return new DBSuccess(result);
        } catch (error) {
            return new DBError(ERROR_UNKNOWN);
        }
    }

    // #endregion

    // #region Teams

    public async getTeamDirectory(): AsyncDBResult<Record<string, IDirectoryTeam>> {
        const teamDirectoryUpdatedAt = (await this.database.ref(`${this.PREFIX}/teamDirectoryUpdatedAt`).once("value")).val();
        const userInfo = await this.getCurrentUserInfo();
        const userCannotSeeUnpublished = userInfo.status === "fail" || !(userInfo.data.flags & UserFlag.SuperAdmin);
        if (teamDirectoryUpdatedAt > this.teamDirectory.updatedAt) {
            const query = this.database.ref(`${this.PREFIX}/teamDirectory`);
            const v = await query.once("value");
            const results: Record<string, IDirectoryTeam> = v.val() || {};
            this.teamDirectory.updatedAt = teamDirectoryUpdatedAt;
            this.teamDirectory.directory = results;
            this.cache.queueSave("teamDirectory");
            if (userCannotSeeUnpublished) {
                for (const i in results) {
                    if (!results[i].published) delete results[i];
                }
            }
            return new DBSuccess(results);
        } else {
            const newDirectory: Record<string, IDirectoryTeam> = {};
            for (const i in this.teamDirectory.directory) {
                if (userCannotSeeUnpublished && !this.teamDirectory.directory[i].published) continue;
                newDirectory[i] = this.teamDirectory.directory[i];
            }
            return new DBSuccess(newDirectory);
        }
    }

    /**
     * Get record of team ID to team data
     * NOTE: Potentially expensive as it might get all the data for all teams, not just IDs
     */
    public async getTeamList({
        listener,
        forceCache
    }: { listener?: Listener<DBResult<Record<string, ITeam>>>; forceCache?: boolean } = {}): AsyncDBResult<Record<string, ITeam>> {
        if (listener) {
            return new Promise(async res => {
                let found = false;
                if (this.teams) {
                    found = true;
                    res(new DBSuccess(this.teams));
                }
                const l = v => {
                    const teams: Record<string, ITeam> = v.val() || {};
                    for (const i in teams) {
                        const val = { ...DEFAULT_TEAM, ...teams[i] };
                        teams[i] = val;
                        this.teams[i] = val;
                    }
                    listener(new DBSuccess(teams));
                    if (!found) {
                        res(new DBSuccess(teams));
                    }
                };
                this.listeners.set(listener, l);
                this.database.ref(`${this.PREFIX}/teams`).on("value", l);
            });
        } else {
            if (forceCache && Object.keys(this.teams).length) {
                return new DBSuccess(this.teams);
            }
            const teams = (await refOnce<Record<string, ITeam>>(`${this.PREFIX}/teams`)) || {};
            for (const i in teams) {
                const val = { ...DEFAULT_TEAM, ...teams[i] };
                teams[i] = val;
                this.teams[i] = val;
            }
            return new DBSuccess(teams);
        }
    }

    public stopListeningTeamList(listener?: (val: unknown) => void) {
        const l = this.listeners.get(listener);
        if (!l) return;
        removeRefListener(`${this.PREFIX}/teams`, l);
    }

    /**
     * Get team by ID
     * @param {string} teamID ID of team to get data for
     * @param {GetTeamOptions} [options] Optional configuration options for data retrieval
     */
    public async getTeam(teamID: string, options?: GetTeamOptions): AsyncDBResult<ITeam> {
        if (!teamID || typeof teamID !== "string") return new DBError(ERROR_TEAM_DOES_NOT_EXIST);
        const listener = options?.listener || undefined;
        const forceCache = options?.forceCache || false;

        if (forceCache) {
            if (this.teams[teamID]) return new DBSuccess(this.teams[teamID]);
        }

        const handleTeam = (va: Partial<ITeam>) => {
            const val: ITeam = {
                ...DEFAULT_TEAM,
                id: teamID,
                seasons: Object.keys(va.roster || {}),
                ...va
            };
            for (const i in val.dualMeets) {
                val.dualMeets[i] = val.dualMeets[i].filter(Boolean);
            }

            // Correction for missing fencers
            const databaseUpdates: Record<string, unknown> = {};
            const rosterFencers = Object.values(val.roster)
                .flatMap(l => Object.values(l))
                .flatMap(l => Object.keys(l));
            const baseFencers = (val.fencers || []).filter((f: string | null | undefined) => Boolean(f));
            for (const fencerID of rosterFencers) {
                if (!baseFencers.includes(fencerID)) {
                    baseFencers.push(fencerID);
                }
            }
            if (baseFencers.length !== (val.fencers || []).length || (val.fencers || []).some((f: string | null | undefined) => !f)) {
                databaseUpdates[`${this.PREFIX}/teams/${teamID}/fencers`] = baseFencers;
                val.fencers = baseFencers;
            }
            if (Object.keys(databaseUpdates).length) {
                this.database.ref().update(databaseUpdates);
            }
            return val;
        };

        if (listener) {
            return new Promise(async res => {
                let found = false;
                if (this.teams[teamID]) {
                    found = true;
                    res(new DBSuccess(this.teams[teamID]));
                }
                const l = v => {
                    const va = v.val();
                    if (!va) {
                        const response = new DBError(ERROR_TEAM_DOES_NOT_EXIST);
                        listener(response);
                        return res(response);
                    }
                    const val: ITeam = handleTeam(va);
                    for (const season of val.seasons) {
                        if (!(season in val.roster)) {
                            val.roster[season] = {
                                Sabre: {},
                                Foil: {},
                                Epee: {},
                                Armorer: {},
                                Unsorted: {}
                            };
                        }
                    }
                    this.teams[teamID] = val;
                    listener(new DBSuccess(val));
                    if (!found) {
                        res(new DBSuccess(val));
                    }
                };
                this.listeners.set(listener, l);
                this.database.ref(`${this.PREFIX}/teams/${teamID}`).on("value", l);
            });
        } else {
            const v = await this.database.ref(`${this.PREFIX}/teams/${teamID}`).once("value");
            const teamData = (v.val() as Partial<ITeam>) || null;
            if (!teamData) {
                return new DBError(ERROR_TEAM_DOES_NOT_EXIST);
            }
            const val: ITeam = handleTeam(teamData);
            for (const season of val.seasons) {
                if (!(season in val.roster)) {
                    val.roster[season] = { Sabre: {}, Foil: {}, Epee: {}, Armorer: {}, Unsorted: {} };
                }
            }
            this.teams[teamID] = val;
            this.cache.queueSave("teams");
            return new DBSuccess(val);
        }
    }

    public stopListeningTeam(id: string, listener?: (val: unknown) => void) {
        const l = this.listeners.get(listener);
        if (!l) return;
        removeRefListener(`${this.PREFIX}/teams/${id}`, l);
    }

    public async getOrganizationFromTeam(teamID: string): AsyncDBResult<IOrganization> {
        const v = await this.database.ref(`${this.PREFIX}/teams/${teamID}`).once("value");
        const dbVal = v.val() as ITeam | null;
        if (dbVal === null) {
            return new DBError(ERROR_TEAM_DOES_NOT_EXIST);
        } else if (!dbVal.orgID) {
            const orgList = await this.getOrganizationList();
            for (const orgID in orgList) {
                if (orgList[orgID].boysTeam === teamID || orgList[orgID].girlsTeam === teamID) {
                    this.database.ref(`${this.PREFIX}/teams/${teamID}/orgID`).set(orgID);
                    return this.getOrganization(orgID);
                }
            }
            return new DBError(ERROR_SCHOOL_DOES_NOT_EXIST);
        } else {
            return this.getOrganization(dbVal.orgID);
        }
    }

    /**
     * Delete a team based on ID
     */
    public async deleteTeam(teamID: string): AsyncDBResult<boolean> {
        const team = await this.getTeam(teamID);

        if (team.status === "fail") {
            return team;
        }

        if (Object.keys(team.data.dualMeets).length) {
            const meets = Object.values(team.data.dualMeets).flat();
            const mapped = await Promise.all(meets.map(l => refOnce<IDualMeet_DB | null>(`${this.PREFIX}/dualMeets/${l}`)));
            const valid = mapped.filter(Boolean);
            console.log(valid);
            if (valid.length) {
                return new DBError("Cannot delete a team with any dual meets.");
            }
        }

        const databaseDeletions: Record<string, unknown> = {};

        databaseDeletions[`${this.PREFIX}/teams/${teamID}`] = null;
        databaseDeletions[`${this.PREFIX}/teamDirectory/${teamID}`] = null;
        databaseDeletions[`${this.PREFIX}/teamDirectoryUpdatedAt`] = Date.now();

        for (const user of team.data.administrators) {
            try {
                const userData = await this.getUserInfo(user);
                if (userData.status === "fail") return userData;
                databaseDeletions[`${this.COMMON_PREFIX}/users/${user}/teams`] = userData.data.teams.filter(l => l !== teamID);
                databaseDeletions[`${this.COMMON_PREFIX}/users/${user}/linkedFencerId`] = null;
            } catch (e) {
                console.warn(`User ${user} no longer exists -- skipping`);
            }
        }

        for (const fencer of team.data.fencers) {
            databaseDeletions[`${this.PREFIX}/fencers/${fencer}`] = null;
        }

        const organizations = await this.getOrganizationList();
        for (const organization of Object.values(organizations.data)) {
            if (organization.boysTeam === teamID) {
                if (!organization.girlsTeam && !organization.girlsJVTeam && !organization.boysJVTeam) {
                    databaseDeletions[`${this.PREFIX}/organizations/${organization.id}`] = null;
                } else {
                    databaseDeletions[`${this.PREFIX}/organizations/${organization.id}/boysTeam`] = null;
                }
            }
            if (organization.girlsTeam === teamID) {
                if (!organization.boysTeam && !organization.girlsJVTeam && !organization.boysJVTeam) {
                    databaseDeletions[`${this.PREFIX}/organizations/${organization.id}`] = null;
                } else {
                    databaseDeletions[`${this.PREFIX}/organizations/${organization.id}/girlsTeam`] = null;
                }
            }
            if (organization.boysJVTeam === teamID) {
                if (!organization.girlsJVTeam && !organization.girlsTeam && !organization.boysTeam) {
                    databaseDeletions[`${this.PREFIX}/organizations/${organization.id}`] = null;
                } else {
                    databaseDeletions[`${this.PREFIX}/organizations/${organization.id}/boysJVTeam`] = null;
                }
            }
            if (organization.girlsJVTeam === teamID) {
                if (!organization.boysJVTeam && !organization.girlsTeam && !organization.boysTeam) {
                    databaseDeletions[`${this.PREFIX}/organizations/${organization.id}`] = null;
                } else {
                    databaseDeletions[`${this.PREFIX}/organizations/${organization.id}/girlsJVTeam`] = null;
                }
            }
        }

        await this.database.ref().update(databaseDeletions);

        return new DBSuccess(true);
    }

    public async updateTeamConference(teamID: string, conference: string[] | string): AsyncDBResult<boolean> {
        const conferenceAsList: string[] = typeof conference === "string" ? [conference] : conference;

        const databaseAdditions = {};
        databaseAdditions[`${this.PREFIX}/teams/${teamID}/conference`] = conferenceAsList;

        await this.database.ref().update(databaseAdditions);

        return new DBSuccess(true);
    }

    public async updateTeamSection(teamID: string, section: "north" | "south"): AsyncDBResult<boolean> {
        const databaseAdditions = {};
        databaseAdditions[`${this.PREFIX}/teams/${teamID}/section`] = section;

        await this.database.ref().update(databaseAdditions);

        return new DBSuccess(true);
    }

    // TODO: refactor functions like this and check for gaps
    public async leaveTeam(teamID: string): AsyncDBResult<boolean> {
        const user = await this.getCurrentUserInfo();
        if (user.status === "fail") return new DBError(ERROR_NOT_LOGGED_IN);
        let administrators = (await refOnce<ITeam["administrators"]>(`${this.PREFIX}/teams/${teamID}/administrators`)) || [];
        const managers = (await refOnce<ITeam["managers"]>(`${this.PREFIX}/teams/${teamID}/managers`)) || [];
        administrators = administrators.filter(l => l !== user.data.id);
        managers.splice(managers.indexOf(user.data.id), 1);
        await this.database.ref(`${this.PREFIX}/teams/${teamID}`).update({ administrators, managers });
        await this.database.ref(`${this.COMMON_PREFIX}/users/${user.data.id}`).update({
            teams: [...user.data.teams.filter(l => l !== teamID)],
            managingTeams: [...user.data.managingTeams.filter(l => l !== teamID)]
        });
        return new DBSuccess(true);
    }

    public async convertTeam(id: string, type: string): AsyncDBResult<boolean> {
        const updates = {};
        updates[`${this.PREFIX}/teams/${id}/region`] = type;
        await this.database.ref().update(updates);
        return new DBSuccess(true);
    }

    public async createTeamAttachedToOrg(
        orgId: string,
        teamName: string,
        teamAbb: string,
        countryCode: string,
        region: string,
        teamGender: "boysTeam" | "girlsTeam" | "boysJVTeam" | "girlsJVTeam" | "both",
        type: SchoolType,
        { published, publishedAt, publishedBy }: { published: boolean; publishedAt?: number; publishedBy?: string },
        conference?: string[] | string
    ): AsyncDBResult<string> {
        const user = this.auth.currentUser;

        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);

        const d = Date.now();
        const databaseAdditions = {};
        const teamPIN = getRandomInt(10000, 99999);
        const teamID = (await this.database.ref(`${this.PREFIX}/teams`).push()).key!;
        const userData = await refOnce<IUser>(`${this.COMMON_PREFIX}/users/${user.uid}`);

        if (!userData) return new DBError(ERROR_ACCOUNT_ISSUE);

        const normalizedTeamName = normalizeApostrophes(teamName).trim();
        const gender = teamGender === "both" ? "both" : teamGender.startsWith("boys") ? "boys" : "girls";
        const newTeam: ITeam = {
            id: teamID,
            administrators: [user.uid],
            abbreviation: teamAbb,
            managers: [],
            linkedUsers: {},
            fencers: [],
            boysAndGirlsTeam: gender,
            countryCode: countryCode,
            region: region,
            color: `rgba(${getRandomInt(0, 100)}, ${getRandomInt(0, 230)}, ${getRandomInt(0, 255)}, ${getRandomInt(40, 80) / 100})`,
            createdAt: d,
            createdBy: user.uid,
            dualMeets: {},
            published,
            name: normalizedTeamName,
            pin: teamPIN,
            roster: {},
            seasons: [CURRENT_SEASON_STR],
            orgID: orgId,
            type,
            lastAccessedSeason: CURRENT_SEASON_STR
        };

        const newDirectoryTeam: IDirectoryTeam = {
            name: normalizedTeamName,
            abbreviation: teamAbb,
            gender,
            orgID: orgId,
            published
        };

        if (published && publishedAt) newTeam.publishedAt = publishedAt;
        if (published && publishedBy) newTeam.publishedBy = publishedBy;
        if (conference) {
            newTeam.conference = typeof conference === "string" ? [conference] : conference;
        }

        databaseAdditions[`${this.PREFIX}/teams/${teamID}`] = newTeam;
        databaseAdditions[`${this.PREFIX}/organizations/${orgId}/${teamGender}`] = newTeam.id;
        databaseAdditions[`${this.PREFIX}/teamDirectory/${teamID}`] = newDirectoryTeam;
        databaseAdditions[`${this.PREFIX}/teamDirectoryUpdatedAt`] = Date.now();

        databaseAdditions[`${this.COMMON_PREFIX}/users/${user.uid}`] = {
            ...userData,
            teams: [...(userData.teams || []), teamID]
        };
        await this.database.ref().update(databaseAdditions);

        return new DBSuccess(teamID);
    }

    /**
     * Delete a season in a team
     */
    public async deleteTeamSeason(teamID: string, seasonName: string): AsyncDBResult<boolean> {
        const user = this.auth.currentUser;

        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);

        const team = await this.getTeam(teamID);

        if (team.status === "fail") {
            return team;
        }

        const databaseDeletions = {};

        databaseDeletions[`${this.PREFIX}/teams/${teamID}/roster/${seasonName}`] = null;
        databaseDeletions[`${this.PREFIX}/teams/${teamID}/roster/${team.data.seasons.indexOf(seasonName)}`] = null;
        databaseDeletions[`${this.PREFIX}/teams/${teamID}/seasons`] = team.data.seasons.filter(l => l !== seasonName);

        await this.database.ref().update(databaseDeletions);

        return new DBSuccess(true);
    }

    public async getTeamRosterAsCSV(teamID: string, season: string): AsyncDBResult<string> {
        const team = await this.getTeam(teamID);
        if (team.status === "fail") {
            return team;
        }

        const teamMembers = team.data.roster[season];
        const sabreRoster = (await Promise.all(
            Object.keys(teamMembers?.Sabre || {}).map(this.getFencer.bind(this))
        )) as DBResult<IExistingFencer>[];
        const foilRoster = (await Promise.all(
            Object.keys(teamMembers?.Foil || {}).map(this.getFencer.bind(this))
        )) as DBResult<IExistingFencer>[];
        const epeeRoster = (await Promise.all(
            Object.keys(teamMembers?.Epee || {}).map(this.getFencer.bind(this))
        )) as DBResult<IExistingFencer>[];

        let csv = "First name,Last name,Graduation year,Weapon,Power Rating,USFA Rating,Home strip,Away strip";

        for (const result of sabreRoster) {
            if (result.status === "fail") return result;
            const fencer = result.data;
            const rating = this.getWeaponRatingFromFencer(fencer, "Sabre") || "";
            csv += `\n${fencer.firstName},${fencer.lastName},${fencer.gradYear},Sabre,${fencer.powerRatingSabre || ""},${rating},${teamMembers.Sabre[fencer.id].home || "Sub"},${teamMembers.Sabre[fencer.id].away || "Sub"}`;
        }
        for (const result of foilRoster) {
            if (result.status === "fail") return result;
            const fencer = result.data;
            const rating = this.getWeaponRatingFromFencer(fencer, "Foil") || "";
            csv += `\n${fencer.firstName},${fencer.lastName},${fencer.gradYear},Foil,${fencer.powerRatingFoil || ""},${rating},${teamMembers.Foil[fencer.id].home || "Sub"},${teamMembers.Foil[fencer.id].away || "Sub"}`;
        }
        for (const result of epeeRoster) {
            if (result.status === "fail") return result;
            const fencer = result.data;
            const rating = this.getWeaponRatingFromFencer(fencer, "Epee") || "";
            csv += `\n${fencer.firstName},${fencer.lastName},${fencer.gradYear},Epee,${fencer.powerRatingEpee || ""},${rating},${teamMembers.Epee[fencer.id].home || "Sub"},${teamMembers.Epee[fencer.id].away || "Sub"}`;
        }

        return new DBSuccess(csv);
    }

    public async getRosterRecords(
        teamId: string,
        season: string
    ): AsyncDBResult<Record<Weapon, Record<string, { wins: number; losses: number }>>> {
        const debugTime = false;

        debugTime && console.time("Getting roster records...");
        debugTime && console.time("Getting team data...");
        const team = await this.getTeam(teamId);
        debugTime && console.timeEnd("Getting team data...");

        if (team.status === "fail") {
            return new DBError(ERROR_TEAM_DOES_NOT_EXIST);
        }

        try {
            debugTime && console.time("Getting meet data...");
            const meets: string[] = team.data.dualMeets[season] || [];
            // const meetsData: IDualMeet[] = await Promise.all(meets.map(l => this.getDualMeet(l)));
            const meetsDataRaw = await Promise.allResolved(meets.map(meet => this.getDualMeetRaw(meet, undefined, true)));
            const meetsData = meetsDataRaw.filter(isSuccess).map(l => l.data);

            const boutIDs: string[][] = [];
            for (const meet of meetsData) {
                boutIDs.push(meet.bouts.filter(Boolean).map(l => l.id));
            }
            debugTime && console.timeEnd("Getting meet data...");

            debugTime && console.time("Getting bout data...");
            const bouts: IDualMeetBout[][] = await Promise.all(
                boutIDs.map(meet =>
                    Promise.allResolved<DBResult<IDualMeetBout>>(meet.map(l => this.getBout(l))).then(l =>
                        l.filter(isSuccess).map(j => j.data)
                    )
                )
            );
            debugTime && console.timeEnd("Getting bout data...");

            debugTime && console.time("Processing bouts...");
            const teamRecords: Record<Weapon, Record<string, { wins: number; losses: number }>> = {
                Sabre: {},
                Foil: {},
                Epee: {}
            };

            for (let i = 0; i < meetsData.length; i++) {
                const meet = meetsData[i];
                // Skip deleted meets
                if (meet.deleted) continue;
                const meetBouts = bouts[i];
                const side = meet.team1ID === teamId ? BoutSide.Fencer1 : BoutSide.Fencer2;

                for (const bout of meetBouts) {
                    const fencer = bout[`fencer${side}`];
                    const winner = boutWinner(bout, { team: false });
                    const fencerWon = winner === side;
                    const fencerLost = winner && winner !== side;
                    const record = teamRecords[bout.weapon][fencer.fencerInfo.id!];
                    if (record) {
                        if (fencerWon) {
                            record.wins++;
                        } else if (fencerLost) {
                            record.losses++;
                        }
                    } else {
                        teamRecords[bout.weapon][fencer.fencerInfo.id!] = {
                            wins: Number(fencerWon),
                            losses: Number(fencerLost)
                        };
                    }
                }
            }
            debugTime && console.timeEnd("Processing bouts...");
            debugTime && console.timeEnd("Getting roster records...");

            return new DBSuccess(teamRecords);
        } catch (e: unknown) {
            console.error("Error in retrieving roster records", e);
            return new DBError("An error occurred when retrieving roster records.");
        }
    }

    public async updateTeamsLastAccessedSeason(teamIDs: string[]) {
        const databaseUpdates: Record<string, unknown> = {};

        for (const id of teamIDs) {
            databaseUpdates[`/teams/${id}/lastAccessedSeason`] = CURRENT_SEASON_STR;
        }

        this.database.ref(this.PREFIX).update(databaseUpdates);
    }

    // #endregion

    // #region Organizations

    public async getOrganizationList(
        listener?: Listener<DBResult<Record<string, IOrganization>>>
    ): AsyncDBResult<Record<string, IOrganization>> {
        if (listener) {
            return new Promise(async res => {
                let found = false;
                if (Object.keys(this.organizations).length) {
                    found = true;
                    res(new DBSuccess(this.organizations));
                }
                this.database.ref(`${this.PREFIX}/organizations`).on("value", v => {
                    const orgs: Record<string, IOrganization> = v.val() || {};
                    for (const i in orgs) {
                        const val = { ...DEFAULT_ORGANIZATION, ...orgs[i] };
                        orgs[i] = val;
                        this.organizations[i] = val;
                    }
                    const response = new DBSuccess(orgs);
                    listener(response);
                    if (!found) {
                        res(response);
                    }
                });
            });
        } else {
            const v = await this.database.ref(`${this.PREFIX}/organizations`).once("value");
            const orgs = v.val() || ({} as Record<string, IOrganization>);
            for (const i in orgs) {
                const val = { ...DEFAULT_ORGANIZATION, ...orgs[i] };
                orgs[i] = val;
                this.organizations[i] = val;
            }
            return new DBSuccess(orgs);
        }
    }

    public stopListeningOrganizationList(listener?: (val: unknown) => void) {
        const l = (listener && this.listeners.get(listener)) || listener;
        removeRefListener(`${this.PREFIX}/organizations`, l);
    }

    public async getOrganization(
        orgID: string,
        options?: { listener?: Listener<DBResult<IOrganization>>; forceCache?: boolean }
    ): AsyncDBResult<IOrganization> {
        if (!orgID || typeof orgID !== "string") return new DBError(ERROR_SCHOOL_DOES_NOT_EXIST);
        const listener = options?.listener;

        if (listener) {
            return new Promise(async res => {
                let found = false;
                if (this.organizations[orgID]) {
                    found = true;
                    res(new DBSuccess(this.organizations[orgID]));
                }
                const l = v => {
                    const org = v.val();
                    if (!org) {
                        const error = new DBError(ERROR_SCHOOL_DOES_NOT_EXIST);
                        listener(error);
                        return res(error);
                    }
                    const val: IOrganization = { ...DEFAULT_ORGANIZATION, ...org };
                    this.organizations[orgID] = val;
                    this.cache.queueSave("organizations");
                    listener(new DBSuccess(val));
                    if (!found) {
                        res(new DBSuccess(val));
                    }
                };
                this.database.ref(`${this.PREFIX}/organizations/${orgID}`).on("value", l);
                this.listeners.set(listener, l);
            });
        } else {
            if (options?.forceCache && orgID in this.organizations) {
                return new DBSuccess(this.organizations[orgID]);
            }

            const v = await this.database.ref(`${this.PREFIX}/organizations/${orgID}`).once("value");
            const org = v.val();
            if (!org) {
                return new DBError(ERROR_SCHOOL_DOES_NOT_EXIST);
            }
            const val: IOrganization = { ...DEFAULT_ORGANIZATION, ...org };
            this.organizations[orgID] = val;
            this.cache.queueSave("organizations");
            return new DBSuccess(val);
        }
    }

    public stopListeningOrganization(id: string, listener?: (val: unknown) => void) {
        const l = (listener && this.listeners.get(listener)) || listener;
        removeRefListener(`${this.PREFIX}/organizations/${id}`, l);
    }

    public async createOrganization(
        name: string,
        countryCode: string,
        region: string,
        teams: {
            boysTeam: CollegeProgramType;
            girlsTeam: CollegeProgramType;
            boysTeamJV?: boolean;
            girlsTeamJV?: boolean;
            boysTeamConference?: string | string[];
            girlsTeamConference?: string | string[];
        },
        abbreviation: string,
        type: SchoolType,
        writeIn: boolean,
        section?: "north" | "south" | "mixed",
        boysSection?: "north" | "south",
        girlsSection?: "north" | "south"
    ): AsyncDBResult<IOrganization> {
        const user = this.auth.currentUser!;
        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);

        const createOrganization = this.functions.httpsCallable("createOrganization");
        const result = await createOrganization({
            prefix: this.PREFIX,
            userId: user.uid,
            name,
            countryCode,
            region,
            teams,
            abbreviation,
            type,
            writeIn,
            section,
            boysSection,
            girlsSection
        });

        return result.data;
    }

    public async editOrganization(edits: IOrganizationEdit): AsyncDBResult<boolean> {
        const user = this.auth.currentUser;
        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);

        const organizationUpdates: Record<string, unknown> = {};

        if (edits.district) {
            organizationUpdates[`${this.PREFIX}/organizations/${edits.id}/district`] = edits.district;
        }

        if (edits.logoPath || edits.logoPath === "") {
            organizationUpdates[`${this.PREFIX}/organizations/${edits.id}/logoPath`] = edits.logoPath;
        }

        if (edits.section) {
            organizationUpdates[`${this.PREFIX}/organizations/${edits.id}/section`] = edits.section;
        }

        // Handle name and abbreviation changes - need to update related teams
        if (edits.name || edits.abbreviation) {
            const orgResult = await this.getOrganization(edits.id);
            if (orgResult.status === "fail") return orgResult;
            const org = orgResult.data;

            const oldName = org.name;
            const oldAbbreviation = org.abbreviation || "";
            const newName = edits.name || oldName;
            const newAbbreviation = edits.abbreviation || oldAbbreviation;

            // Update organization name and abbreviation
            if (edits.name) {
                organizationUpdates[`${this.PREFIX}/organizations/${edits.id}/name`] = edits.name;
            }
            if (edits.abbreviation) {
                organizationUpdates[`${this.PREFIX}/organizations/${edits.id}/abbreviation`] = edits.abbreviation;
            }

            // Update all related teams
            const teamIds: string[] = [];
            if (org.boysTeam) teamIds.push(org.boysTeam);
            if (org.girlsTeam) teamIds.push(org.girlsTeam);
            if (org.boysJVTeam) teamIds.push(org.boysJVTeam);
            if (org.girlsJVTeam) teamIds.push(org.girlsJVTeam);

            for (const teamId of teamIds) {
                const teamResult = await this.getTeam(teamId);
                if (teamResult.status === "fail") continue;
                const team = teamResult.data;

                // Extract the suffix from the old team name (e.g., " Men's", " Women's - JV")
                // Use indexOf to find the first occurrence and replace only that part
                let teamSuffix = "";
                let abbreviationSuffix = "";

                const nameIndex = team.name.indexOf(oldName);
                if (nameIndex === 0) {
                    // Organization name is at the start
                    teamSuffix = team.name.substring(oldName.length).trim();
                } else {
                    // Organization name not at start, try to extract suffix differently
                    if (team.name.includes("Men's")) {
                        teamSuffix = team.name.includes("JV") ? "Men's - JV" : "Men's";
                    } else if (team.name.includes("Women's")) {
                        teamSuffix = team.name.includes("JV") ? "Women's - JV" : "Women's";
                    } else {
                        // Fallback: preserve everything after organization name
                        teamSuffix = nameIndex >= 0 ? team.name.substring(nameIndex + oldName.length).trim() : "";
                    }
                }

                // Handle abbreviation similarly
                if (team.abbreviation) {
                    const abbIndex = team.abbreviation.indexOf(oldAbbreviation);
                    if (abbIndex === 0) {
                        abbreviationSuffix = team.abbreviation.substring(oldAbbreviation.length).trim();
                    } else {
                        // Try to extract based on pattern
                        if (team.abbreviation.includes("Men's")) {
                            abbreviationSuffix = team.abbreviation.includes("JV") ? " Men's - JV" : " Men's";
                        } else if (team.abbreviation.includes("Women's") || team.abbreviation.includes("Womens")) {
                            abbreviationSuffix = team.abbreviation.includes("JV") ? " Women's - JV" : " Women's";
                        } else {
                            abbreviationSuffix = abbIndex >= 0 ? team.abbreviation.substring(abbIndex + oldAbbreviation.length).trim() : "";
                        }
                    }
                } else {
                    // No abbreviation, generate default suffix
                    if (teamSuffix.includes("Men's")) {
                        abbreviationSuffix = teamSuffix.includes("JV") ? " Men's - JV" : " Men's";
                    } else if (teamSuffix.includes("Women's")) {
                        abbreviationSuffix = teamSuffix.includes("JV") ? " Women's - JV" : " Women's";
                    }
                }

                const newTeamName = teamSuffix ? `${newName} ${teamSuffix}` : newName;
                const newTeamAbbreviation = abbreviationSuffix ? `${newAbbreviation} ${abbreviationSuffix}` : newAbbreviation;

                organizationUpdates[`${this.PREFIX}/teams/${teamId}/name`] = newTeamName;
                if (newTeamAbbreviation) {
                    organizationUpdates[`${this.PREFIX}/teams/${teamId}/abbreviation`] = newTeamAbbreviation;
                }

                // Update teamsDirectory entry
                organizationUpdates[`${this.PREFIX}/teamDirectory/${teamId}/name`] = newTeamName;
                if (newTeamAbbreviation) {
                    organizationUpdates[`${this.PREFIX}/teamDirectory/${teamId}/abbreviation`] = newTeamAbbreviation;
                }

                organizationUpdates[`${this.PREFIX}/teamDirectoryUpdatedAt`] = Date.now();
            }
        }

        await this.database.ref().update(organizationUpdates);

        return new DBSuccess(true);
    }

    public async requestOrganizationPublication(
        takeover: boolean,
        orgId: string,
        orgName: string,
        name: string,
        school: string,
        additionalInfo?: string
    ): AsyncDBResult<boolean> {
        const user = this.auth.currentUser;

        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);
        if (!name) return new DBError("No name provided.");
        if (!school) return new DBError("No school provided.");

        const applicationID = this.database.ref(`${this.PREFIX}/publicationRequests`).push().key!;

        const application: IPublicationApplication = {
            id: applicationID,
            name,
            orgName,
            orgId,
            school,
            submittedAt: Date.now(),
            userId: user.uid,
            additionalInfo: additionalInfo || null,
            takeover
        };

        try {
            const sendApplicationNotice = this.functions.httpsCallable("sendApplicationNotice");
            await sendApplicationNotice({
                appId: applicationID,
                orgName,
                name,
                school,
                additionalInfo,
                takeover
            });
            await this.database.ref(`${this.PREFIX}/publicationRequests/${applicationID}`).set(application);
            return new DBSuccess(true);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            const code = e.code;
            const message = e.message;
            const details = e.details;
            return new DBError(`Error sending email: ${code} - ${message} ${details}`);
        }
    }

    public async acceptOrganizationPublication(
        id: string,
        orgId: string,
        orgName: string,
        email: string,
        userId: string,
        takeover: boolean
    ): AsyncDBResult<boolean> {
        const userInfo = await this.getUserInfo(userId);
        if (userInfo.status === "fail") return userInfo;
        const user = userInfo.data;
        const d = Date.now();

        const databaseUpdates = {};

        const orgRequest = await this.getOrganization(orgId);
        if (orgRequest.status === "fail") return new DBError(ERROR_SCHOOL_DOES_NOT_EXIST);
        const org = orgRequest.data;

        databaseUpdates[`${this.PREFIX}/publicationRequests/${id}`] = null;

        if (takeover) {
            const teams = user.teams;

            if (org.boysTeam) {
                databaseUpdates[`${this.PREFIX}/teams/${org.boysTeam}/administrators`] = [userId];
                databaseUpdates[`${this.PREFIX}/teams/${org.boysTeam}/writeIn`] = false;
                teams.push(org.boysTeam);
            }
            if (org.girlsTeam) {
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsTeam}/administrators`] = [userId];
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsTeam}/writeIn`] = false;
                teams.push(org.girlsTeam);
            }
            if (org.boysJVTeam) {
                databaseUpdates[`${this.PREFIX}/teams/${org.boysJVTeam}/administrators`] = [userId];
                databaseUpdates[`${this.PREFIX}/teams/${org.boysJVTeam}/writeIn`] = false;
                teams.push(org.boysJVTeam);
            }
            if (org.girlsJVTeam) {
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsJVTeam}/administrators`] = [userId];
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsJVTeam}/writeIn`] = false;
                teams.push(org.girlsJVTeam);
            }

            databaseUpdates[`${this.PREFIX}/organizations/${orgId}/administrators`] = [userId];
            databaseUpdates[`${this.PREFIX}/organizations/${orgId}/writeIn`] = false;
            databaseUpdates[`${this.COMMON_PREFIX}/users/${userId}/teams`] = teams;
        } else {
            databaseUpdates[`${this.PREFIX}/organizations/${orgId}/published`] = true;
            databaseUpdates[`${this.PREFIX}/organizations/${orgId}/publishedAt`] = d;
            databaseUpdates[`${this.PREFIX}/organizations/${orgId}/publishedBy`] = user.id;

            if (org.boysTeam) {
                databaseUpdates[`${this.PREFIX}/teams/${org.boysTeam}/published`] = true;
                databaseUpdates[`${this.PREFIX}/teams/${org.boysTeam}/publishedAt`] = d;
                databaseUpdates[`${this.PREFIX}/teams/${org.boysTeam}/publishedBy`] = user.id;
                databaseUpdates[`${this.PREFIX}/teamDirectory/${org.boysTeam}/published`] = true;
                databaseUpdates[`${this.PREFIX}/teamDirectoryUpdatedAt`] = Date.now();
            }
            if (org.girlsTeam) {
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsTeam}/published`] = true;
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsTeam}/publishedAt`] = d;
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsTeam}/publishedBy`] = user.id;
                databaseUpdates[`${this.PREFIX}/teamDirectory/${org.girlsTeam}/published`] = true;
                databaseUpdates[`${this.PREFIX}/teamDirectoryUpdatedAt`] = Date.now();
            }
            if (org.boysJVTeam) {
                databaseUpdates[`${this.PREFIX}/teams/${org.boysJVTeam}/published`] = true;
                databaseUpdates[`${this.PREFIX}/teams/${org.boysJVTeam}/publishedAt`] = d;
                databaseUpdates[`${this.PREFIX}/teams/${org.boysJVTeam}/publishedBy`] = user.id;
                databaseUpdates[`${this.PREFIX}/teamDirectory/${org.boysJVTeam}/published`] = true;
                databaseUpdates[`${this.PREFIX}/teamDirectoryUpdatedAt`] = Date.now();
            }
            if (org.girlsJVTeam) {
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsJVTeam}/published`] = true;
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsJVTeam}/publishedAt`] = d;
                databaseUpdates[`${this.PREFIX}/teams/${org.girlsJVTeam}/publishedBy`] = user.id;
                databaseUpdates[`${this.PREFIX}/teamDirectory/${org.girlsJVTeam}/published`] = true;
                databaseUpdates[`${this.PREFIX}/teamDirectoryUpdatedAt`] = Date.now();
            }
        }

        try {
            this.database.ref().update(databaseUpdates);

            const sendOrganizationAcceptance = this.functions.httpsCallable("sendOrganizationAcceptance");
            await sendOrganizationAcceptance({
                orgName,
                email,
                orgId,
                takeover
            });
            return new DBSuccess(true);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            const code = e.code;
            const message = e.message;
            const details = e.details;
            return new DBError(`Error sending email: ${code} - ${message} ${details}`);
        }
    }

    public async rejectOrganizationPublication(
        id: string,
        orgName: string,
        email: string,
        orgId: string,
        takeover: boolean,
        reason?: string
    ): AsyncDBResult<boolean> {
        const user = this.auth.currentUser;

        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);

        const databaseUpdates = {
            [`${this.PREFIX}/publicationRequests/${id}`]: null
        };

        try {
            this.database.ref().update(databaseUpdates);

            const sendOrganizationRejection = this.functions.httpsCallable("sendOrganizationRejection");
            await sendOrganizationRejection({
                orgName,
                email,
                orgId,
                reason,
                takeover
            });
            return new DBSuccess(true);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            const code = e.code;
            const message = e.message;
            const details = e.details;
            return new DBError(`Error sending email: ${code} - ${message} ${details}`);
        }
    }

    public async unpublishOrganization(id: string): AsyncDBResult<boolean> {
        const orgResult = await this.getOrganization(id);
        if (orgResult.status === "fail") return orgResult;
        const org = orgResult.data;

        const databaseUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/organizations/${id}/published`]: false,
            [`${this.PREFIX}/organizations/${id}/publishedAt`]: null,
            [`${this.PREFIX}/organizations/${id}/publishedBy`]: null
        };

        const subteams = [org.boysTeam, org.girlsTeam, org.boysJVTeam, org.girlsJVTeam];
        for (const team of subteams) {
            if (team) {
                databaseUpdates[`${this.PREFIX}/teams/${team}/published`] = false;
                databaseUpdates[`${this.PREFIX}/teams/${team}/publishedAt`] = null;
                databaseUpdates[`${this.PREFIX}/teams/${team}/publishedBy`] = null;
                databaseUpdates[`${this.PREFIX}/teamDirectory/${team}/published`] = false;
                databaseUpdates[`${this.PREFIX}/teamDirectoryUpdatedAt`] = Date.now();
            }
        }

        try {
            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e: unknown) {
            if (e instanceof Error) {
                return new DBError(e.message);
            } else {
                return new DBError(ERROR_UNKNOWN);
            }
        }
    }

    public getOrganizationRequests(
        listener?: Listener<DBResult<Record<string, IPublicationApplication>>>
    ): AsyncDBResult<Record<string, IPublicationApplication>> {
        return new Promise(res => {
            this.database.ref(`${this.PREFIX}/publicationRequests`).on("value", v => {
                const val: Record<string, IPublicationApplication> = v.val();

                if (!val) {
                    const response = new DBSuccess({});
                    listener && listener(response);
                    res(response);
                } else {
                    const response = new DBSuccess(val);
                    listener && listener(response);
                    res(response);
                }
            });
        });
    }

    public stopListeningOrganizationRequests() {
        this.database.ref(`${this.PREFIX}/publicationRequests`).off();
    }

    public async makeOrganizationUnmanaged(org: string | IOrganization): AsyncDBResult<boolean> {
        let data = org;
        if (typeof data === "string") {
            const org2 = await this.getOrganization(data);
            if (org2.status === "fail") return org2;
            data = org2.data;
        }

        const databaseUpdates: Record<string, unknown> = {};

        databaseUpdates[`${this.PREFIX}/organizations/${data.id}/administrators`] = [];
        if (data.boysTeam) databaseUpdates[`${this.PREFIX}/teams/${data.boysTeam}/administrators`] = [];
        if (data.girlsTeam) databaseUpdates[`${this.PREFIX}/teams/${data.girlsTeam}/administrators`] = [];
        if (data.boysJVTeam) databaseUpdates[`${this.PREFIX}/teams/${data.boysJVTeam}/administrators`] = [];
        if (data.girlsJVTeam) databaseUpdates[`${this.PREFIX}/teams/${data.girlsJVTeam}/administrators`] = [];

        const teamIDs = [data.boysTeam, data.girlsTeam, data.boysJVTeam, data.girlsJVTeam];

        const usersResult = await this.getUserList();
        if (usersResult.status === "fail") return usersResult;
        const users = usersResult.data;
        for (const id in users) {
            const filteredTeams = users[id].teams.filter(l => teamIDs.includes(l));
            const filteredManagingTeams = users[id].managingTeams.filter(l => teamIDs.includes(l));

            if (users[id].teams.length !== filteredTeams.length) {
                databaseUpdates[`${this.PREFIX}/users/${id}/teams`] = filteredTeams;
            }
            if (users[id].managingTeams.length !== filteredManagingTeams.length) {
                databaseUpdates[`${this.PREFIX}/users/${id}/managingTeams`] = filteredManagingTeams;
            }
        }

        await this.database.ref().update(databaseUpdates);
        return new DBSuccess(true);
    }

    // #endregion

    // #region Fencers

    /**
     * Get fencer by ID
     * @param fencerID
     * @returns
     */
    public async getAllFencers({ forceCache }: { forceCache?: boolean } = {}): AsyncDBResult<Record<string, IExistingFencer>> {
        if (forceCache && Object.keys(this.fencers).length) {
            return new DBSuccess(this.fencers);
        }
        const data = await refOnce<Record<string, IExistingFencer>>(`${this.PREFIX}/fencers`);
        return new DBSuccess(data);
    }

    /**
     * Get fencer by ID
     * @param fencerID
     * @returns
     */
    public async getFencer(fencerID: string, options?: { forceCache: boolean }): AsyncDBResult<IExistingFencer> {
        if (!fencerID || typeof fencerID !== "string") return new DBError(ERROR_INVALID_ID);
        if (fencerID.match(/(\.|#|\$|\[|\])/g)) return new DBError(ERROR_INVALID_ID);
        const forceCache = options?.forceCache ?? true;
        if (forceCache) {
            const cached = this.fencers[fencerID];
            if (cached && Object.keys(cached).length) {
                return new DBSuccess({ ...cached });
            }
        }

        const data = await refOnce<IExistingFencer>(`${this.PREFIX}/fencers/${fencerID}`);
        if (data === null) return new DBError(ERROR_FENCER_DOES_NOT_EXIST);
        this.fencers[fencerID] = data;
        this.cache.queueSave("fencers");
        return new DBSuccess({ ...data });
    }

    /**
     * Get all fencers by a query
     * @param param0
     */
    public async getFencersByQuery({
        id,
        firstName,
        lastName,
        limit
    }: Partial<{ id: string; firstName: string; lastName: string; limit: number }>): AsyncDBResult<IExistingFencer[]> {
        const trueLimit = limit || 20;
        if (id) {
            const fencer = await this.getFencer(id);
            if (fencer.status === "fail") return fencer;
            return new DBSuccess([fencer.data]);
        } else if (lastName) {
            const incremented = incrementString(lastName);
            const v = await fire
                .database()
                .ref(`${this.PREFIX}/fencers`)
                .orderByChild("lastName")
                .startAt(lastName)
                .endBefore(incremented)
                .limitToFirst(trueLimit)
                .once("value");
            const fencersObj = v.val() as Record<string, IExistingFencer> | null;
            if (!fencersObj) return new DBError(ERROR_FENCER_DOES_NOT_EXIST);
            let fencers = Object.values(fencersObj);
            if (firstName) {
                fencers = fencers.filter(l => l.firstName.includes(firstName));
            }
            return new DBSuccess(fencers);
        } else if (firstName) {
            const incremented = incrementString(firstName);
            const v = await fire
                .database()
                .ref(`${this.PREFIX}/fencers`)
                .orderByChild("firstName")
                .startAt(firstName)
                .endBefore(incremented)
                .limitToFirst(trueLimit)
                .once("value");
            const fencersObj = v.val() as Record<string, IExistingFencer> | null;
            if (!fencersObj) return new DBError(ERROR_FENCER_DOES_NOT_EXIST);
            const fencers = Object.values(fencersObj);
            return new DBSuccess(Object.values(fencers));
        } else {
            return new DBError(ERROR_UNKNOWN);
        }
    }

    public async getFencersByTeam(teamID: string): AsyncDBResult<Record<string, IExistingFencer>> {
        const v = await fire.database().ref(`${this.PREFIX}/fencers`).once("value");
        const allFencers = (v.val() as Record<string, IExistingFencer>) || {};
        // Filter fencers where teamID exists anywhere in the teams array
        const fencers: Record<string, IExistingFencer> = {};
        for (const [id, fencer] of Object.entries(allFencers)) {
            if (fencer.teams && fencer.teams.includes(teamID)) {
                fencers[id] = fencer;
            }
        }
        return new DBSuccess(fencers);
    }

    public async createNewFencerInTeamDatabaseObj(
        teamId: string,
        firstName: string,
        lastName: string,
        gradYear?: number,
        season?: string,
        weapon?: RosterWeapon,
        rating?: string | null
    ): AsyncDBResult<{
        databaseAdditions: Record<string, unknown>;
        newFencer: IExistingFencer;
    }> {
        const user = this.auth.currentUser;

        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);

        const normalizedFirstName = normalizeApostrophes(firstName).trim();
        const normalizedLastName = normalizeApostrophes(lastName).trim();
        const d = new Date();
        const databaseAdditions: Record<string, unknown> = {};
        const fencerPIN = Math.floor(Math.random() * 90000) + 10000;
        const fencerID = this.database.ref(`${this.PREFIX}/fencers/`).push().key!;

        const team = await this.getTeam(teamId);
        if (team.status === "fail") return new DBError(ERROR_TEAM_DOES_NOT_EXIST);
        const teamData = team.data;

        const newFencer: IExistingFencer = {
            id: fencerID,
            firstName: normalizedFirstName,
            lastName: normalizedLastName,
            gradYear: gradYear,
            createdBy: user.uid,
            createdAt: Number(d),
            updatedAt: Number(d),
            pin: fencerPIN,
            teams: [teamId],
            bouts: [],
            gender: GROWN_UP_STR[teamData.boysAndGirlsTeam]
        };

        if (weapon && (weapon === "Sabre" || weapon === "Foil" || weapon === "Epee") && rating && rating.trim()) {
            this.setWeaponRatingOnFencer(newFencer, weapon, rating);
        }

        for (const i in newFencer) {
            databaseAdditions[`${this.PREFIX}/fencers/${fencerID}/${i}`] = newFencer[i] || null;
        }

        const existingFencers = (teamData.fencers || []).filter((f: string | null | undefined) => Boolean(f));
        databaseAdditions[`${this.PREFIX}/teams/${teamId}/fencers`] = [...existingFencers, fencerID];
        if (teamId in this.teams) this.teams[teamId].fencers = [...existingFencers, fencerID];

        if (season && !teamData.seasons.includes(season)) {
            databaseAdditions[`${this.PREFIX}/teams/${teamId}/seasons`] = [...(teamData.seasons || []), season];
            if (teamId in this.teams) this.teams[teamId].seasons = [...(teamData.seasons || []), season];
        }

        if (season && weapon) {
            databaseAdditions[`${this.PREFIX}/teams/${teamId}/roster/${season}/${weapon}/${fencerID}/home`] = 0;
            databaseAdditions[`${this.PREFIX}/teams/${teamId}/roster/${season}/${weapon}/${fencerID}/away`] = 0;
            if (teamId in this.teams) {
                // Ensure the nested roster structure exists before accessing it
                if (!this.teams[teamId].roster) {
                    this.teams[teamId].roster = {} as Record<
                        string,
                        Record<RosterWeapon, Record<string, { home: number; away: number; power?: number }>>
                    >;
                }
                if (!this.teams[teamId].roster[season]) {
                    this.teams[teamId].roster[season] = {} as Record<
                        RosterWeapon,
                        Record<string, { home: number; away: number; power?: number }>
                    >;
                }
                if (!this.teams[teamId].roster[season][weapon]) {
                    this.teams[teamId].roster[season][weapon] = {};
                }
                this.teams[teamId].roster[season][weapon][fencerID] = { home: 0, away: 0 };
            }
        }

        if (teamId in this.teams) this.cache.queueSave("teams");

        return new DBSuccess({ databaseAdditions, newFencer });
    }

    public async createNewFencerInTeam(
        teamId: string,
        firstName: string,
        lastName: string,
        gradYear: number,
        season?: string,
        weapon?: Weapon,
        rating?: string | null
    ): AsyncDBResult<string> {
        try {
            const dbResult = await this.createNewFencerInTeamDatabaseObj(
                teamId,
                firstName,
                lastName,
                gradYear,
                season,
                weapon as RosterWeapon,
                rating
            );
            if (dbResult.status === "fail") return dbResult;
            const { databaseAdditions, newFencer } = dbResult.data;

            await this.database.ref().update(databaseAdditions);

            return new DBSuccess(newFencer.id);
        } catch (e: unknown) {
            if (e instanceof DBError) {
                throw e;
            } else {
                console.error(e);
                return new DBError(ERROR_UNKNOWN);
            }
        }
    }

    public async editFencer(fencer: Partial<IExistingFencer>): AsyncDBResult<boolean> {
        const user = this.auth.currentUser;
        const d = new Date();

        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);
        if (!fencer.id) return new DBError("Fencer ID is required to edit a fencer.");

        const fencerUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/fencers/${fencer.id}/updatedAt`]: Number(d)
        };

        if (fencer.firstName !== undefined) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/firstName`] = normalizeApostrophes(fencer.firstName).trim();
        }
        if (fencer.lastName !== undefined) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/lastName`] = normalizeApostrophes(fencer.lastName).trim();
        }
        if (fencer.gradYear !== undefined) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/gradYear`] = fencer.gradYear;
        } else if (fencer.gradYear === null) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/gradYear`] = null;
        }
        if (fencer.ratingSabre !== undefined) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/ratingSabre`] = fencer.ratingSabre || null;
        }
        if (fencer.ratingFoil !== undefined) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/ratingFoil`] = fencer.ratingFoil || null;
        }
        if (fencer.ratingEpee !== undefined) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/ratingEpee`] = fencer.ratingEpee || null;
        }
        if (fencer.powerRatingSabre !== undefined) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/powerRatingSabre`] = fencer.powerRatingSabre !== null ? Number(fencer.powerRatingSabre) : null;
        }
        if (fencer.powerRatingFoil !== undefined) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/powerRatingFoil`] = fencer.powerRatingFoil !== null ? Number(fencer.powerRatingFoil) : null;
        }
        if (fencer.powerRatingEpee !== undefined) {
            fencerUpdates[`${this.PREFIX}/fencers/${fencer.id}/powerRatingEpee`] = fencer.powerRatingEpee !== null ? Number(fencer.powerRatingEpee) : null;
        }

        await this.database.ref().update(fencerUpdates);
        return new DBSuccess(true);
    }

    public async runActionQueue(teamId: string, queue: ActionQueue): AsyncDBResult<boolean> {
        const d = new Date();

        const idRedefs: Record<string, string> = {};
        const databaseModifications: Record<string, unknown> = {};
        const stripUpdates: Record<string, { home: number; away: number }> = {};

        for (const action of queue) {
            if (action.type === "updateStrip") {
                if (action.id in stripUpdates) {
                    stripUpdates[action.id][action.home ? "home" : "away"] = action.strip;
                } else {
                    stripUpdates[action.id] = {
                        home: action.home ? action.strip : 0,
                        away: !action.home ? action.strip : 0
                    };
                }
            }
        }

        for (const action of queue) {
            if (action.type === "cloneSeason") {
                await this.createNewClonedSeason(teamId, action.seasonToClone, action.newSeason);
            } else {
                const id = action.id in idRedefs ? idRedefs[action.id] : action?.id;
                switch (action.type) {
                    case "addFencer": {
                        databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${action.weapon}/${id}/home`] = 0;
                        databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${action.weapon}/${id}/away`] = 0;
                        if (action.weapon === "Unsorted") {
                            const weapons: RosterWeapon[] = ["Sabre", "Foil", "Epee"];
                            for (const weapon of weapons) {
                                databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${weapon}/${id}/home`] = null;
                                databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${weapon}/${id}/away`] = null;
                                databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${weapon}/${id}/power`] =
                                    null;
                            }
                        } else {
                            databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/Unsorted/${id}/home`] = null;
                            databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/Unsorted/${id}/away`] = null;
                            databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/Unsorted/${id}/power`] = null;
                        }
                        const cachedTeam = this.teams[id];
                        if (cachedTeam) {
                            cachedTeam.roster[action.season][action.weapon][id] = { home: 0, away: 0 };
                            delete cachedTeam.roster[action.season].Unsorted[id];
                        }
                        break;
                    }
                    case "createFencer": {
                        const dbResult = await this.createNewFencerInTeamDatabaseObj(
                            teamId,
                            action.firstName,
                            action.lastName,
                            action.graduationYear,
                            action.season,
                            action.weapon,
                            action.rating || null
                        );
                        if (dbResult.status === "fail") return dbResult;
                        const { databaseAdditions, newFencer } = dbResult.data;
                        idRedefs[action.id] = newFencer.id;
                        if (action.id in stripUpdates) {
                            stripUpdates[newFencer.id] = stripUpdates[action.id];
                            delete stripUpdates[action.id];
                        }
                        const fencersStr = `${this.PREFIX}/teams/${teamId}/fencers`;
                        for (const i in databaseAdditions) {
                            if (i === fencersStr && fencersStr in databaseModifications) {
                                const additions = (databaseAdditions[i] as unknown[] || []).filter((f: unknown) => Boolean(f));
                                const modifications = (databaseModifications[i] as unknown[] || []).filter((f: unknown) => Boolean(f));
                                databaseModifications[fencersStr] = [
                                    ...new Set([...additions, ...modifications])
                                ];
                            } else {
                                databaseModifications[i] = databaseAdditions[i];
                            }
                        }
                        break;
                    }
                    case "deleteFencer": {
                        databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${action.weapon}/${id}/home`] = null;
                        databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${action.weapon}/${id}/away`] = null;
                        databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${action.weapon}/${id}/power`] = null;
                        const cachedTeam = this.teams[id];
                        if (cachedTeam) {
                            delete cachedTeam.roster[action.season][action.weapon][id];
                        }
                        break;
                    }
                    case "editFencer": {
                        const fencerResult = await this.getFencer(id);
                        if (fencerResult.status === "success") {
                            const fencer = {...fencerResult.data};
                            databaseModifications[`${this.PREFIX}/fencers/${id}/firstName`] = action.firstName;
                            databaseModifications[`${this.PREFIX}/fencers/${id}/lastName`] = action.lastName;
                            if (action.graduationYear) {
                                databaseModifications[`${this.PREFIX}/fencers/${id}/gradYear`] = action.graduationYear;
                            } else {
                                databaseModifications[`${this.PREFIX}/fencers/${id}/gradYear`] = null;
                            }
                            const isWeapon = action.weapon === "Sabre" || action.weapon === "Foil" || action.weapon === "Epee";
                            if (action.rating !== undefined && isWeapon) {
                                const field =
                                    action.weapon === "Sabre"
                                        ? "ratingSabre"
                                        : action.weapon === "Foil"
                                        ? "ratingFoil"
                                        : "ratingEpee";
                                const trimmed = action.rating?.trim();
                                databaseModifications[`${this.PREFIX}/fencers/${id}/${field}`] = trimmed ? trimmed : null;
                            }
                            databaseModifications[`${this.PREFIX}/fencers/${id}/updatedAt`] = Number(d);
                            if (action.power !== undefined) {
                                databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${action.weapon}/${id}/power`] =
                                    action.power;
                            }
                            const cachedFencer = this.fencers[id];
                            if (cachedFencer) {
                                cachedFencer.firstName = action.firstName;
                                cachedFencer.lastName = action.lastName;
                                cachedFencer.gradYear = action.graduationYear;
                                if (action.rating !== undefined && isWeapon) {
                                    this.setWeaponRatingOnFencer(cachedFencer, action.weapon as Weapon, action.rating || null);
                                }
                            }
                            this.cache.queueSave("fencers");
                            const cachedTeam = this.teams[id];
                            if (cachedTeam && action.power !== undefined && cachedTeam.roster[action.season]?.[action.weapon]?.[id]) {
                                if (action.power !== null) {
                                    cachedTeam.roster[action.season][action.weapon][id].power = action.power;
                                } else {
                                    delete cachedTeam.roster[action.season][action.weapon][id].power;
                                }
                            }

                            const oldName = `${fencer.firstName} ${fencer.lastName}`;
                            const newName = `${action.firstName} ${action.lastName}`;
                            if (fencer.bouts && fencer.bouts.length > 0) {
                                const bouts = await Promise.all(fencer.bouts.map(boutId => this.getBout(boutId)));
                                for (const bout of bouts) {
                                    if (bout.status === "success") {
                                        if (bout.data.fencer1.fencerInfo.id === id) {
                                            databaseModifications[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/firstName`] =
                                                action.firstName;
                                            databaseModifications[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/lastName`] =
                                                action.lastName;
                                            databaseModifications[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/gradYear`] =
                                                action.graduationYear || null;
                                        }
                                        if (bout.data.fencer2.fencerInfo.id === id) {
                                            databaseModifications[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/firstName`] =
                                                action.firstName;
                                            databaseModifications[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/lastName`] =
                                                action.lastName;
                                            databaseModifications[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/gradYear`] =
                                                action.graduationYear || null;
                                        }

                                        // Sync name changes to bout events
                                        if (oldName !== newName) {
                                            const eventUpdates = await this.getUpdatesToChangeNamesForEvents(
                                                bout.data.id,
                                                oldName,
                                                newName,
                                                { updateCache: true }
                                            );
                                            if (eventUpdates.status === "success") {
                                                Object.assign(databaseModifications, eventUpdates.data);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        break;
                    }
                    case "updateStrip": {
                        const homeStr = action.home ? "home" : "away";
                        databaseModifications[`${this.PREFIX}/teams/${teamId}/roster/${action.season}/${action.weapon}/${id}/${homeStr}`] =
                            stripUpdates[id][homeStr];
                        const cachedTeam = this.teams[id];
                        if (cachedTeam) {
                            cachedTeam.roster[action.season][action.weapon][id][homeStr] = stripUpdates[id][homeStr];
                        }
                        break;
                    }
                }
            }
        }

        await this.database.ref().update(databaseModifications);
        return new DBSuccess(true);
    }

    /**
     * Combines two fencers, merging their statistics and deleting the original fencer
     * @param originId Fencer that will be deleted
     * @param targetId Fencer that receives new statistics
     * @param teamId Team of both fencers
     * @returns Success
     */
    public async mergeFencer(originId: string, targetId: string, teamId: string | ITeam): AsyncDBResult<boolean> {
        let team = teamId;
        if (typeof team === "string") {
            const dbteam = await this.getTeam(team);
            if (dbteam.status === "fail") return dbteam;
            team = dbteam.data;
        }

        const [originalFencerResult, targetFencerResult] = await Promise.all([this.getFencer(originId), this.getFencer(targetId)]);
        if (originalFencerResult.status === "fail") return originalFencerResult;
        if (targetFencerResult.status === "fail") return targetFencerResult;
        const originalFencer = originalFencerResult.data;
        const targetFencer = targetFencerResult.data;

        const bouts: string[] = [];
        const meetIds = Object.values(team.dualMeets).flat();
        for (const meet of meetIds) {
            const dualMeetData = await this.getDualMeetRaw(meet);
            if (dualMeetData.status === "fail") continue;
            bouts.push(...dualMeetData.data.bouts.map(l => l.id));
        }
        const boutData = await Promise.all(bouts.map(l => this.getBout(l)));

        const databaseUpdates: Record<string, unknown> = {};

        const oldName = `${originalFencer.firstName} ${originalFencer.lastName}`;
        const newName = `${targetFencer.firstName} ${targetFencer.lastName}`;

        for (const bout of boutData) {
            if (bout.status === "fail") continue;
            let changed = false;
            if (bout.data.fencer1.fencerInfo.id === originId) {
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/id`] = targetId;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/firstName`] =
                    targetFencer.firstName;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/lastName`] =
                    targetFencer.lastName;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/gradYear`] =
                    targetFencer.gradYear || null;
                changed = true;
            }
            if (bout.data.fencer2.fencerInfo.id === originId) {
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/id`] = targetId;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/firstName`] =
                    targetFencer.firstName;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/lastName`] =
                    targetFencer.lastName;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/gradYear`] =
                    targetFencer.gradYear || null;
                changed = true;
            }

            if (changed && oldName !== newName) {
                const eventUpdates = await this.getUpdatesToChangeNamesForEvents(bout.data.id, oldName, newName);
                if (eventUpdates.status === "success") {
                    Object.assign(databaseUpdates, eventUpdates.data);
                }
            }
        }

        for (const season of team.seasons) {
            databaseUpdates[`${this.PREFIX}/teams/${team.id}/roster/${season}/Sabre/${originId}`] = null;
            databaseUpdates[`${this.PREFIX}/teams/${team.id}/roster/${season}/Foil/${originId}`] = null;
            databaseUpdates[`${this.PREFIX}/teams/${team.id}/roster/${season}/Epee/${originId}`] = null;
        }

        databaseUpdates[`${this.PREFIX}/fencers/${originId}/bouts`] = [];
        databaseUpdates[`${this.PREFIX}/fencers/${targetId}/bouts`] = [...(targetFencer.bouts || []), ...(originalFencer.bouts || [])];

        const originRecordsRes = await this.getFencerRecord(originId);
        if (originRecordsRes.status === "fail") return originRecordsRes;
        const originRecords = originRecordsRes.data;

        for (const boutId in originRecords.bouts) {
            databaseUpdates[`${this.PREFIX}/fencerRecords/${targetId}/bouts/${boutId}`] = originRecords.bouts[boutId];
        }

        await this.database.ref().update(databaseUpdates);
        return new DBSuccess(true);
    }

    /**
     * Merges two fencers' bout history and records but keeps the roster slot by reassigning it to the target fencer.
     * Intended for cross-weapon merges so the target fencer appears in multiple weapons like addExistingFencer.
     * @param originId Fencer whose bouts/records will be merged into target
     * @param targetId Fencer receiving bouts/records
     * @param teamId Team of both fencers
     */
    public async mergeFencerKeepRoster(originId: string, targetId: string, teamId: string | ITeam): AsyncDBResult<boolean> {
        let team = teamId;
        if (typeof team === "string") {
            const dbteam = await this.getTeam(team);
            if (dbteam.status === "fail") return dbteam;
            team = dbteam.data;
        }

        const [originalFencerResult, targetFencerResult] = await Promise.all([this.getFencer(originId), this.getFencer(targetId)]);
        if (originalFencerResult.status === "fail") return originalFencerResult;
        if (targetFencerResult.status === "fail") return targetFencerResult;
        const originalFencer = originalFencerResult.data;
        const targetFencer = targetFencerResult.data;

        // Gather all bouts for this team
        const bouts: string[] = [];
        const meetIds = Object.values(team.dualMeets).flat();
        for (const meet of meetIds) {
            const dualMeetData = await this.getDualMeetRaw(meet);
            if (dualMeetData.status === "fail") continue;
            bouts.push(...dualMeetData.data.bouts.map(l => l.id));
        }
        const boutData = await Promise.all(bouts.map(l => this.getBout(l)));

        const databaseUpdates: Record<string, unknown> = {};

        // Reassign bouts from origin -> target
        const oldName = `${originalFencer.firstName} ${originalFencer.lastName}`;
        const newName = `${targetFencer.firstName} ${targetFencer.lastName}`;

        for (const bout of boutData) {
            if (bout.status === "fail") continue;
            let changed = false;
            if (bout.data.fencer1.fencerInfo.id === originId) {
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/id`] = targetId;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/firstName`] =
                    targetFencer.firstName;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/lastName`] =
                    targetFencer.lastName;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer1/fencerInfo/gradYear`] =
                    targetFencer.gradYear || null;
                changed = true;
            }
            if (bout.data.fencer2.fencerInfo.id === originId) {
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/id`] = targetId;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/firstName`] =
                    targetFencer.firstName;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/lastName`] =
                    targetFencer.lastName;
                databaseUpdates[`${this.PREFIX}/bouts/${bout.data.id}/fencer2/fencerInfo/gradYear`] =
                    targetFencer.gradYear || null;
                changed = true;
            }

            if (changed && oldName !== newName) {
                const eventUpdates = await this.getUpdatesToChangeNamesForEvents(bout.data.id, oldName, newName);
                if (eventUpdates.status === "success") {
                    Object.assign(databaseUpdates, eventUpdates.data);
                }
            }
        }

        // Merge bouts arrays
        databaseUpdates[`${this.PREFIX}/fencers/${originId}/bouts`] = [];
        databaseUpdates[`${this.PREFIX}/fencers/${targetId}/bouts`] = [
            ...(targetFencer.bouts || []),
            ...(originalFencer.bouts || [])
        ];

        // Merge fencerRecords
        const originRecordsRes = await this.getFencerRecord(originId);
        if (originRecordsRes.status === "success") {
            const originRecords = originRecordsRes.data;
            for (const boutId in originRecords.bouts) {
                databaseUpdates[`${this.PREFIX}/fencerRecords/${targetId}/bouts/${boutId}`] = originRecords.bouts[boutId];
            }
        }

        // Reassign roster slot(s) from origin -> target across all seasons/weapons
        for (const season of team.seasons) {
            const seasonRoster = team.roster?.[season];
            if (!seasonRoster) continue;
            const weapons: (RosterWeapon | "Unsorted")[] = ["Sabre", "Foil", "Epee", "Unsorted"];
            for (const weapon of weapons) {
                const weaponRoster = seasonRoster[weapon as RosterWeapon] as Record<string, { home: number; away: number; power?: number }> | undefined;
                if (!weaponRoster) continue;
                const originSlot = weaponRoster[originId];
                if (!originSlot) continue;

                // If target is not already rostered in this weapon, clone the slot to target
                if (!(targetId in weaponRoster)) {
                    databaseUpdates[`${this.PREFIX}/teams/${team.id}/roster/${season}/${weapon}/${targetId}/home`] = originSlot.home || 0;
                    databaseUpdates[`${this.PREFIX}/teams/${team.id}/roster/${season}/${weapon}/${targetId}/away`] = originSlot.away || 0;
                    if (originSlot.power !== undefined) {
                        databaseUpdates[`${this.PREFIX}/teams/${team.id}/roster/${season}/${weapon}/${targetId}/power`] = originSlot.power;
                    }
                }

                // Remove the origin slot key to avoid dangling duplicate identity
                databaseUpdates[`${this.PREFIX}/teams/${team.id}/roster/${season}/${weapon}/${originId}`] = null;
            }
        }

        await this.database.ref().update(databaseUpdates);
        return new DBSuccess(true);
    }

    public async createNewClonedSeason(
        teamID: string,
        oldSeason: string,
        newSeason: string,
        cloneFromOtherTeam?: string
    ): AsyncDBResult<boolean> {
        const [teamData, otherTeamData] = await Promise.all([
            this.getTeam(teamID),
            cloneFromOtherTeam ? this.getTeam(cloneFromOtherTeam) : null
        ]);
        if (teamData.status === "fail") return new DBError(ERROR_TEAM_DOES_NOT_EXIST);
        if (otherTeamData?.status === "fail") return new DBError(ERROR_TEAM_DOES_NOT_EXIST);
        const team = teamData.data;
        const otherTeam = otherTeamData?.data;

        const oldSeasonRoster = (cloneFromOtherTeam ? otherTeam?.roster[oldSeason] : team.roster[oldSeason]) || {
            Sabre: {},
            Foil: {},
            Epee: {}
        };
        const oldTeamSeasons = (team.seasons || []).filter(Boolean);

        const databaseAdditions = {};

        databaseAdditions[`${this.PREFIX}/teams/${teamID}/seasons`] = [...oldTeamSeasons, newSeason];

        databaseAdditions[`${this.PREFIX}/teams/${teamID}/roster/${newSeason}`] = {
            Sabre: oldSeasonRoster.Sabre ? { ...oldSeasonRoster.Sabre } : {},
            Foil: oldSeasonRoster.Foil ? { ...oldSeasonRoster.Foil } : {},
            Epee: oldSeasonRoster.Epee ? { ...oldSeasonRoster.Epee } : {}
        };

        await this.database.ref().update(databaseAdditions);
        return new DBSuccess(true);
    }

    public async importSeason(teamID: string, season: string, roster: RosterCSV): AsyncDBResult<boolean> {
        if (!teamID || !season) return new DBError(ERROR_UNKNOWN);

        const user = this.auth.currentUser!;
        const d = new Date();

        const newFencers: (IExistingFencer & {
            weapon?: RosterWeapon;
            homeStrip?: number;
            awayStrip?: number;
        })[] = [];

        const teamData = await this.getTeam(teamID);
        if (teamData.status === "fail") return new DBError(ERROR_TEAM_DOES_NOT_EXIST);
        const team = teamData.data;

        const fencers = Object.values(team.fencers);

        for (const fencer of roster) {
            const fencerID = this.database.ref(`${this.PREFIX}/fencers/`).push().key!;

            const newFencer: IExistingFencer & {
                weapon?: RosterWeapon;
                homeStrip?: number;
                awayStrip?: number;
            } = {
                id: fencerID,
                firstName: fencer.firstName,
                lastName: fencer.lastName,
                createdBy: user.uid,
                createdAt: Number(d),
                updatedAt: Number(d),
                pin: getRandomInt(10000, 99999),
                teams: [teamID],
                bouts: [],
                weapon: fencer.weapon,
                homeStrip: fencer.homeStrip,
                awayStrip: fencer.awayStrip,
                gender: GROWN_UP_STR[team.boysAndGirlsTeam]
            };

            if (fencer.gradYear) newFencer.gradYear = fencer.gradYear;
            if (fencer.powerRating) {
                switch (fencer.weapon) {
                    case "Sabre": newFencer.powerRatingSabre = fencer.powerRating; break;
                    case "Foil": newFencer.powerRatingFoil = fencer.powerRating; break;
                    case "Epee": newFencer.powerRatingEpee = fencer.powerRating; break;
                }
            }
            if (fencer.usfaRating && (fencer.weapon === "Sabre" || fencer.weapon === "Foil" || fencer.weapon === "Epee")) {
                this.setWeaponRatingOnFencer(newFencer, fencer.weapon, fencer.usfaRating);
            }

            newFencers.push(newFencer);
        }

        const databaseAdditions = {};

        const filteredFencers = fencers.filter((f: string | null | undefined) => Boolean(f));
        databaseAdditions[`${this.PREFIX}/teams/${teamID}/fencers`] = [...filteredFencers, ...newFencers.map(l => l.id)];

        for (const fencer of newFencers) {
            databaseAdditions[`${this.PREFIX}/fencers/${fencer.id}`] = fencer;

            const weapon = fencer.weapon;
            const home = fencer.homeStrip;
            const away = fencer.awayStrip;

            delete fencer.weapon;
            delete fencer.homeStrip;
            delete fencer.awayStrip;

            databaseAdditions[`${this.PREFIX}/teams/${teamID}/roster/${season}/${weapon}/${fencer.id}`] = {
                home,
                away
            };
        }

        await this.database.ref().update(databaseAdditions);
        return new DBSuccess(true);
    }

    public async addManager(teamID: string, userID: string): AsyncDBResult<boolean> {
        const user = await this.getUserInfo(userID);
        if (user.status === "fail") return user;
        const managers = (await refOnce<ITeam["managers"]>(`${this.PREFIX}/teams/${teamID}/managers`)) || [];
        if (managers.includes(userID)) return new DBSuccess(true);
        await this.database.ref(`${this.PREFIX}/teams/${teamID}/managers`).update({ [managers.length]: userID });
        await this.database.ref(`${this.COMMON_PREFIX}/users/${userID}`).update({
            managingTeams: [...user.data.managingTeams.filter(l => l !== teamID), teamID]
        });
        return new DBSuccess(true);
    }

    public async removeManager(teamID: string, userID: string): AsyncDBResult<boolean> {
        const user = await this.getUserInfo(userID);
        if (user.status === "fail") return user;
        const managers = (await refOnce<ITeam["managers"]>(`${this.PREFIX}/teams/${teamID}/managers`)) || [];
        managers.splice(managers.indexOf(userID), 1);
        await this.database.ref(`${this.PREFIX}/teams/${teamID}`).update({ managers });
        await this.database.ref(`${this.COMMON_PREFIX}/users/${userID}`).update({
            managingTeams: [...user.data.managingTeams.filter(l => l !== teamID)]
        });
        return new DBSuccess(true);
    }

    public async addScorekeeper(teamID: string, userID: string): AsyncDBResult<boolean> {
        const linkedUsers = (await refOnce<ITeam["linkedUsers"]>(`${this.PREFIX}/teams/${teamID}/linkedUsers`)) || {};
        const newValue = (linkedUsers[userID] || 0) | LinkedTeamType.Scorekeeper;
        await this.database.ref(`${this.PREFIX}/teams/${teamID}/linkedUsers`).update({ [userID]: newValue });
        await this.database.ref(`${this.COMMON_PREFIX}/users/${userID}/linkedTeams`).update({ [teamID]: newValue });
        return new DBSuccess(true);
    }

    public async removeScorekeeper(teamID: string, userID: string): AsyncDBResult<boolean> {
        const linkedUsers = (await refOnce<ITeam["linkedUsers"]>(`${this.PREFIX}/teams/${teamID}/linkedUsers`)) || {};
        const newValue = (linkedUsers[userID] || 0) & ~LinkedTeamType.Scorekeeper || null;
        await this.database.ref(`${this.PREFIX}/teams/${teamID}/linkedUsers`).update({ [userID]: newValue });
        await this.database.ref(`${this.COMMON_PREFIX}/users/${userID}/linkedTeams`).update({ [teamID]: newValue });
        return new DBSuccess(true);
    }

    public async addAdministrator(teamID: string, userID: string): AsyncDBResult<boolean> {
        const user = await this.getUserInfo(userID);
        if (user.status === "fail") return user;
        const administrators = (await refOnce<ITeam["administrators"]>(`${this.PREFIX}/teams/${teamID}/administrators`)) || [];
        if (administrators.includes(userID)) return new DBSuccess(true);
        await this.database.ref(`${this.PREFIX}/teams/${teamID}/administrators`).update({ [administrators.length]: userID });
        await this.database.ref(`${this.COMMON_PREFIX}/users/${userID}`).update({
            teams: [...user.data.teams.filter(l => l !== teamID), teamID]
        });
        return new DBSuccess(true);
    }

    public async removeAdministrator(teamID: string, userID: string): AsyncDBResult<boolean> {
        const user = await this.getUserInfo(userID);
        if (user.status === "fail") return user;
        const administrators = (await refOnce<ITeam["administrators"]>(`${this.PREFIX}/teams/${teamID}/administrators`)) || [];
        administrators.splice(administrators.indexOf(userID), 1);
        await this.database.ref(`${this.PREFIX}/teams/${teamID}`).update({ administrators });
        await this.database.ref(`${this.COMMON_PREFIX}/users/${userID}`).update({
            teams: [...user.data.teams.filter(l => l !== teamID)]
        });
        return new DBSuccess(true);
    }

    // #endregion

    // #region Fencer records
    /**
     * Get fencer records for all fencers
     * @param id
     * @param options
     * @returns
     */
    public async getAllFencerRecords(): AsyncDBResult<Record<string, IFencerRecord>> {
        const v = await this.database.ref(`${this.PREFIX}/fencerRecords`).once("value");
        const records = v.val();
        const result: DBResult<Record<string, IFencerRecord>> = records
            ? new DBSuccess(records)
            : new DBError(ERROR_FENCER_RECORDS_DOES_NOT_EXIST);
        return result;
    }

    /**
     * Get fencer records by fencer ID
     * @param id
     * @param options
     * @returns
     */
    public async getFencerRecord(id: string, options?: { listener?: Listener<DBResult<IFencerRecord>> }): AsyncDBResult<IFencerRecord> {
        if (!id || typeof id !== "string") return new DBError(ERROR_FENCER_RECORDS_DOES_NOT_EXIST);
        if (options?.listener) {
            return new Promise(res => {
                this.database.ref(`${this.PREFIX}/fencerRecords/${id}`).on("value", v => {
                    const records = v.val();
                    const result: DBResult<IFencerRecord> = records
                        ? new DBSuccess(records)
                        : new DBError(ERROR_FENCER_RECORDS_DOES_NOT_EXIST);
                    options.listener!(result);
                    res(result);
                });
            });
        } else {
            const v = await this.database.ref(`${this.PREFIX}/fencerRecords/${id}`).once("value");
            const records = v.val();
            const result: DBResult<IFencerRecord> = records ? new DBSuccess(records) : new DBSuccess(ERROR_FENCER_RECORDS_DOES_NOT_EXIST);
            return result;
        }
    }

    public async getDbUpdatesForAddBoutToFencerRecord(
        fencerId: string,
        boutId: string | IDualMeetBout,
        dualMeetData?: IDualMeet_Base
    ): AsyncDBResult<Record<string, unknown>> {
        let bout = boutId;
        if (typeof bout === "string") {
            const boutRequest = await this.getBout(bout);
            if (boutRequest.status === "fail") return boutRequest;
            bout = boutRequest.data;
        }

        let meet = dualMeetData;
        if (!meet && bout.dualMeetId) {
            const meetResult = await this.getDualMeetRaw(bout.dualMeetId, undefined, true);
            if (meetResult.status === "fail") return meetResult;
            meet = meetResult.data;
        }

        const winner = boutWinner(bout, { team: false, requireEnded: false });

        let won = false;
        let lost = false;
        if (winner === BoutSide.Fencer1) {
            if (bout.fencer1.fencerInfo.id === fencerId) won = true;
            if (bout.fencer2.fencerInfo.id === fencerId) lost = true;
        }
        if (winner === BoutSide.Fencer2) {
            if (bout.fencer1.fencerInfo.id === fencerId) lost = true;
            if (bout.fencer2.fencerInfo.id === fencerId) won = true;
        }

        const databaseUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/fencerRecords/${fencerId}/id`]: fencerId,
            [`${this.PREFIX}/fencerRecords/${fencerId}/bouts/${bout.id}/won`]: won,
            [`${this.PREFIX}/fencerRecords/${fencerId}/bouts/${bout.id}/lost`]: lost,
            [`${this.PREFIX}/fencerRecords/${fencerId}/bouts/${bout.id}/season`]: meet!.season,
            [`${this.PREFIX}/fencerRecords/${fencerId}/bouts/${bout.id}/weapon`]: bout.weapon
        };

        return new DBSuccess(databaseUpdates);
    }

    public async getUpdatesToRemoveBoutFromFencerRecord(
        fencerId: string,
        boutId: string | IDualMeetBout,
        dualMeetData?: IDualMeet_Base
    ): AsyncDBResult<Record<string, unknown>> {
        let bout = boutId;
        if (typeof bout === "string") {
            const boutRequest = await this.getBout(bout);
            if (boutRequest.status === "fail") return boutRequest;
            bout = boutRequest.data;
        }

        let meet = dualMeetData;
        if (!meet && bout.dualMeetId) {
            // TODO: add support for tournaments
            const meetResult = await this.getDualMeetRaw(bout.dualMeetId, undefined, true);
            if (meetResult.status === "fail") return meetResult;
            meet = meetResult.data;
        }

        const databaseUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/fencerRecords/${fencerId}/bouts/${boutId}/won`]: null,
            [`${this.PREFIX}/fencerRecords/${fencerId}/bouts/${boutId}/lost`]: null,
            [`${this.PREFIX}/fencerRecords/${fencerId}/bouts/${boutId}/weapon`]: null,
            [`${this.PREFIX}/fencerRecords/${fencerId}/bouts/${boutId}/season`]: null
        };

        return new DBSuccess(databaseUpdates);
    }

    /**
     * Manually searches through all of a fencer's bouts to update their records.
     * @param id Fencer ID
     * @returns
     */
    public async migrateFencerRecords(fencer: string | IExistingFencer, focusedSeason?: string): AsyncDBResult<boolean> {
        let fencerInfo = fencer;
        if (typeof fencerInfo === "string") {
            const res = await this.getFencer(fencerInfo);
            if (res.status === "fail") return res;
            fencerInfo = res.data;
        }
        const id = fencerInfo.id;
        console.log("Migrating fencer records for", id);

        const databaseUpdates: Record<string, unknown> = {};

        const existingRecords = await this.getFencerRecord(id);
        if (existingRecords.status === "success") {
            for (const boutId in existingRecords.data.bouts) {
                databaseUpdates[`${this.PREFIX}/fencerRecords/${id}/bouts/${boutId}/won`] = null;
                databaseUpdates[`${this.PREFIX}/fencerRecords/${id}/bouts/${boutId}/lost`] = null;
                databaseUpdates[`${this.PREFIX}/fencerRecords/${id}/bouts/${boutId}/season`] = null;
                databaseUpdates[`${this.PREFIX}/fencerRecords/${id}/bouts/${boutId}/weapon`] = null;
            }
        }

        for (const teamID of fencerInfo.teams) {
            const teamData = await this.getTeam(teamID);
            if (teamData.status === "fail") continue;
            const team = teamData.data;

            for (const season in team.dualMeets) {
                if (focusedSeason && season !== focusedSeason) continue;
                for (const meet of team.dualMeets[season]) {
                    console.log("Migrating meet", meet, id);
                    try {
                        const meetData = await this.getDualMeetRaw(meet, undefined, true);
                        if (meetData.status === "fail") continue;
                        // Skip deleted meets
                        if (meetData.data.deleted) continue;
                        // console.log("Migrating meet", meetData.data.name);
                        const bouts = await Promise.all(meetData.data.bouts.map(l => this.getBout(l.id, undefined, true)));

                        for (const boutRequest of bouts) {
                            if (boutRequest.status === "fail") continue;
                            const bout = boutRequest.data;
                            if (bout.id === "-OglpbhFERIqERHc0SA3") console.log(bout);
                            if (!(bout.fencer1.fencerInfo.id === id) && !(bout.fencer2.fencerInfo.id === id)) continue;
                            console.log("Migrating bout", bout.id);
                            const winner = boutWinner(bout, { team: false });

                            let won = false;
                            let lost = false;
                            if (winner === BoutSide.Fencer1) {
                                if (bout.fencer1.fencerInfo.id === id) won = true;
                                if (bout.fencer2.fencerInfo.id === id) lost = true;
                            }
                            if (winner === BoutSide.Fencer2) {
                                if (bout.fencer1.fencerInfo.id === id) lost = true;
                                if (bout.fencer2.fencerInfo.id === id) won = true;
                            }

                            databaseUpdates[`${this.PREFIX}/fencerRecords/${id}/id`] = id;
                            databaseUpdates[`${this.PREFIX}/fencerRecords/${id}/bouts/${bout.id}/won`] = won;
                            databaseUpdates[`${this.PREFIX}/fencerRecords/${id}/bouts/${bout.id}/lost`] = lost;
                            databaseUpdates[`${this.PREFIX}/fencerRecords/${id}/bouts/${bout.id}/season`] = season;
                            databaseUpdates[`${this.PREFIX}/fencerRecords/${id}/bouts/${bout.id}/weapon`] = bout.weapon;
                        }
                    } catch (e: unknown) {
                        // asdf
                    }
                }
            }
        }

        await this.database.ref().update(databaseUpdates);
        console.log("Finished migrating fencer records for", id);
        return new DBSuccess(true);
    }

    // TODO: remove DBResult maybe?
    public async getUpdatesToSubBoutRecord(
        boutId: string | IDualMeetBout,
        side: BoutSide,
        newId: string | null
    ): AsyncDBResult<Record<string, unknown>> {
        let bout = boutId;
        if (typeof bout === "string") {
            const response = await this.getBout(bout);
            if (response.status === "fail") return response;
            bout = response.data;
        }

        const oldFencer = bout[`fencer${side}`].fencerInfo.id;

        const databaseUpdates: Record<string, unknown> = {};

        databaseUpdates[`${this.PREFIX}/fencerRecords/${oldFencer}/bouts/${bout.id}/won`] = null;
        databaseUpdates[`${this.PREFIX}/fencerRecords/${oldFencer}/bouts/${bout.id}/lost`] = null;
        databaseUpdates[`${this.PREFIX}/fencerRecords/${oldFencer}/bouts/${bout.id}/season`] = null;
        databaseUpdates[`${this.PREFIX}/fencerRecords/${oldFencer}/bouts/${bout.id}/weapon`] = null;

        if (newId) {
            const oldRecord = (await this.database.ref(`${this.PREFIX}/fencerRecords/${oldFencer}/bouts/${bout.id}`).once("value")).val();
            databaseUpdates[`${this.PREFIX}/fencerRecords/${newId}/bouts/${bout.id}/won`] = oldRecord?.won ?? null;
            databaseUpdates[`${this.PREFIX}/fencerRecords/${newId}/bouts/${bout.id}/lost`] = oldRecord?.lost ?? null;
            databaseUpdates[`${this.PREFIX}/fencerRecords/${newId}/bouts/${bout.id}/season`] = oldRecord?.season ?? null;
            databaseUpdates[`${this.PREFIX}/fencerRecords/${newId}/bouts/${bout.id}/weapon`] = oldRecord?.weapon ?? null;
        }

        return new DBSuccess(databaseUpdates);
    }

    public async subBoutRecord(boutId: string | IDualMeetBout, side: BoutSide, newId: string | null): AsyncDBResult<boolean> {
        const updates = this.getUpdatesToSubBoutRecord(boutId, side, newId);
        await this.database.ref().update(updates);
        return new DBSuccess(true);
    }

    // #endregion

    // #region Rankings

    public async getRankings(
        season: string,
        weapon: Weapon,
        gender: "man" | "woman" | "both"
    ): AsyncDBResult<Record<string, IFencerRanking>> {
        const v = await this.database.ref(`${this.PREFIX}/rankings/${season}/${weapon}/${gender}`).once("value");
        const val = v.val() || {};

        return new DBSuccess(val);
    }

    public async getLastRankingsUpdate(season: string): AsyncDBResult<number> {
        const v = await this.database.ref(`${this.PREFIX}/rankings/${season}/updatedAt`).once("value");
        const val = v.val() || {};

        return new DBSuccess(val);
    }

    public async calculateRankings(season: string): AsyncDBResult<boolean> {
        const calculateRankings = this.functions.httpsCallable("calculateRankings");
        const result = await calculateRankings({ prefix: this.PREFIX, season });
        return result.data;
    }

    public async calculateSingleFencerRanking(season: string, fencerID: string, weapon: Weapon): AsyncDBResult<FencerCalculationData> {
        const calculateSingleFencerRanking = this.functions.httpsCallable("calculateSingleFencerRanking");
        const result = await calculateSingleFencerRanking({
            prefix: this.PREFIX,
            season,
            fencerID,
            weapon
        });
        return result.data;
    }

    public async getPowerPoints(season: string, gender: "boys" | "girls"): AsyncDBResult<Record<string, IPowerPointsStats>> {
        const v = await this.database.ref(`${this.PREFIX}/powerPoints/${season}/${gender}`).once("value");
        const val = v.val() || {};
        return new DBSuccess(val);
    }

    public async getLastPowerPointsUpdate(season: string): AsyncDBResult<number> {
        const v = await this.database.ref(`${this.PREFIX}/powerPoints/${season}/updatedAt`).once("value");
        const val = v.val() || {};

        return new DBSuccess(val);
    }

    public async calculatePowerPoints(season: string): AsyncDBResult<boolean> {
        const calculatePowerPoints = this.functions.httpsCallable("calculatePowerPoints");
        const result = await calculatePowerPoints({ prefix: this.PREFIX, season });
        return result.data;
    }

    public async getHighSchoolBoutReport(season: string, conference: string): AsyncDBResult<IHighSchoolBoutReportData> {
        const getHighSchoolBoutReport = this.functions.httpsCallable("getHighSchoolBoutReport");
        const result = await getHighSchoolBoutReport({
            prefix: this.PREFIX,
            season,
            conference
        });
        return result.data;
    }

    public async getSPI(season: string, weapon: Weapon, gender: "mens" | "womens"): AsyncDBResult<Record<string, ISPIResult>> {
        const v = await this.database.ref(`${this.PREFIX}/spi/${season}/${weapon}/${gender}`).once("value");
        const val = v.val() || {};
        return new DBSuccess(val);
    }

    public async getLastSPIUpdate(season: string): AsyncDBResult<number> {
        const v = await this.database.ref(`${this.PREFIX}/spi/${season}/updatedAt`).once("value");
        const val = v.val() || {};
        return new DBSuccess(val);
    }

    public async calculateSPI(season: string): AsyncDBResult<boolean> {
        const calculateSPI = this.functions.httpsCallable("calculateSPI");
        const result = await calculateSPI({ prefix: this.PREFIX, season });
        return result.data;
    }

    public async getTeamSPI(season: string, gender: "mens" | "womens"): AsyncDBResult<Record<string, ITeamSPIResult>> {
        const v = await this.database.ref(`${this.PREFIX}/teamSpi/${season}/${gender}`).once("value");
        const val = v.val() || {};
        return new DBSuccess(val);
    }

    public async getSquadSPI(season: string, gender: "mens" | "womens"): AsyncDBResult<Record<string, ISquadSPIResult>> {
        const v = await this.database.ref(`${this.PREFIX}/squadSpi/${season}/${gender}`).once("value");
        const val = v.val() || {};
        return new DBSuccess(val);
    }

    public async getLastTeamSPIUpdate(season: string): AsyncDBResult<number> {
        const v = await this.database.ref(`${this.PREFIX}/teamSpi/${season}/updatedAt`).once("value");
        const val = v.val() || 0;
        return new DBSuccess(val);
    }

    public async getLastSquadSPIUpdate(season: string): AsyncDBResult<number> {
        const v = await this.database.ref(`${this.PREFIX}/squadSpi/${season}/updatedAt`).once("value");
        const val = v.val() || 0;
        return new DBSuccess(val);
    }

    public async calculateTeamSPI(season: string): AsyncDBResult<boolean> {
        const calculateTeamSPI = this.functions.httpsCallable("calculateTeamSPI");
        const result = await calculateTeamSPI({ prefix: this.PREFIX, season });
        return result.data;
    }

    // #endregion

    // #region Invites

    public async inviteAdmin(team: string): AsyncDBResult<boolean> {
        const userRequest = await this.getCurrentUserInfo();
        if (userRequest.status === "fail") return userRequest;
        const user = userRequest.data;
        const newInviteRef = this.database.ref(`${this.PREFIX}/invites/`).push();
        const code = genRandomStr().slice(0, 6).toUpperCase();
        const invite: ICoachInvite = {
            id: newInviteRef.key!,
            type: "team",
            team,
            code,
            createdAt: Date.now(),
            createdBy: user.id,
            expiresAt: Date.now() + 1000 * 60 * 60 * 24,
            claimed: false
        };

        await newInviteRef.set(invite);
        return new DBSuccess(true);
    }

    public async deleteInvite(id: string): AsyncDBResult<boolean> {
        await this.database.ref(`${this.PREFIX}/invites/${id}`).set(null);
        return new DBSuccess(true);
    }

    public async joinTeamUsingInvite(code: string): AsyncDBResult<boolean> {
        const user = await this.getCurrentUserInfo();
        if (user.status === "fail") return new DBError(ERROR_NOT_LOGGED_IN);

        const matchingInvite = await this.getInviteFromCode(code);
        if (matchingInvite.status === "fail") return matchingInvite;
        if (matchingInvite.data.claimed) return new DBError(ERROR_INVITE_CLAIMED);

        await this.database.ref(`${this.PREFIX}/invites/${matchingInvite.data.id}`).update({
            "/claimed": true,
            "/claimedAt": Date.now()
        });
        const success = await this.addAdministrator(matchingInvite.data.team, user.data.id);
        return success;
    }

    public async getInviteFromCode(code: string): AsyncDBResult<ICoachInvite> {
        const v = await this.database.ref(`${this.PREFIX}/invites`).orderByChild("code").equalTo(code.toUpperCase()).once("value");
        if (!v.val()) return new DBError(ERROR_INVALID_INVITE);
        const invite = Object.values(v.val())[0] as ICoachInvite;
        if (!invite) return new DBError(ERROR_INVALID_INVITE);
        return new DBSuccess(invite);
    }

    public async getFencerInviteFromCode(code: string): AsyncDBResult<ICoachInvite> {
        const getFencerInviteFromCode = this.functions.httpsCallable("getFencerInviteFromCode");
        const result = await getFencerInviteFromCode({ prefix: this.PREFIX, code });
        return result.data;
    }

    public async getInvite(id: string, listener?: (v: ICoachInvite | null) => void): Promise<ICoachInvite | null> {
        if (listener) {
            return new Promise(res => {
                this.database.ref(`${this.PREFIX}/invites/${id}`).on("value", v => {
                    const val = v.val();
                    listener(val);
                    res(val);
                });
            });
        } else {
            const v = await this.database.ref(`${this.PREFIX}/invites/${id}`).once("value");
            return v.val();
        }
    }

    public async getInvitesForTeam(
        team: string,
        listener?: (v: Record<string, ICoachInvite>) => void
    ): Promise<Record<string, ICoachInvite>> {
        if (listener) {
            return new Promise(res => {
                this.database
                    .ref(`${this.PREFIX}/invites`)
                    .orderByChild("team")
                    .equalTo(team)
                    .on("value", v => {
                        const val = v.val() || {};
                        listener(val);
                        res(val);
                    });
            });
        } else {
            const v = await this.database.ref(`${this.PREFIX}/invites`).orderByChild("team").equalTo(team).once("value");
            return v.val() || {};
        }
    }

    // #endregion

    // #region Dual Meets

    /**
     * Get raw dual meet list
     */
    public async getDualMeetDirectory(options: {
        season: number;
        limit?: number;
        endBeforeTimestamp?: number;
    }): AsyncDBResult<Record<string, IDirectoryDualMeet>> {
        let query = this.database.ref(`${this.PREFIX}/dualMeetsDirectory`).orderByChild("startedAt");
        const start = new Date(`${options.season}-08-01`).getTime(); // Aug 1 of season
        const end = new Date(`${options.season + 1}-07-31T23:59:59.999Z`).getTime(); // July 31 of next year
        query = query.startAt(start);
        if (options?.limit) query = query.limitToLast(options.limit);
        if (options?.endBeforeTimestamp) {
            query = query.endAt(Math.min(end, options.endBeforeTimestamp - 1));
        } else {
            query = query.endAt(end);
        }

        const v = await query.once("value");
        const results: Record<string, IDirectoryDualMeet> = v.val() || {};
        const userInfo = await this.getCurrentUserInfo();
        if (userInfo.status === "fail" || !(userInfo.data.flags & UserFlag.SuperAdmin)) {
            for (const i in results) {
                if (results[i].deleted) delete results[i];
            }
        }
        return new DBSuccess(results);
    }

    /**
     * Get dual meet list (with caching)
     * @param listener Listener function
     */
    public async getDualMeets(listener?: Listener<DBResult<Record<string, IDualMeet>>>): AsyncDBResult<Record<string, IDualMeet>> {
        if (listener) {
            return new Promise(async res => {
                let found = false;
                if (Object.keys(this.dualMeets).length) {
                    found = true;
                    const processedArr = await Promise.all(Object.values(this.dualMeets).map(meet => this.processMeet(meet, true)));
                    const filtered = processedArr.filter(isSuccess).map(l => l.data);
                    const processed = Object.fromEntries(filtered.map(l => [l.id, l]));
                    const response = new DBSuccess(processed);
                    listener(response);
                    res(response);
                }
                const l = async v => {
                    const meets: Record<string, IDualMeet_DB> = v.val() || {};
                    const processedMeetPromises = await Promise.all(Object.values(meets).map(meet => this.processMeet(meet)));
                    const result: Record<string, IDualMeet> = {};
                    for (const meet of processedMeetPromises) {
                        if (meet.status === "fail") continue;
                        this.dualMeets[meet.data.id] = meet.data;
                        result[meet.data.id] = meet.data;
                    }
                    const response = new DBSuccess(result);
                    listener(response);
                    res(response);
                };
                this.database.ref(`${this.PREFIX}/dualMeets`).on("value", l);
                this.listeners.set(listener, l);
            });
        } else {
            const v = await this.database.ref(`${this.PREFIX}/dualMeets`).once("value");
            const meets = (v.val() || {}) as Record<string, IDualMeet_DB>;
            const processedMeetPromises = await Promise.all(Object.values(meets).map(l => this.processMeet(l)));
            const result: Record<string, IDualMeet> = {};
            for (const meet of processedMeetPromises) {
                if (meet.status === "fail") continue;
                this.dualMeets[meet.data.id] = meet.data;
                result[meet.data.id] = meet.data;
            }
            return new DBSuccess(result);
        }
    }

    public getLiveDualMeets(listener?: Listener<DBResult<IDualMeet[]>>): AsyncDBResult<IDualMeet[]> {
        return new Promise(res => {
            const todaysDate = new Date();
            todaysDate.setHours(0, 0, 0, 0);
            const startTimeStamp = todaysDate.getTime(); // yesterday
            const endTimeStamp = startTimeStamp + 86400000; // tomorrow
            this.database
                .ref(`${this.PREFIX}/dualMeets`)
                .orderByChild("startedAt")
                .startAt(startTimeStamp)
                .endAt(endTimeStamp)
                .on("value", async queryResults => {
                    const val: Record<string, IDualMeet> | null = queryResults.val();

                    const resolved = val ? Object.values(val).filter(l => l.published && !l.deleted) : [];

                    listener && listener(new DBSuccess(resolved));
                    res(new DBSuccess(resolved));
                });
        });
    }

    public stopListeningLiveDualMeets() {
        removeRefListener(`${this.PREFIX}/dualMeets`);
    }

    /**
     * Get dual meet (with caching)
     * @param id Dual meet id
     * @param listener Listener function
     */
    public async getDualMeet(id: string, listener?: Listener<DBResult<IDualMeet>>): AsyncDBResult<IDualMeet> {
        if (!id || typeof id !== "string") return new DBError(ERROR_DUAL_MEET_DOES_NOT_EXIST);
        if (listener) {
            return new Promise(async res => {
                let found = false;
                if (this.dualMeets[id]) {
                    found = true;
                    const processed = await this.processMeet(this.dualMeets[id], false);
                    listener(processed);
                    res(processed);
                }
                const l = async v => {
                    const meet = v.val();
                    if (meet) {
                        this.dualMeets[id] = meet;
                        this.cache.queueSave("dualMeets");
                        const realMeet = await this.processMeet(meet);
                        listener(realMeet);
                        if (!found) {
                            res(realMeet);
                        }
                    } else {
                        const error = new DBError(ERROR_DUAL_MEET_DOES_NOT_EXIST);
                        listener(error);
                        res(error);
                    }
                };
                this.database.ref(`${this.PREFIX}/dualMeets/${id}`).on("value", l);
                this.listeners.set(listener, l);
            });
        } else {
            const meet = await refOnce<IDualMeet_DB>(`${this.PREFIX}/dualMeets/${id}`);
            if (meet) {
                const realMeet = await this.processMeet(meet);
                if (realMeet.status === "success") {
                    this.dualMeets[id] = realMeet.data;
                    this.cache.queueSave("dualMeets");
                }
                return realMeet;
            } else {
                return new DBError(ERROR_DUAL_MEET_DOES_NOT_EXIST);
            }
        }
    }

    /**
     * Stops listening to a dual meet (both processed and raw)
     * @param id Dual meet id
     * @param listener Listener function
     */
    public stopListeningDualMeet(id: string, listener?: (val: unknown) => void) {
        const l = (listener && this.listeners.get(listener)) || listener;
        removeRefListener(`${this.PREFIX}/dualMeets/${id}`, l);
    }

    /**
     * Get dual meet score only
     * @param id Dual meet id
     * @param listener Listener function
     */
    public async getDualMeetRaw(id: string, listener?: Listener<DBResult<IDualMeet_DB>>, forceCache = false): AsyncDBResult<IDualMeet_DB> {
        if (listener) {
            return new Promise(async res => {
                let found = false;
                if (this.dualMeets[id]) {
                    found = true;
                    const processed = new DBSuccess(this.dualMeets[id]);
                    listener(processed);
                    res(processed);
                }
                const l = async v => {
                    const meet = v.val() as IDualMeet_DB;
                    if (meet) {
                        this.dualMeets[id] = meet;
                        this.cache.queueSave("dualMeets");
                        const response = new DBSuccess(meet);
                        listener(response);
                        if (!found) {
                            res(response);
                        }
                    } else {
                        res(new DBError(ERROR_DUAL_MEET_DOES_NOT_EXIST));
                    }
                };
                this.database.ref(`${this.PREFIX}/dualMeets/${id}`).on("value", l);
                this.listeners.set(listener, l);
            });
        } else {
            if (forceCache) {
                if (id in this.dualMeets) {
                    return new DBSuccess(this.dualMeets[id]);
                }
            }
            const meet = await refOnce<IDualMeet_DB>(`${this.PREFIX}/dualMeets/${id}`);
            if (meet) {
                this.dualMeets[id] = meet;
                this.cache.queueSave("dualMeets");
                return new DBSuccess(meet);
            } else {
                return new DBError(ERROR_DUAL_MEET_DOES_NOT_EXIST);
            }
        }
    }

    private async getDBUpdatesForBout(
        isTournamentBout: boolean,
        eventId: string,
        weapon: Weapon,
        team1ID: string,
        team2ID: string,
        fencer1ID: string | null,
        fencer2ID: string | null,
        boutOrder: number
    ): AsyncDBResult<{
        id: string;
        fencer1ID: string;
        fencer2ID: string;
        fencer1Name: string;
        fencer2Name: string;
        updates: Record<string, unknown>;
    }> {
        const databaseUpdates: Record<string, unknown> = {};
        const boutID = this.database.ref(`${this.PREFIX}/bouts/`).push().key!;

        const team1Result = await this.getTeam(team1ID, { forceCache: true });
        if (team1Result.status === "fail") return team1Result;
        const team1 = team1Result.data;

        let fencer1Info: IExistingFencer | undefined;
        if (fencer1ID) {
            const fencer1Result = await this.getFencer(fencer1ID);
            if (fencer1Result.status === "fail") return fencer1Result;
            fencer1Info = fencer1Result.data;
        }

        if (fencer1Info) {
            databaseUpdates[`${this.PREFIX}/fencers/${fencer1ID}/bouts/${fencer1Info.bouts.length}`] = boutID;
        }

        // We have to do this stuff because firebase hates undefined
        const fencer1RealID = fencer1Info?.id || `writeIn${Math.floor(Math.random() * 100000)}`;
        const fencer1Obj: IDualMeetBoutFencerInfo = {
            firstName: fencer1Info?.firstName || "Unknown",
            lastName: fencer1Info?.lastName || "fencer",
            teamName: team1.name || "Unknown team",
            teamAbbreviation: team1.abbreviation || genAbbreviationFromName(team1.name || "") || "",
            id: fencer1RealID
        };

        if (fencer1Info?.gradYear) fencer1Obj.gradYear = fencer1Info.gradYear;

        // Potential for perf improvements here by getting all the fencers used in one batch to avoid duplicates but w/e
        const team2Result = await this.getTeam(team2ID, { forceCache: true });
        if (team2Result.status === "fail") return team2Result;
        const team2 = team2Result.data;

        let fencer2Info: IExistingFencer | undefined;
        if (fencer2ID) {
            const fencer2Result = await this.getFencer(fencer2ID);
            if (fencer2Result.status === "fail") return fencer2Result;
            fencer2Info = fencer2Result.data;
        }

        if (fencer2Info) {
            databaseUpdates[`${this.PREFIX}/fencers/${fencer2ID}/bouts/${fencer2Info.bouts.length}`] = boutID;
        }

        const fencer2RealID = fencer2Info?.id || `writeIn${Math.floor(Math.random() * 100000)}`;
        const fencer2Obj: IDualMeetBoutFencerInfo = {
            firstName: fencer2Info?.firstName || "Unknown",
            lastName: fencer2Info?.lastName || "fencer",
            teamName: team2.name || "Unknown team",
            teamAbbreviation: team2.abbreviation || genAbbreviationFromName(team2.name || "") || "",
            id: fencer2RealID
        };

        if (fencer2Info?.gradYear) fencer2Obj.gradYear = fencer2Info.gradYear;

        const strips: [number, number] = isTournamentBout
            ? ([
                  [3, 3],
                  [2, 2],
                  [1, 1]
              ][boutOrder] as [number, number])
            : [
                  [3, 6] as [number, number],
                  [1, 5] as [number, number],
                  [2, 4] as [number, number],
                  [1, 6] as [number, number],
                  [3, 4] as [number, number],
                  [2, 5] as [number, number],
                  [1, 4] as [number, number],
                  [2, 6] as [number, number],
                  [3, 5] as [number, number]
              ][boutOrder];

        const bout: IDualMeetBout = {
            id: boutID,
            [isTournamentBout ? "poolId" : "dualMeetId"]: eventId,
            color: genRandomColor(),
            currentSpectatorsNum: 0,
            log: [],
            order: boutOrder,
            pin: getRandomInt(10000, 99999),
            weapon,
            type: BoutType.DualMeet,
            priority: null,
            fencer1: {
                fencerInfo: fencer1Obj!,
                cardInfo: { yellow: false, red: 0, black: false },
                score: 0,
                timeout: false,
                strip: strips[0],
                forfeit: false
            },
            fencer2: {
                fencerInfo: fencer2Obj!,
                cardInfo: { yellow: false, red: 0, black: false },
                score: 0,
                timeout: false,
                strip: strips[1],
                forfeit: false
            },
            switchedSides: false,
            boutTime: 180000
        };

        databaseUpdates[`${this.PREFIX}/bouts/${bout.id}`] = bout;

        return new DBSuccess({
            id: boutID,
            updates: databaseUpdates,
            fencer1ID: fencer1RealID,
            fencer2ID: fencer2RealID,
            fencer1Name: fencer1Info?.firstName || "Unknown fencer",
            fencer2Name: fencer2Info?.firstName || "Unknown fencer"
        });
    }

    private async getDbUpdatesForDualMeet(
        id: string,
        team1:
            | ITeam
            | {
                  name: string;
                  abbreviation: string;
                  fencers: IDualMeetTeam["fencers"];
              },
        team2:
            | ITeam
            | {
                  name: string;
                  abbreviation: string;
                  fencers: IDualMeetTeam["fencers"];
              },
        type: DualMeetType,
        season: string,
        startDate: Date,
        customName?: string,
        complementId?: string,
        eventId?: string
    ) {
        const databaseAdditions: Record<string, unknown> = {};
        const pin1 = getRandomInt(10000, 99999);
        const pin2 = getRandomInt(10000, 99999);
        const d = new Date();
        const user = this.auth.currentUser!;

        if ("id" in team1) {
            databaseAdditions[`${this.PREFIX}/teams/${team1.id}/dualMeets/${season}`] = [...(team1.dualMeets?.[season] || []), id];
        }

        if ("id" in team2) {
            databaseAdditions[`${this.PREFIX}/teams/${team2.id}/dualMeets/${season}`] = [...(team2.dualMeets?.[season] || []), id];
        }

        const team1Name = team1.name;
        const team2Name = team2.name;
        const team1Abbreviation = team1.abbreviation;
        const team2Abbreviation = team2.abbreviation;

        const [team1Roster, team2Roster] = [
            ("id" in team1 ? team1?.roster?.[season] : team1.fencers)!,
            ("id" in team2 ? team2?.roster?.[season] : team2.fencers)!
        ];

        const fencerBouts: Record<string, string[]> = {};

        let writeInIdx = 0;
        const bouts: IDualMeetBout[] = [];
        const weapons: Weapon[] = ["Sabre", "Foil", "Epee"];
        for (const weapon of weapons) {
            const stripOrder: [number, number][] = [
                [3, 6] as [number, number],
                [1, 5] as [number, number],
                [2, 4] as [number, number],
                [1, 6] as [number, number],
                [3, 4] as [number, number],
                [2, 5] as [number, number],
                [1, 4] as [number, number],
                [2, 6] as [number, number],
                [3, 5] as [number, number]
            ];
            let boutOrder = 0;

            for (const strips of stripOrder) {
                const boutID = this.database.ref(`${this.PREFIX}/bouts/`).push().key!;
                let fencer1Obj: IDualMeetBoutFencer["fencerInfo"] | undefined;
                if ("id" in team1) {
                    const fencer1ID = team1Roster?.[weapon]
                        ? Object.entries(team1Roster[weapon]).find(l => l[1].home === strips[0])?.[0]
                        : undefined;
                    const fencer1Info = fencer1ID ? await refOnce<IExistingFencer>(`${this.PREFIX}/fencers/${fencer1ID}`) : undefined;
                    if (fencer1ID && !fencerBouts[fencer1ID]) {
                        fencerBouts[fencer1ID] = fencer1Info?.bouts || [];
                    }
                    if (fencer1ID) {
                        fencerBouts[fencer1ID] = [...fencerBouts[fencer1ID], boutID];
                    }
                    // We have to do this stuff because firebase hates undefined
                    fencer1Obj = {
                        firstName: fencer1Info?.firstName || "Unknown",
                        lastName: fencer1Info?.lastName || "fencer",
                        teamName: team1.name || "Unknown team",
                        teamAbbreviation: team1.abbreviation || genAbbreviationFromName(team1.name || "") || "",
                        id: fencer1Info?.id || `writeIn${writeInIdx++}`
                    };

                    if (fencer1Info?.gradYear) fencer1Obj.gradYear = fencer1Info.gradYear;
                } else {
                    const fencer1 = team1.fencers[weapon].find(l => l.home === strips[0]);
                    fencer1Obj = {
                        firstName: fencer1?.firstName || "Unknown",
                        lastName: fencer1?.lastName || "fencer",
                        teamAbbreviation: team1Abbreviation,
                        teamName: team1Name
                    };
                }

                let fencer2Obj: IDualMeetBoutFencer["fencerInfo"] | undefined;
                if ("id" in team2) {
                    const fencer2ID = team2Roster?.[weapon]
                        ? Object.entries(team2Roster[weapon]).find(l => l[1].away === strips[1])?.[0]
                        : undefined;
                    // Potential for perf improvements here by getting all the fencers used in one batch to avoid duplicates but w/e
                    const fencer2Info = fencer2ID ? await refOnce<IExistingFencer>(`${this.PREFIX}/fencers/${fencer2ID}`) : undefined;

                    if (fencer2ID && !fencerBouts[fencer2ID]) {
                        fencerBouts[fencer2ID] = fencer2Info?.bouts || [];
                    }

                    if (fencer2ID) {
                        fencerBouts[fencer2ID] = [...fencerBouts[fencer2ID], boutID];
                    }

                    fencer2Obj = {
                        firstName: fencer2Info?.firstName || "Unknown",
                        lastName: fencer2Info?.lastName || "fencer",
                        teamName: team2.name || "Unknown team",
                        teamAbbreviation: team2.abbreviation || genAbbreviationFromName(team2.name || "") || "",
                        id: fencer2Info?.id || `writeIn${writeInIdx++}`
                    };

                    if (fencer2Info?.gradYear) fencer2Obj.gradYear = fencer2Info.gradYear;
                } else {
                    const fencer2 = team2.fencers[weapon].find(l => l.away === strips[1]);
                    fencer2Obj = {
                        firstName: fencer2?.firstName || "Unknown",
                        lastName: fencer2?.lastName || "fencer",
                        teamAbbreviation: team2Abbreviation,
                        teamName: team2Name
                    };
                }

                const bout: IDualMeetBout = {
                    id: boutID,
                    dualMeetId: id,
                    color: genRandomColor(),
                    currentSpectatorsNum: 0,
                    log: [],
                    order: boutOrder++,
                    pin: getRandomInt(10000, 99999),
                    weapon,
                    type: BoutType.DualMeet,
                    priority: null,
                    fencer1: {
                        fencerInfo: fencer1Obj!,
                        cardInfo: { yellow: false, red: 0, black: false },
                        score: 0,
                        timeout: false,
                        strip: strips[0],
                        forfeit: false
                    },
                    fencer2: {
                        fencerInfo: fencer2Obj!,
                        cardInfo: { yellow: false, red: 0, black: false },
                        score: 0,
                        timeout: false,
                        strip: strips[1],
                        forfeit: false
                    },
                    switchedSides: false,
                    boutTime: 180000
                };

                bouts.push(bout);
                databaseAdditions[`${this.PREFIX}/bouts/${bout.id}`] = bout;
            }
        }

        for (const fencerID in fencerBouts) {
            databaseAdditions[`${this.PREFIX}/fencers/${fencerID}/bouts`] = fencerBouts[fencerID];
        }

        const reorderedBouts: IDualMeetBout[] = [];

        // For each round
        for (let i = 0; i < 3; i++) {
            // For each weapon
            for (let j = 0; j < 3; j++) {
                // For each bout
                for (let k = 0; k < 3; k++) {
                    reorderedBouts.push(bouts[j * 9 + i * 3 + k]);
                }
            }
        }

        const nameStrA = type === DualMeetType.CollegeEvent ? "A" : "H";
        const nameStrB = type === DualMeetType.CollegeEvent ? "B" : "A";

        const newDualMeet: IDualMeet_DB = {
            id,
            type,
            pin1,
            pin2,
            color: `rgba(${getRandomInt(0, 100)}, ${getRandomInt(0, 230)}, ${getRandomInt(0, 255)}, ${getRandomInt(40, 80) / 100})`,
            createdAt: Number(d),
            createdBy: user.uid,
            published: false,
            season,
            name: customName || `${team1Name} (${nameStrA}) vs. ${team2Name} (${nameStrB})`,
            bouts: reorderedBouts.map(l => ({ id: l.id, winner: 0 })),
            startedAt: Number(startDate)
        };

        if ("id" in team1) {
            newDualMeet.team1ID = team1.id;
        } else {
            newDualMeet.team1WriteInData = {
                name: team1.name,
                administrators: [],
                managers: [],
                fencers: team1.fencers,
                abbreviation: team1.abbreviation || genAbbreviationFromName(team1.name)
            };
        }

        if ("id" in team2) {
            newDualMeet.team2ID = team2.id;
        } else {
            newDualMeet.team2WriteInData = {
                name: team2.name,
                administrators: [],
                managers: [],
                fencers: team2.fencers,
                abbreviation: team2.abbreviation || genAbbreviationFromName(team2.name)
            };
        }

        if (complementId) {
            newDualMeet.correspondingMeet = complementId;
            databaseAdditions[`${this.PREFIX}/dualMeets/${complementId}/correspondingMeet`] = id;
        }

        if (eventId) {
            newDualMeet.eventID = eventId;
        }

        databaseAdditions[`${this.PREFIX}/dualMeets/${id}`] = newDualMeet;

        databaseAdditions[`${this.PREFIX}/dualMeetsDirectory/${id}`] = { name: newDualMeet.name, startedAt: newDualMeet.startedAt };

        return databaseAdditions;
    }

    public async createNewDualMeet(
        team1:
            | string
            | {
                  name: string;
                  abbreviation: string;
                  fencers: IDualMeetTeam["fencers"];
              },
        team2:
            | string
            | {
                  name: string;
                  abbreviation: string;
                  fencers: IDualMeetTeam["fencers"];
              },
        type: DualMeetType,
        season: string,
        startDate: Date,
        customName?: string,
        complementId?: string,
        eventId?: string
    ): AsyncDBResult<string> {
        const user = this.auth.currentUser!;
        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);

        let team1Data: string | ITeam | { name: string; abbreviation: string; fencers: IDualMeetTeam["fencers"] } = team1;
        if (typeof team1Data === "string") {
            const response = await this.getTeam(team1Data, { forceCache: false });
            if (response.status === "fail") return response;
            team1Data = response.data;
        }
        let team2Data: string | ITeam | { name: string; abbreviation: string; fencers: IDualMeetTeam["fencers"] } = team2;
        if (typeof team2Data === "string") {
            const response = await this.getTeam(team2Data, { forceCache: false });
            if (response.status === "fail") return response;
            team2Data = response.data;
        }

        const createNewDualMeet = this.functions.httpsCallable("createNewDualMeet");
        const result = await createNewDualMeet({
            prefix: this.PREFIX,
            team1: team1Data,
            team2: team2Data,
            type,
            season,
            startDate: startDate.getTime(),
            customName,
            complementId
        });

        return result.data;
    }

    public async getDbUpdatesForDeletingDualMeet(meetID: string, teams?: Record<string, ITeam>): AsyncDBResult<Record<string, unknown>> {
        const databaseUpdates: Record<string, unknown> = {};

        const meetRequest = await this.getDualMeetRaw(meetID);
        if (meetRequest.status === "fail") return meetRequest;
        const meetData = meetRequest.data;

        // Delete all the bouts and the meet itself
        for (const bout of meetData.bouts) {
            databaseUpdates[`${this.PREFIX}/bouts/${bout.id}`] = null;
        }
        databaseUpdates[`${this.PREFIX}/dualMeets/${meetID}`] = null;

        if (meetData.team1ID) {
            const team1 = teams?.[meetData.team1ID] || (await refOnce<ITeam>(`${this.PREFIX}/teams/${meetData.team1ID}`));
            const team1Meets = [...(team1?.dualMeets?.[meetData.season] || [])];
            if (team1Meets.includes(meetID)) {
                team1Meets.splice(team1Meets.indexOf(meetID), 1);
            }
            databaseUpdates[`${this.PREFIX}/teams/${meetData.team1ID}/dualMeets/${meetData.season}`] = team1Meets;
        }
        if (meetData.team2ID) {
            const team2 = teams?.[meetData.team2ID] || (await refOnce<ITeam>(`${this.PREFIX}/teams/${meetData.team2ID}`));
            const team2Meets = [...(team2?.dualMeets?.[meetData.season] || [])];
            if (team2Meets.includes(meetID)) {
                team2Meets.splice(team2Meets.indexOf(meetID), 1);
            }
            databaseUpdates[`${this.PREFIX}/teams/${meetData.team2ID}/dualMeets/${meetData.season}`] = team2Meets;
        }

        // Get all the bouts
        const boutsData = await Promise.all(meetData.bouts.map(l => this.getBout(l.id)));

        // Remove all the events
        for (const bout of boutsData) {
            if (bout.status === "fail") continue;
            for (const event of bout.data.log || []) {
                if (!event.trim()) continue;
                databaseUpdates[`${this.PREFIX}/boutEvents/${event}`] = null;
            }
        }

        // Get all the participating fencers
        // We also do fencer record updates here
        const fencers: string[] = [];

        for (const bout of boutsData) {
            if (bout.status === "fail") continue;
            const fencer1ID = bout.data.fencer1.fencerInfo.id;
            const fencer2ID = bout.data.fencer2.fencerInfo.id;
            if (fencer1ID && !fencer1ID.startsWith("writeIn") && !fencers.includes(fencer1ID)) {
                fencers.push(fencer1ID);
            }
            if (fencer2ID && !fencer2ID.startsWith("writeIn") && !fencers.includes(fencer2ID)) {
                fencers.push(fencer2ID);
            }
            databaseUpdates[`${this.PREFIX}/fencerRecords/${fencer1ID}/bouts/${bout.data.id}`] = null;
            databaseUpdates[`${this.PREFIX}/fencerRecords/${fencer2ID}/bouts/${bout.data.id}`] = null;
        }

        const fencersData = await Promise.all(fencers.map(l => this.getFencer(l)));

        // Filter out every fencer's bouts and their records
        for (const fencer of fencersData) {
            if (fencer.status === "fail") continue;
            const modifiedBouts = (fencer.data.bouts || []).filter(l => !meetData.bouts.some(j => j.id === l));
            databaseUpdates[`${this.PREFIX}/fencers/${fencer.data.id}/bouts`] = modifiedBouts;
        }

        // Are we in an event?
        eventHandler: if (meetData.eventID) {
            const eventRequest = await this.getCollegeEvent(meetData.eventID);
            if (eventRequest.status === "fail") break eventHandler;
            const event = eventRequest.data;

            const mens = event.mensRounds.some(l => l.meets.some(j => j.id === meetData.id));

            if (mens) {
                const roundIdx = event.mensRounds.findIndex(l => l.meets.some(j => j.id === meetData.id));
                const filtered = event.mensRounds[roundIdx].meets.filter(l => l.id !== meetData.id);
                event.mensRounds[roundIdx].meets = filtered;
                databaseUpdates[`${this.PREFIX}/events/${event.id}/mensRounds/${roundIdx}/meets`] = filtered;

                const byeUpdatesResult = await this.getCollegeEventByeUpdates(event, "mens");
                if (byeUpdatesResult.status === "fail") return byeUpdatesResult;
                for (const i in byeUpdatesResult.data) {
                    databaseUpdates[i] = byeUpdatesResult.data[i];
                }
            }

            const womens = event.womensRounds.some(l => l.meets.some(j => j.id === meetData.id));

            if (womens) {
                const roundIdx = event.womensRounds.findIndex(l => l.meets.some(j => j.id === meetData.id));
                const filtered = event.womensRounds[roundIdx].meets.filter(l => l.id !== meetData.id);
                event.womensRounds[roundIdx].meets = filtered;
                databaseUpdates[`${this.PREFIX}/events/${event.id}/womensRounds/${roundIdx}/meets`] = filtered;

                const byeUpdatesResult = await this.getCollegeEventByeUpdates(event, "womens");
                if (byeUpdatesResult.status === "fail") return byeUpdatesResult;
                for (const i in byeUpdatesResult.data) {
                    databaseUpdates[i] = byeUpdatesResult.data[i];
                }
            }
        }

        databaseUpdates[`${this.PREFIX}/dualMeetsDirectory/${meetID}`] = null;

        return new DBSuccess(databaseUpdates);
    }

    public async deleteDualMeet(dualMeetID: string): AsyncDBResult<boolean> {
        const deleteDualMeet = this.functions.httpsCallable("deleteDualMeet");
        const result = await deleteDualMeet({
            prefix: this.PREFIX,
            dualMeetID
        });

        if (result.data.status === "fail") return result.data;

        delete this.dualMeets[dualMeetID];
        return result.data;
    }

    public async undeleteDualMeet(dualMeetID: string): AsyncDBResult<boolean> {
        const undeleteDualMeet = this.functions.httpsCallable("undeleteDualMeet");
        const result = await undeleteDualMeet({
            prefix: this.PREFIX,
            dualMeetID
        });

        if (result.data.status === "fail") return result.data;

        // Invalidate cache to force reload
        delete this.dualMeets[dualMeetID];
        return result.data;
    }

    public async hardDeleteDualMeet(meetID: string): AsyncDBResult<void> {
        const updatesResult = await this.getDbUpdatesForDeletingDualMeet(meetID);
        if (updatesResult.status === "fail") return updatesResult;

        const updateResult = await this.updateDatabase(updatesResult.data);
        if (updateResult.status === "fail") return updateResult;

        delete this.dualMeets[meetID];
        return new DBSuccess(void 0);
    }

    public async setDualMeetDate(meetID: string, date: Date) {
        // Validate that date is a proper Date object
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            throw new Error("Invalid date provided to setDualMeetDate");
        }

        const setDualMeetDate = this.functions.httpsCallable("setDualMeetDate");
        const result = await setDualMeetDate({
            prefix: this.PREFIX,
            dualMeetID: meetID,
            date: date.getTime()
        });

        if (result.data.status === "fail") return result.data;

        this.dualMeets[meetID].startedAt = date.getTime();
        this.cache.queueSave("dualMeets");
        return result.data;
    }

    public async publishDualMeet(dualMeetID: string): AsyncDBResult<boolean> {
        try {
            // Get the meet data first
            const meetResult = await this.getDualMeetRaw(dualMeetID);
            if (meetResult.status === "fail") return meetResult;
            const meet = meetResult.data;

            const databaseUpdates: Record<string, unknown> = {
                [`${this.PREFIX}/dualMeets/${dualMeetID}/published`]: true
            };

            const relevantBouts = meet.bouts.filter(l => l.winner !== 0);

            // Add all bouts to fencer records
            await Promise.all(
                relevantBouts.map(async boutInfo => {
                    // Get the full bout data
                    const boutResult = await this.getBout(boutInfo.id);
                    if (boutResult.status === "fail") return undefined;
                    const bout = boutResult.data;

                    if (!bout.endedAt) return undefined; // Skip unfinished bouts

                    // Skip bouts where fencer IDs are undefined
                    if (!bout.fencer1.fencerInfo.id || !bout.fencer2.fencerInfo.id) return undefined;

                    // Add bout to both fencers' records
                    const addUpdates1 = await this.getDbUpdatesForAddBoutToFencerRecord(bout.fencer1.fencerInfo.id, bout.id, meet);
                    if (addUpdates1.status === "fail") return undefined;
                    Object.assign(databaseUpdates, addUpdates1.data);

                    const addUpdates2 = await this.getDbUpdatesForAddBoutToFencerRecord(bout.fencer2.fencerInfo.id, bout.id, meet);
                    if (addUpdates2.status === "fail") return undefined;
                    Object.assign(databaseUpdates, addUpdates2.data);
                    return undefined;
                })
            );

            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e) {
            return new DBError((e as Error).message);
        }
    }

    public async unpublishDualMeet(dualMeetID: string): AsyncDBResult<boolean> {
        try {
            // Get the meet data first
            const meetResult = await this.getDualMeetRaw(dualMeetID);
            if (meetResult.status === "fail") return meetResult;
            const meet = meetResult.data;

            const databaseUpdates: Record<string, unknown> = {
                [`${this.PREFIX}/dualMeets/${dualMeetID}/published`]: false
            };

            const relevantBouts = meet.bouts.filter(l => l.winner !== 0);

            await Promise.all(
                relevantBouts.map(async boutInfo => {
                    // Get the full bout data
                    const boutResult = await this.getBout(boutInfo.id);
                    if (boutResult.status === "fail") return undefined;
                    const bout = boutResult.data;

                    // Skip bouts where fencer IDs are undefined
                    if (!bout.fencer1.fencerInfo.id || !bout.fencer2.fencerInfo.id) return undefined;

                    // Remove bout from both fencers' records
                    const removeUpdates1 = await this.getUpdatesToRemoveBoutFromFencerRecord(bout.fencer1.fencerInfo.id, bout.id, meet);
                    if (removeUpdates1.status === "fail") return undefined;
                    Object.assign(databaseUpdates, removeUpdates1.data);

                    const removeUpdates2 = await this.getUpdatesToRemoveBoutFromFencerRecord(bout.fencer2.fencerInfo.id, bout.id, meet);
                    if (removeUpdates2.status === "fail") return undefined;
                    Object.assign(databaseUpdates, removeUpdates2.data);
                    return undefined;
                })
            );

            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e) {
            return new DBError((e as Error).message);
        }
    }

    /**
     * Ends a dual meet, either prematurely or after all bouts have concluded.
     * `force` forces the dual meet to end, no matter what, without forfeitting (admin purposes or when meet has concluded).
     * `left` and `right` forfeit the side for the side provided in the argument
     */
    public async endDualMeet(id: string, forfeit: "force" | "left" | "right") {
        const databaseAdditions: Record<string, unknown> = {};

        if (forfeit !== "force") {
            const meetInfo = await refOnce<IDualMeet_DB>(`${this.PREFIX}/dualMeets/${id}`);

            const bouts = await Promise.all(meetInfo.bouts.map(l => refOnce<IDualMeetBout>(`${this.PREFIX}/bouts/${l}`)));

            for (const bout of bouts.filter(l => !l.endedAt)) {
                databaseAdditions[`${this.PREFIX}/bouts/${bout.id}/fencer${forfeit === "left" ? "1" : "2"}/forfeit`] = true;
            }
        }

        databaseAdditions[`${this.PREFIX}/dualMeets/${id}/endedAt`] = Date.now();

        this.database.ref().update(databaseAdditions);
    }

    public processMeet = async (meet: IDualMeet_DB, forceCache = false): AsyncDBResult<IDualMeet> => {
        try {
            console.time("Processing meet");
            const realMeet: Partial<IDualMeet & IDualMeet_DB> = { ...meet };
            delete realMeet.team1Attendance;
            delete realMeet.team1AttendanceSignature;
            delete realMeet.team1ID;
            delete realMeet.team1WriteInData;
            delete realMeet.team2Attendance;
            delete realMeet.team2AttendanceSignature;
            delete realMeet.team2ID;
            delete realMeet.team2WriteInData;
            if (
                !realMeet.endedAt &&
                realMeet.startedAt &&
                realMeet.startedAt + 86400000 < Date.now()
            ) {
                const endedDate = realMeet.startedAt + 86400000;
                await this.database.ref(`${this.PREFIX}/dualMeets/${meet.id}`).update({ endedAt: endedDate });
            }
            team1: if (meet.team1ID) {
                const teamRequest = await this.getTeam(meet.team1ID, { forceCache });
                if (teamRequest.status === "fail") break team1;
                const team = teamRequest.data;

                let district: string | undefined = undefined;
                if (team.orgID) {
                    const orgRequest = await this.getOrganization(team.orgID, { forceCache });
                    if (orgRequest.status === "success") {
                        district = orgRequest.data.district;
                    }
                }

                const seasonRoster = team.roster[meet.season];
                const meetTeamFencers: IDualMeetTeam["fencers"] = {
                    Sabre: [],
                    Foil: [],
                    Epee: []
                };
                for (const weapon in seasonRoster) {
                    if (weapon === "Armorer" || weapon === "Unsorted") continue;
                    const arr: IDualMeetTeam["fencers"]["Sabre"] = [];
                    for (const id in seasonRoster[weapon]) {
                        const fencer = await this.getFencer(id);
                        if (fencer.status === "fail") continue;
                        arr.push({
                            ...fencer.data,
                            home: seasonRoster[weapon][id],
                            away: 0,
                            teamName: team.name
                        });
                    }
                    meetTeamFencers[weapon] = arr;
                }

                const meetTeam: IDualMeetTeam = {
                    id: meet.team1ID,
                    administrators: team.administrators,
                    managers: team.managers,
                    name: team.name,
                    fencers: meetTeamFencers,
                    abbreviation: team.abbreviation || genAbbreviationFromName(team.name),
                    attendance: meet.team1Attendance,
                    attendanceSignature: meet.team1AttendanceSignature,
                    conference: typeof team.conference === "string" ? team.conference : team.conference?.[0],
                    district
                };
                realMeet.team1 = meetTeam;
            } else if (meet.team1WriteInData) {
                realMeet.team1 = meet.team1WriteInData;
            }
            team2: if (meet.team2ID) {
                const teamRequest = await this.getTeam(meet.team2ID, { forceCache });
                if (teamRequest.status === "fail") break team2;
                const team = teamRequest.data;

                let district: string | undefined = undefined;
                if (team.orgID) {
                    const orgRequest = await this.getOrganization(team.orgID, { forceCache });
                    if (orgRequest.status === "success") {
                        district = orgRequest.data.district;
                    }
                }

                const seasonRoster = team.roster[meet.season];
                const meetTeamFencers: IDualMeetTeam["fencers"] = {
                    Sabre: [],
                    Foil: [],
                    Epee: []
                };
                for (const weapon in seasonRoster) {
                    if (weapon === "Armorer" || weapon === "Unsorted") continue;
                    const arr: IDualMeetTeam["fencers"]["Sabre"] = [];
                    for (const id in seasonRoster[weapon]) {
                        const fencer = await this.getFencer(id);
                        if (fencer.status === "fail") continue;
                        arr.push({
                            ...fencer.data,
                            away: seasonRoster[weapon][id],
                            home: 0,
                            teamName: team.name
                        });
                    }
                    meetTeamFencers[weapon] = arr;
                }
                const meetTeam: IDualMeetTeam = {
                    id: meet.team2ID,
                    administrators: team.administrators,
                    managers: team.managers,
                    name: team.name,
                    fencers: meetTeamFencers,
                    abbreviation: team.abbreviation || genAbbreviationFromName(team.name),
                    attendance: meet.team2Attendance,
                    attendanceSignature: meet.team2AttendanceSignature,
                    conference: typeof team.conference === "string" ? team.conference : team.conference?.[0],
                    district
                };
                realMeet.team2 = meetTeam;
            } else if (meet.team2WriteInData) {
                realMeet.team2 = meet.team2WriteInData;
            }
            console.timeEnd("Processing meet");
            return new DBSuccess(realMeet as IDualMeet);
        } catch {
            return new DBError(ERROR_PROCESSING_MEET);
        }
    };

    public stopListeningDualMeetsRaw(listener?: (val: unknown) => void) {
        const l = this.listeners.get(listener);
        if (!l) return;
        removeRefListener(`${this.PREFIX}/dualMeets`, l);
    }

    public stopListeningDualMeets(listener?: (val: unknown) => void) {
        const l = this.listeners.get(listener);
        if (!l) return;
        removeRefListener(`${this.PREFIX}/dualMeets`, l);
    }

    public async signMeet(meetID: string, category: DualMeetSignatureKey | "allRefs", signature: string): AsyncDBResult<boolean> {
        if (!signature) return new DBError("No signature provided.");
        if (category === "allRefs") {
            await this.database.ref(`${this.PREFIX}/dualMeets/${meetID}/signatures`).update({
                sabreRef: signature,
                foilRef: signature,
                epeeRef: signature
            });
        } else {
            await this.database.ref(`${this.PREFIX}/dualMeets/${meetID}/signatures/${category}`).set(signature);
        }
        return new DBSuccess(true);
    }

    public async setMeetReferee(id: string, names: Partial<Record<Weapon, string>>) {
        const databaseAdditions = {};

        for (const i in names) {
            databaseAdditions[`${this.PREFIX}/dualMeets/${id}/${i.toLowerCase()}Referee`] = names[i];
        }

        await this.database.ref().update(databaseAdditions);

        return true;
    }

    public async setSingleReferee(id: string, value: boolean) {
        await this.database.ref(`${this.PREFIX}/dualMeets/${id}/singleReferee`).set(value);
        return true;
    }

    public async setAttendance(id: string, team: "team1" | "team2", fencers: ID[], signature: string) {
        await this.database.ref(`${this.PREFIX}/dualMeets/${id}/${team}Attendance`).set(fencers);
        await this.database.ref(`${this.PREFIX}/dualMeets/${id}/${team}AttendanceSignature`).set(signature);
        return true;
    }

    public async setScoresheet(id: string, path: string | null, type: "foil" | "epee" | "sabre" | "all") {
        await this.database.ref(`${this.PREFIX}/dualMeets/${id}/scoresheets/${type}`).set(path);
        return true;
    }

    /**
     * In case the meet's cached score is out of sync, it checks each bout and repairs the cache score to the proper score
     * @param id
     */
    public async repairMeetScore(id: string): AsyncDBResult<boolean>;
    public async repairMeetScore(meet: IDualMeet): AsyncDBResult<boolean>;
    public async repairMeetScore(idOrMeet: string | IDualMeet_DB): AsyncDBResult<boolean> {
        let meet = idOrMeet;
        if (typeof meet === "string") {
            const meetResult = await this.getDualMeetRaw(meet);
            if (meetResult.status === "fail") return meetResult;
            meet = meetResult.data;
        }
        const databaseUpdates: Record<string, unknown> = {};
        const boutResults = await Promise.all(meet.bouts.map(l => this.getBout(l.id)));
        for (let i = 0; i < meet.bouts.length; i++) {
            const result = boutResults[i];
            if (result.status === "fail") return result;
            const winner = boutWinner(result.data, { team: true }) ?? 0;
            console.log(winner, result.data, meet.bouts[i].winner);
            if (winner !== meet.bouts[i].winner) {
                databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/bouts/${i}/winner`] = winner;
            }
        }

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async unlockDualMeet(id: string): AsyncDBResult<boolean> {
        try {
            const databaseUpdates: Record<string, unknown> = {};

            databaseUpdates[`${this.PREFIX}/dualMeets/${id}/unlockedAt`] = Date.now();
            databaseUpdates[`${this.PREFIX}/dualMeets/${id}/unlockedDuration`] = 24 * 60 * 60 * 1000;

            await this.database.ref().update(databaseUpdates);

            return new DBSuccess(true);
        } catch (e) {
            return new DBError((e as Error).message);
        }
    }

    public async lockDualMeet(id: string): AsyncDBResult<boolean> {
        const databaseUpdates: Record<string, unknown> = {};

        databaseUpdates[`${this.PREFIX}/dualMeets/${id}/unlockedAt`] = null;
        databaseUpdates[`${this.PREFIX}/dualMeets/${id}/unlockedDuration`] = null;

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    // #endregion

    // #region Bouts

    public getBout(id: string, listener?: Listener<DBResult<IDualMeetBout>>, forceCache = true): AsyncDBResult<IDualMeetBout> {
        if (!id || typeof id !== "string") return Promise.resolve(new DBError(ERROR_BOUT_DOES_NOT_EXIST));
        if (listener) {
            return new Promise(async (res, rej) => {
                let found = false;
                if (this.dualMeetBouts[id]) {
                    found = true;
                    res(new DBSuccess(this.dualMeetBouts[id]));
                }
                const l = v => {
                    const val: IDualMeetBout = v.val();
                    const bout: IDualMeetBout = { ...DEFAULT_BOUT, ...val };
                    if (bout) {
                        this.dualMeetBouts[id] = bout;
                        const response = new DBSuccess(bout);
                        listener(response);
                        if (!found) {
                            res(response);
                        }
                    } else {
                        return res(new DBError(ERROR_BOUT_DOES_NOT_EXIST));
                    }
                };
                this.listeners.set(listener, l);
                this.database.ref(`${this.PREFIX}/bouts/${id}`).on("value", l);
            });
        } else {
            return new Promise(async (res, rej) => {
                if (forceCache) {
                    if (id in this.dualMeetBouts) {
                        return res(new DBSuccess(this.dualMeetBouts[id]));
                    }
                }
                const boutRaw = await refOnce<IDualMeetBout>(`${this.PREFIX}/bouts/${id}`);
                if (boutRaw) {
                    const bout = { ...DEFAULT_BOUT, ...boutRaw };
                    this.dualMeetBouts[id] = bout;
                    res(new DBSuccess(bout));
                } else {
                    return res(new DBError(ERROR_BOUT_DOES_NOT_EXIST));
                }
            });
        }
    }

    public stopListeningBout(id: string, listener?: (val: unknown) => void) {
        const l = (listener && this.listeners.get(listener)) || listener;
        removeRefListener(`${this.PREFIX}/bouts/${id}`, l);
    }

    public async getBoutEvent(id: string): AsyncDBResult<IBoutEvent> {
        if (id in this.boutEvents) {
            return new DBSuccess(this.boutEvents[id]);
        } else {
            const event = await refOnce<IBoutEvent>(`${this.PREFIX}/boutEvents/${id}`);
            this.boutEvents[id] = event;
            if (event) {
                return new DBSuccess(event);
            } else {
                return new DBError(ERROR_BOUT_EVENT_DOES_NOT_EXIST);
            }
        }
    }

    public getBoutEventsFromArray(ids: string[]) {
        return Promise.all(ids.map(l => this.getBoutEvent(l)));
    }

    public updateBout(boutID: string, attributes: Record<string, unknown>, time?: number) {
        const d = Date.now();
        const updates: Record<string, unknown> = {
            ...attributes,
            updatedAt: d
        };
        if (time !== undefined) {
            updates.boutTime = time;
        }
        this.database.ref(`${this.PREFIX}/bouts/${boutID}`).update(updates);
    }

    public async endBout(boutData: string | IBoutCore, winner: BoutSide | 0, dualMeetId?: string): AsyncDBResult<boolean> {
        try {
            const userInfoResult = await this.getCurrentUserInfo();
            if (userInfoResult.status === "fail") return userInfoResult;
            const userInfo = userInfoResult.data;

            let bout = boutData;
            if (typeof bout === "string") {
                const boutResult = await this.getBout(bout);
                if (boutResult.status === "fail") return boutResult;
                bout = boutResult.data;
            }

            const d = Date.now();
            let databaseUpdates: Record<string, unknown> = {
                [`${this.PREFIX}/bouts/${bout.id}/updatedAt`]: d,
                [`${this.PREFIX}/bouts/${bout.id}/endedAt`]: d
            };

            if (dualMeetId) {
                const meet = await this.getDualMeet(dualMeetId);
                if (meet.status === "fail") return meet;
                const boutIdx = meet.data.bouts.findIndex(l => l.id === bout.id);

                databaseUpdates[`${this.PREFIX}/dualMeets/${dualMeetId}/bouts/${boutIdx}/winner`] = winner;

                if (!meet.data.published) {
                    databaseUpdates[`${this.PREFIX}/dualMeets/${dualMeetId}/published`] = true;
                    databaseUpdates[`${this.PREFIX}/dualMeets/${dualMeetId}/publishedAt`] = Date.now();
                    databaseUpdates[`${this.PREFIX}/dualMeets/${dualMeetId}/publishedBy`] = userInfo.id;
                }

                if ("id" in bout.fencer1.fencerInfo) {
                    const fencerId = bout.fencer1.fencerInfo.id;
                    if (fencerId) {
                        const fencer1Updates = await this.getDbUpdatesForAddBoutToFencerRecord(fencerId, bout as IDualMeetBout, meet.data);
                        if (fencer1Updates.status === "fail") return fencer1Updates;
                        databaseUpdates = { ...databaseUpdates, ...fencer1Updates.data };

                        if (winner !== 0 && meet.data.eventID) {
                            const fencerData = await this.getFencer(fencerId);
                            if (fencerData.status === "fail") return fencerData;
                            const team = await this.getTeam(meet.data.team1.id!, { forceCache: true });
                            if (team.status === "fail") return team;
                            const gender: "mens" | "womens" = team.data.boysAndGirlsTeam === "boys" ? "mens" : "womens";
                            databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/updatedAt`] = Date.now();
                            databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${fencerId}/name`] =
                                `${fencerData.data.firstName} ${fencerData.data.lastName}`;
                            databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${fencerId}/teamID`] =
                                meet.data.team1.id!;
                            databaseUpdates[
                                `${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${fencerId}/bouts/${bout.weapon}/${bout.id}`
                            ] = {
                                opposingTeamID: meet.data.team2.id!,
                                winner: winner === BoutSide.Fencer1 ? 1 : 2,
                                ts: bout.fencer1.score,
                                tr: bout.fencer2.score
                            } as ICollegeEventFencerResult["bouts"][Weapon][ID];
                        }
                    }
                }

                if ("id" in bout.fencer2.fencerInfo) {
                    const fencerId = bout.fencer2.fencerInfo.id;
                    if (fencerId) {
                        const fencer2Updates = await this.getDbUpdatesForAddBoutToFencerRecord(fencerId, bout as IDualMeetBout, meet.data);
                        if (fencer2Updates.status === "fail") return fencer2Updates;
                        databaseUpdates = { ...databaseUpdates, ...fencer2Updates.data };

                        if (winner !== 0 && meet.data.eventID) {
                            const fencerData = await this.getFencer(fencerId);
                            if (fencerData.status === "fail") return fencerData;
                            const team = await this.getTeam(meet.data.team2.id!, { forceCache: true });
                            if (team.status === "fail") return team;
                            const gender: "mens" | "womens" = team.data.boysAndGirlsTeam === "boys" ? "mens" : "womens";
                            databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/updatedAt`] = Date.now();
                            databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${fencerId}/name`] =
                                `${fencerData.data.firstName} ${fencerData.data.lastName}`;
                            databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${fencerId}/teamID`] =
                                meet.data.team2.id!;
                            databaseUpdates[
                                `${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${fencerId}/bouts/${bout.weapon}/${bout.id}`
                            ] = {
                                opposingTeamID: meet.data.team1.id!,
                                winner: winner === BoutSide.Fencer2 ? 1 : 2,
                                ts: bout.fencer2.score,
                                tr: bout.fencer1.score
                            } as ICollegeEventFencerResult["bouts"][Weapon][ID];
                        }
                    }
                }
            }

            if (bout.type === BoutType.DualMeet && (bout as IDualMeetBout).poolId) {
                const pool = await this.getTournamentPool((bout as IDualMeetBout).poolId!, { preferCache: true });
                if (pool.status === "fail") return pool;
                const poolData = pool.data;

                if (poolData.variant === "cba") {
                    let team1Index = -1;
                    let team2Index = -1;
                    let strip: "C" | "B" | "A" = "C";

                    let finishedBefore = true;
                    for (const i in poolData.bouts) {
                        for (const j in poolData.bouts[i]) {
                            if (!poolData.bouts[i][j]) continue;
                            if (
                                poolData.bouts[i][j].C.winner === undefined ||
                                poolData.bouts[i][j].B.winner === undefined ||
                                poolData.bouts[i][j].A.winner === undefined
                            )
                                finishedBefore = false;
                            if (
                                poolData.bouts[i][j].C.id === bout.id ||
                                poolData.bouts[i][j].B.id === bout.id ||
                                poolData.bouts[i][j].A.id === bout.id
                            ) {
                                team1Index = Number(i);
                                team2Index = Number(j);
                                if (poolData.bouts[i][j].C.id === bout.id) {
                                    strip = "C";
                                } else if (poolData.bouts[i][j].B.id === bout.id) {
                                    strip = "B";
                                } else if (poolData.bouts[i][j].A.id === bout.id) {
                                    strip = "A";
                                }
                            }
                        }
                    }

                    if (team1Index !== -1 && team2Index !== -1) {
                        const boutPrefix = `${this.PREFIX}/tournament-pools/${poolData.id}/bouts/${team1Index}/${team2Index}/${strip}`;
                        databaseUpdates[`${boutPrefix}/winner`] = winner;
                        databaseUpdates[`${boutPrefix}/score1`] = bout.fencer1.score;
                        databaseUpdates[`${boutPrefix}/score2`] = bout.fencer2.score;

                        poolData.bouts[team1Index][team2Index][strip].winner = winner;
                        poolData.bouts[team1Index][team2Index][strip].score1 = bout.fencer1.score;
                        poolData.bouts[team1Index][team2Index][strip].score2 = bout.fencer2.score;
                    }

                    let finished = true;
                    for (const i in poolData.bouts) {
                        for (const j in poolData.bouts[i]) {
                            if (!poolData.bouts[i][j]) continue;
                            if (poolData.bouts[i][j].C.winner || poolData.bouts[i][j].B.winner || poolData.bouts[i][j].A.winner)
                                finished = false;
                        }
                    }
                    databaseUpdates[`${this.PREFIX}/tournament-rounds/${poolData.roundId}/pools/${poolData.id}/completed`] = finished;

                    if (finished) {
                        const roundResult = await this.getTournamentRound(poolData.roundId);
                        if (roundResult.status === "fail") return roundResult;
                        const roundData = roundResult.data;

                        let allFinished = true;
                        for (const id in roundData.pools) {
                            if (!roundData.pools[id].completed) {
                                allFinished = false;
                                break;
                            }
                        }

                        const eventResult = await this.getTournamentEvent(poolData.eventId, { preferCache: true });
                        if (eventResult.status === "fail") return eventResult;
                        const eventData = eventResult.data;

                        const roundIdx = eventData.rounds.findIndex(l => l.id === roundData.id);

                        databaseUpdates[`${this.PREFIX}/tournament-events/${poolData.eventId}/rounds/${roundIdx}/finished`] = allFinished;
                    }
                }

                const roundUpdateResult = await this.getDbUpdatesForRoundResultsFromPool(poolData.roundId, poolData);
                if (roundUpdateResult.status === "fail") return roundUpdateResult;
                Object.assign(databaseUpdates, roundUpdateResult.data);
            }

            await this.database.ref().update(databaseUpdates);

            return new DBSuccess(true);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async unendBout(boutData: string | IBoutCore, winner: BoutSide | 0, dualMeetId?: string): AsyncDBResult<boolean> {
        let bout = boutData;
        if (typeof bout === "string") {
            const boutResult = await this.getBout(bout);
            if (boutResult.status === "fail") return boutResult;
            bout = boutResult.data;
        }

        const d = Date.now();
        let databaseUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/bouts/${bout.id}/updatedAt`]: d
        };

        if (dualMeetId) {
            const meet = await this.getDualMeet(dualMeetId);
            if (meet.status === "fail") return meet;
            const boutIdx = meet.data.bouts.findIndex(l => l.id === bout.id);

            databaseUpdates[`${this.PREFIX}/dualMeets/${dualMeetId}/bouts/${boutIdx}/winner`] = winner;

            if ("id" in bout.fencer1.fencerInfo) {
                const fencerId = bout.fencer1.fencerInfo.id;
                if (fencerId) {
                    const fencer1Updates = this.getDbUpdatesForAddBoutToFencerRecord(fencerId, bout as IDualMeetBout, meet.data);
                    databaseUpdates = { ...databaseUpdates, ...fencer1Updates };
                }
            }

            if ("id" in bout.fencer2.fencerInfo) {
                const fencerId = bout.fencer2.fencerInfo.id;
                if (fencerId) {
                    const fencer2Updates = this.getDbUpdatesForAddBoutToFencerRecord(fencerId, bout as IDualMeetBout, meet.data);
                    databaseUpdates = { ...databaseUpdates, ...fencer2Updates };
                }
            }
        }

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async createBoutEvent(boutID: string, event: Omit<IBoutEvent, "id">) {
        const eventID = this.database.ref(`${this.PREFIX}/boutEvents`).push().key;

        // TODO: put revision calcuation in the actual scorer instead of getting bout data again
        const boutData = await refOnce<IDualMeetBout>(`${this.PREFIX}/bouts/${boutID}`);
        const eventIdx = boutData.log?.length || 0;

        const copiedEvent = { ...event, id: eventID };

        await this.database.ref(`${this.PREFIX}/boutEvents/${eventID}`).set(copiedEvent);
        this.database.ref(`${this.PREFIX}/bouts/${boutID}/log/${eventIdx}`).set(eventID);
    }

    public async reviseBoutEvent(boutID: string, eventIdx: number, event: Omit<IBoutEvent, "id">) {
        const eventID = this.database.ref(`${this.PREFIX}/boutEvents`).push().key;

        // TODO: put revision calcuation in the actual scorer instead of getting bout data again
        const boutData = await refOnce<IDualMeetBout>(`${this.PREFIX}/bouts/${boutID}`);

        const copiedEvent = { ...event, id: eventID };

        if (boutData?.endedAt) {
            copiedEvent.description = "Revision: " + copiedEvent.description;
        }

        await this.database.ref(`${this.PREFIX}/boutEvents/${eventID}`).set(copiedEvent);
        this.database.ref(`${this.PREFIX}/bouts/${boutID}/log/${eventIdx}`).set(eventID);
    }

    /**
     * Get database updates for bout events when substitute a fencer in for another fencer. Only replaces the old fencer's name with the new names
     * @param boutID ID for the bout whose events will be replaced
     * @param oldName Name that will be replaced
     * @param newName Name that will show in the new descriptions
     * @param options Function configuration options
     */
    public async getUpdatesToChangeNamesForEvents(
        boutID: string,
        oldName: string,
        newName: string,
        options?: { updateCache: boolean }
    ): AsyncDBResult<Record<string, unknown>> {
        const databaseUpdates: Record<string, unknown> = {};

        const bout = await this.getBout(boutID, undefined, true);
        if (bout.status === "fail") return bout;

        const boutEventsRaw = await this.getBoutEventsFromArray(bout.data.log);
        const boutEvents = boutEventsRaw.filter(isSuccess).map(l => l.data);

        console.log(boutEvents);

        for (const event of boutEvents) {
            if (!event?.description) continue;
            const newDesc = event.description.replaceAll(oldName, newName);
            console.log(oldName, newName, event.description, newDesc);
            if (newDesc !== event.description) {
                databaseUpdates[`${this.PREFIX}/boutEvents/${event.id}/description`] = newDesc;
            }

            if (options?.updateCache) {
                if (this.boutEvents[event.id]) {
                    this.boutEvents[event.id].description = newDesc;
                }
            }
        }

        return new DBSuccess(databaseUpdates);
    }

    /**
     * Changes the descriptions for all bout events to substitute a fencer in for another fencer. Only replaces the old fencer's name with the new names
     * @param boutID ID for the bout whose events will be replaced
     * @param oldName Name that will be replaced
     * @param newName Name that will show in the new descriptions
     * @param options Function configuration options
     */
    public async changeNamesForEvents(boutID: string, oldName: string, newName: string): AsyncDBResult<boolean> {
        const updates = await this.getUpdatesToChangeNamesForEvents(boutID, oldName, newName, { updateCache: true });
        if (updates.status === "fail") return updates;

        await this.database.ref().update(updates.data);
        return new DBSuccess(true);
    }

    public async getDbUpdatesToSubBoutFencer(
        boutID: string,
        side: "left" | "right",
        fencerID: string | IExistingFencer,
        medical: boolean,
        looped: boolean,
        recordsCount: boolean
    ): AsyncDBResult<Record<string, unknown>> {
        let fencer = fencerID;
        if (typeof fencer === "string") {
            const fencerRequest = await this.getFencer(fencer);
            if (fencerRequest.status === "fail") return fencerRequest;
            fencer = fencerRequest.data;
        }

        const boutResult = await this.getBout(boutID, undefined, true);
        if (boutResult.status === "fail") return boutResult;
        const boutData = boutResult.data;

        const oldFencerID = (side === "left" ? boutData.fencer1 : boutData.fencer2).fencerInfo.id;
        const oldFencerResult = oldFencerID ? await this.getFencer(oldFencerID) : new DBError("");
        const oldFencer = oldFencerResult.status === "success" ? oldFencerResult.data : undefined;

        const sideStr = side === "left" ? 1 : 2;

        let databaseUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/bouts/${boutID}/fencer${sideStr}/fencerInfo/firstName`]: fencer.firstName,
            [`${this.PREFIX}/bouts/${boutID}/fencer${sideStr}/fencerInfo/lastName`]: fencer.lastName,
            [`${this.PREFIX}/bouts/${boutID}/fencer${sideStr}/fencerInfo/gradYear`]: fencer.gradYear || null,
            [`${this.PREFIX}/bouts/${boutID}/fencer${sideStr}/fencerInfo/id`]: fencer.id,
            [`${this.PREFIX}/fencers/${fencer.id}/bouts`]: [...(fencer.bouts || []), boutID]
        };
        if (fencer.id in this.fencers) {
            this.fencers[fencer.id].bouts = [...(fencer.bouts || []), boutID];
        }

        if (!looped) {
            databaseUpdates[`${this.PREFIX}/bouts/${boutID}/fencer${sideStr}/substitution`] = true;
        }

        if (medical && !boutData[side === "left" ? "fencer1" : "fencer2"].medicalFencerInfo) {
            const oldFencerData = side === "left" ? boutData.fencer1 : boutData.fencer2;
            databaseUpdates[`${this.PREFIX}/bouts/${boutID}/fencer${sideStr}/medicalFencerInfo`] = oldFencerData.fencerInfo;
        }

        if (oldFencer?.bouts) {
            databaseUpdates[`${this.PREFIX}/fencers/${oldFencer.id}/bouts`] = oldFencer.bouts.filter(l => l !== boutID);
            if (oldFencer.id in this.fencers) {
                this.fencers[oldFencer.id].bouts = oldFencer.bouts.filter(l => l !== boutID);
            }
        }

        this.cache.queueSave("fencers");

        if (!medical) {
            const oldFencer = side === "left" ? boutData.fencer1 : boutData.fencer2;
            const otherFencer = side === "left" ? boutData.fencer2 : boutData.fencer1;
            let oldFencerName = `${oldFencer.fencerInfo.firstName} ${oldFencer.fencerInfo.lastName}`;
            const otherFencerName = `${otherFencer.fencerInfo.firstName} ${otherFencer.fencerInfo.lastName}`;
            if (oldFencerName === otherFencerName) {
                oldFencerName += ` `;
            }
            const eventUpdates = await this.getUpdatesToChangeNamesForEvents(
                boutID,
                oldFencerName,
                `${fencer.firstName} ${fencer.lastName}`,
                {
                    updateCache: true
                }
            );
            if (eventUpdates.status === "fail") return eventUpdates;
            for (const i in eventUpdates.data) {
                databaseUpdates[i] = eventUpdates.data[i];
            }
        }

        boutData[side === "left" ? "fencer1" : "fencer2"].forfeit = false;
        const newWinner = boutWinner(boutData);
        databaseUpdates[`${this.PREFIX}/bouts/${boutID}/winner`] = newWinner;

        // TODO: maybe find a better way to check for this?
        if (boutData.dualMeetId && this.PREFIX === "/college") {
            const meet = await this.getDualMeet(boutData.dualMeetId, undefined);
            if (meet.status === "fail") return meet;

            const teamResult =
                sideStr === 1
                    ? await this.getTeam(meet.data.team1.id!, { forceCache: true })
                    : await this.getTeam(meet.data.team2.id!, { forceCache: true });
            if (teamResult.status === "fail") return teamResult;
            const gender: "mens" | "womens" = teamResult.data.boysAndGirlsTeam === "boys" ? "mens" : "womens";

            if (newWinner !== null) {
                databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/updatedAt`] = Date.now();
                databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${fencer.id}/name`] =
                    `${fencer.firstName} ${fencer.lastName}`;
                databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${fencer.id}/teamID`] =
                    sideStr === 1 ? meet.data.team1.id! : meet.data.team2.id!;
                databaseUpdates[
                    `${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${fencer.id}/bouts/${boutData.weapon}/${boutData.id}`
                ] = {
                    opposingTeamID: sideStr === 1 ? meet.data.team2.id! : meet.data.team1.id!,
                    winner: (newWinner === BoutSide.Fencer1) !== (side === "left") ? 1 : 2,
                    ts: sideStr === 1 ? boutData.fencer1.score : boutData.fencer2.score,
                    tr: sideStr === 1 ? boutData.fencer2.score : boutData.fencer1.score
                } as ICollegeEventFencerResult["bouts"][Weapon][ID];
                if (oldFencerID) {
                    databaseUpdates[
                        `${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${oldFencerID}/bouts/${boutData.weapon}/${boutData.id}`
                    ] = null;
                }
            }
        }

        if (boutData.poolId) {
            const poolResult = await this.getTournamentPool(boutData.poolId, { preferCache: true });
            if (poolResult.status === "fail") return poolResult;
            const pool = poolResult.data;

            let loopDone = false;
            for (const i in pool.bouts) {
                for (const j in pool.bouts[i]) {
                    const bouts = pool.bouts[i][j];
                    if (!bouts) continue;
                    if ("id" in bouts) {
                    } else {
                        let stripStr = "";
                        if (bouts.C.id === boutID) stripStr = "C";
                        if (bouts.B.id === boutID) stripStr = "B";
                        if (bouts.A.id === boutID) stripStr = "A";

                        if (stripStr) {
                            databaseUpdates[`${this.PREFIX}/tournament-pools/${pool.id}/bouts/${i}/${j}/${stripStr}/fencer${sideStr}ID`] =
                                fencer.id;
                            databaseUpdates[`${this.PREFIX}/tournament-pools/${pool.id}/bouts/${i}/${j}/${stripStr}/fencer${sideStr}Name`] =
                                `${fencer.firstName} ${fencer.lastName}`;
                            databaseUpdates[`${this.PREFIX}/tournament-pools/${pool.id}/bouts/${i}/${j}/${stripStr}/winner`] = newWinner;
                            databaseUpdates[`${this.PREFIX}/tournament-pools/${pool.id}/stripRestrictions/${fencer.id}`] = stripStr;
                            loopDone = true;
                            break;
                        }
                    }
                }
                if (loopDone) break;
            }
        }

        if (recordsCount) {
            const recordUpdates = await this.getUpdatesToSubBoutRecord(
                boutID,
                side === "left" ? BoutSide.Fencer1 : BoutSide.Fencer2,
                fencer.id
            );
            if (recordUpdates.status === "fail") return recordUpdates;
            databaseUpdates = { ...databaseUpdates, ...recordUpdates.data };
        }
        return new DBSuccess(databaseUpdates);
    }

    public async subBoutFencer(boutID: string, side: "left" | "right", fencerID: string, medical: boolean, looped: boolean) {
        const result = await this.getDbUpdatesToSubBoutFencer(boutID, side, fencerID, medical, looped, true);
        if (result.status === "fail") return result;
        await this.database.ref().update(result.data);
        return new DBSuccess(true);
    }

    /**
     * @deprecated Use `subBoutWriteIn` instead
     */
    public async subBoutWriteInFencer(boutID: string, side: "left" | "right", fencer: IDualMeetBoutFencer["fencerInfo"], medical: boolean) {
        const boutData = await refOnce<IDualMeetBout>(`${this.PREFIX}/bouts/${boutID}`);
        const oldFencer = await refOnce<IExistingFencer>(
            `${this.PREFIX}/fencers/${(side === "left" ? boutData.fencer1 : boutData.fencer2).fencerInfo.id}`
        );
        const boutEvents = await Promise.all((boutData.log || []).map(l => refOnce<IBoutEvent>(`${this.PREFIX}/boutEvents/${l}`)));
        const normalizedFirstName = normalizeApostrophes(fencer.firstName).trim();
        const normalizedLastName = normalizeApostrophes(fencer.lastName).trim();

        const databaseUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/fencerInfo/firstName`]: normalizedFirstName,
            [`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/fencerInfo/lastName`]: normalizedLastName,
            [`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/fencerInfo/gradYear`]: fencer.gradYear || null,
            [`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/fencerInfo/id`]: fencer.id || null
        };

        if (medical && !boutData[side === "left" ? "fencer1" : "fencer2"].medicalFencerInfo) {
            const oldFencerData = side === "left" ? boutData.fencer1 : boutData.fencer2;
            databaseUpdates[`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/medicalFencerInfo`] = oldFencerData.fencerInfo;
        }

        if (oldFencer) {
            databaseUpdates[`${this.PREFIX}/fencers/${oldFencer.id}/bouts`] = oldFencer.bouts.filter(l => l !== boutID);
        }

        if (!medical) {
            for (const event of boutEvents) {
                const oldFencer = side === "left" ? boutData.fencer1 : boutData.fencer2;
                const modifiedDescription = event.description.replaceAll(
                    `${oldFencer.fencerInfo.firstName} ${oldFencer.fencerInfo.lastName}`,
                    `${normalizedFirstName} ${normalizedLastName}`
                );

                if (modifiedDescription !== event.description) {
                    databaseUpdates[`${this.PREFIX}/boutEvents/${event.id}/description`] = modifiedDescription;
                }
            }
        }

        this.database.ref().update(databaseUpdates);
    }

    public async subBoutWriteIn(
        boutIDs: string[],
        teamID: string,
        season: string,
        weapon: Weapon,
        side: "left" | "right",
        info: { firstName: string; lastName: string; gradYear?: number },
        medical: boolean
    ): AsyncDBResult<IExistingFencer> {
        const normalizedInfo = {
            ...info,
            firstName: normalizeApostrophes(info.firstName).trim(),
            lastName: normalizeApostrophes(info.lastName).trim()
        };
        const dbResponse = await this.createNewFencerInTeamDatabaseObj(
            teamID,
            normalizedInfo.firstName,
            normalizedInfo.lastName,
            normalizedInfo.gradYear,
            season,
            weapon
        );
        if (dbResponse.status === "fail") return dbResponse;
        const { databaseAdditions: databaseUpdates, newFencer } = dbResponse.data;

        let firstBout = true;
        for (const boutID of boutIDs) {
            const boutData = await refOnce<IDualMeetBout>(`${this.PREFIX}/bouts/${boutID}`);
            const oldFencer = await refOnce<IExistingFencer>(
                `${this.PREFIX}/fencers/${(side === "left" ? boutData.fencer1 : boutData.fencer2).fencerInfo.id}`
            );
            const boutEvents = await Promise.all((boutData.log || []).map(l => refOnce<IBoutEvent>(`${this.PREFIX}/boutEvents/${l}`)));

            databaseUpdates[`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/fencerInfo/firstName`] =
                normalizedInfo.firstName;
            databaseUpdates[`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/fencerInfo/lastName`] =
                normalizedInfo.lastName;
            databaseUpdates[`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/fencerInfo/gradYear`] = null;
            databaseUpdates[`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/fencerInfo/id`] = newFencer.id;
            databaseUpdates[`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/substitution`] = true;

            if (medical && firstBout && !boutData[side === "left" ? "fencer1" : "fencer2"].medicalFencerInfo) {
                const oldFencerData = side === "left" ? boutData.fencer1 : boutData.fencer2;
                databaseUpdates[`${this.PREFIX}/bouts/${boutID}/fencer${side === "left" ? 1 : 2}/medicalFencerInfo`] =
                    oldFencerData.fencerInfo;
            }

            if (oldFencer) {
                databaseUpdates[`${this.PREFIX}/fencers/${oldFencer.id}/bouts`] = (oldFencer.bouts || []).filter(l => l !== boutID);
            }

            if (!medical) {
                for (const event of boutEvents) {
                    if (!event) continue;
                    const oldFencer = side === "left" ? boutData.fencer1 : boutData.fencer2;
                    const otherFencer = side === "left" ? boutData.fencer2 : boutData.fencer1;

                    let name = `${oldFencer.fencerInfo.firstName} ${oldFencer.fencerInfo.lastName}`;
                    if (
                        oldFencer.fencerInfo.firstName === otherFencer.fencerInfo.firstName &&
                        oldFencer.fencerInfo.lastName === otherFencer.fencerInfo.lastName
                    ) {
                        name += ` (${boutData[`fencer${side === "left" ? 1 : 2}`].fencerInfo.teamName})`;
                    }
                    const otherName = `${normalizeApostrophes(otherFencer.fencerInfo.firstName).trim()} ${normalizeApostrophes(
                        otherFencer.fencerInfo.lastName
                    ).trim()}`;
                    let newName = `${normalizedInfo.firstName} ${normalizedInfo.lastName}`;
                    if (newName === otherName) {
                        newName += ` (${boutData[`fencer${side === "left" ? 1 : 2}`].fencerInfo.teamName})`;
                    }
                    const modifiedDescription = event.description.replaceAll(name, newName);

                    if (modifiedDescription !== event.description) {
                        databaseUpdates[`${this.PREFIX}/boutEvents/${event.id}/description`] = modifiedDescription;
                    }
                }
            }

            // TODO: maybe find a better way to check for this?
            if (boutData.dualMeetId && this.PREFIX === "/college") {
                const meet = await this.getDualMeetRaw(boutData.dualMeetId, undefined, true);
                if (meet.status === "fail") return meet;

                const teamResult =
                    side === "left"
                        ? await this.getTeam(meet.data.team1ID!, { forceCache: true })
                        : await this.getTeam(meet.data.team2ID!, { forceCache: true });
                if (teamResult.status === "fail") return teamResult;
                const gender: "mens" | "womens" = teamResult.data.boysAndGirlsTeam === "boys" ? "mens" : "womens";

                const newWinner = boutWinner(boutData);

                if (newWinner !== null) {
                    databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/updatedAt`] = Date.now();
                    databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${newFencer.id}/name`] =
                        `${newFencer.firstName} ${newFencer.lastName}`;
                    databaseUpdates[`${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${newFencer.id}/teamID`] =
                        side === "left" ? meet.data.team1ID! : meet.data.team2ID!;
                    databaseUpdates[
                        `${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${newFencer.id}/bouts/${boutData.weapon}/${boutData.id}`
                    ] = {
                        opposingTeamID: side === "left" ? meet.data.team2ID! : meet.data.team1ID!,
                        winner: (newWinner === BoutSide.Fencer1) !== (side === "left") ? 1 : 2,
                        ts: side === "left" ? boutData.fencer1.score : boutData.fencer2.score,
                        tr: side === "left" ? boutData.fencer2.score : boutData.fencer1.score
                    } as ICollegeEventFencerResult["bouts"][Weapon][ID];
                    if (oldFencer) {
                        databaseUpdates[
                            `${this.PREFIX}/eventResults/${meet.data.eventID}/${gender}/${oldFencer.id}/bouts/${boutData.weapon}/${boutData.id}`
                        ] = null;
                    }
                }
            }

            databaseUpdates[`${this.PREFIX}/fencers/${newFencer.id}/bouts`] = boutIDs;

            firstBout = false;

            const updates = await this.getUpdatesToSubBoutRecord(
                boutID,
                side === "left" ? BoutSide.Fencer1 : BoutSide.Fencer2,
                newFencer.id
            );
            if (updates.status === "fail") return updates;
            for (const i in updates.data) {
                databaseUpdates[i] = updates.data[i];
            }
        }

        this.database.ref().update(databaseUpdates);

        return new DBSuccess(newFencer);
    }

    public async getUpdatesToRemoveBoutFencer(
        boutID: string | IDualMeetBout,
        side: "left" | "right",
        noFencer: boolean
    ): AsyncDBResult<Record<string, unknown>> {
        let boutData = boutID;
        if (typeof boutData === "string") {
            const response = await this.getBout(boutID as string);
            if (response.status === "fail") return response;
            boutData = response.data;
        }

        const oldFencer = await refOnce<IExistingFencer>(
            `${this.PREFIX}/fencers/${(side === "left" ? boutData.fencer1 : boutData.fencer2).fencerInfo.id}`
        );
        const boutEvents = await Promise.all((boutData.log || []).map(l => refOnce<IBoutEvent>(`${this.PREFIX}/boutEvents/${l}`)));

        const sideStr = side === "left" ? 1 : 2;
        const otherSideForfeit = side === "left" ? boutData.fencer2.forfeit : boutData.fencer1.forfeit;
        const winner = otherSideForfeit ? 0 : side === "left" ? BoutSide.Fencer2 : BoutSide.Fencer1;

        const databaseUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/bouts/${boutData.id}/fencer${sideStr}/fencerInfo/firstName`]: noFencer ? "No" : "Unknown",
            [`${this.PREFIX}/bouts/${boutData.id}/fencer${sideStr}/fencerInfo/lastName`]: "fencer",
            [`${this.PREFIX}/bouts/${boutData.id}/fencer${sideStr}/fencerInfo/gradYear`]: null,
            [`${this.PREFIX}/bouts/${boutData.id}/fencer${sideStr}/fencerInfo/id`]: null,
            [`${this.PREFIX}/bouts/${boutData.id}/updatedAt`]: Date.now(),
            [`${this.PREFIX}/bouts/${boutData.id}/endedAt`]: Date.now(),
            [`${this.PREFIX}/bouts/${boutData.id}/winner`]: winner
        };

        if (oldFencer?.bouts) {
            databaseUpdates[`${this.PREFIX}/fencers/${oldFencer.id}/bouts`] = oldFencer.bouts.filter(l => l !== boutID);
        }

        for (const event of boutEvents) {
            if (!event?.description) continue;
            const oldFencer = side === "left" ? boutData.fencer1 : boutData.fencer2;
            const modifiedDescription = event.description.replaceAll(
                `${oldFencer.fencerInfo.firstName} ${oldFencer.fencerInfo.lastName}`,
                noFencer ? "No fencer" : "Unknown fencer"
            );

            if (modifiedDescription !== event.description) {
                databaseUpdates[`${this.PREFIX}/boutEvents/${event.id}/description`] = modifiedDescription;
            }
            if (this.boutEvents[event.id]) {
                this.boutEvents[event.id].description = modifiedDescription;
            }
        }

        // TODO: check that this updates fencer records properly?

        if (boutData.poolId) {
            const poolResult = await this.getTournamentPool(boutData.poolId, { preferCache: true });
            if (poolResult.status === "fail") return poolResult;
            const pool = poolResult.data;

            let loopDone = false;
            for (const i in pool.bouts) {
                for (const j in pool.bouts[i]) {
                    const bouts = pool.bouts[i][j];
                    if (!bouts) continue;
                    if ("id" in bouts) {
                    } else {
                        let stripStr = "";
                        if (bouts.C.id === boutID) stripStr = "C";
                        if (bouts.B.id === boutID) stripStr = "B";
                        if (bouts.A.id === boutID) stripStr = "A";

                        if (stripStr) {
                            databaseUpdates[`${this.PREFIX}/tournament-pools/${pool.id}/bouts/${i}/${j}/${stripStr}/fencer${sideStr}ID`] =
                                null;
                            databaseUpdates[`${this.PREFIX}/tournament-pools/${pool.id}/bouts/${i}/${j}/${stripStr}/fencer${sideStr}Name`] =
                                noFencer ? "No fencer" : "Unknown fencer";
                            databaseUpdates[`${this.PREFIX}/tournament-pools/${pool.id}/bouts/${i}/${j}/${stripStr}/winner`] = winner;
                            // TODO: if the old fencer has no restrictions anymore, remove them
                            loopDone = true;
                            break;
                        }
                    }
                }
                if (loopDone) break;
            }
        }

        return new DBSuccess(databaseUpdates);
    }

    public async removeBoutFencer(boutID: string | IDualMeetBout, side: "left" | "right", noFencer: boolean) {
        const updates = await this.getUpdatesToRemoveBoutFencer(boutID, side, noFencer);
        await this.database.ref().update(updates);
        return true;
    }

    public async setCurrentBoutEditor(id: string, key: string | null) {
        const databaseUpdates = {};
        databaseUpdates[`${this.PREFIX}/bouts/${id}/currentlyEditingUser`] = key;
        this.database.ref().update(databaseUpdates);
    }

    public async getManualBoutUpdateUpdates(
        boutData: IDualMeetBout,
        score1: number,
        score2: number,
        fencer1: { info: IExistingFencer; forfeit: boolean; medicalForfeit: boolean; teamID: string },
        fencer2: { info: IExistingFencer; forfeit: boolean; medicalForfeit: boolean; teamID: string },
        priority: "left" | "right" | null
    ): AsyncDBResult<Record<string, unknown>> {
        const databaseUpdates: Record<string, unknown> = {};
        try {
            const userInfoResult = await this.getCurrentUserInfo();
            if (userInfoResult.status === "fail") return userInfoResult;
            const userInfo = userInfoResult.data;

            const id = boutData.id;
            const meetResult = await this.getDualMeetRaw(boutData.dualMeetId!, undefined, true);
            if (meetResult.status === "fail") return meetResult;
            const meet = meetResult.data;

            // Get old winner before updating
            const oldWinner = boutWinner(boutData, { team: false });

            databaseUpdates[`${this.PREFIX}/bouts/${id}/endedAt`] = Date.now();
            databaseUpdates[`${this.PREFIX}/bouts/${id}/updatedAt`] = Date.now();
            databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/score`] = score1;
            databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/score`] = score2;
            databaseUpdates[`${this.PREFIX}/bouts/${id}/priority`] = priority;

            // Handle fencer1 updates
            if (fencer1.info.id !== boutData.fencer1.fencerInfo.id) {
                if (fencer1.forfeit) {
                    const subUpdates1 = await this.getUpdatesToRemoveBoutFencer(id, "left", true);
                    if (subUpdates1.status === "fail") return subUpdates1;
                    for (const i in subUpdates1.data) {
                        databaseUpdates[i] = subUpdates1.data[i];
                    }
                } else {
                    const subUpdates1 = await this.getDbUpdatesToSubBoutFencer(id, "left", fencer1.info, false, false, meet.published);
                    if (subUpdates1.status === "fail") return subUpdates1;
                    for (const i in subUpdates1.data) {
                        databaseUpdates[i] = subUpdates1.data[i];
                    }
                }
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/forfeit`] = fencer1.forfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/medicalForfeit`] = fencer1.medicalForfeit;
            } else {
                // Preserve existing fencer info while updating forfeit status
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/forfeit`] = fencer1.forfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/medicalForfeit`] = fencer1.medicalForfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/fencerInfo`] = boutData.fencer1.fencerInfo;
            }

            // Handle fencer2 updates
            if (fencer2.info.id !== boutData.fencer2.fencerInfo.id) {
                if (fencer2.forfeit) {
                    const subUpdates2 = await this.getUpdatesToRemoveBoutFencer(id, "right", true);
                    if (subUpdates2.status === "fail") return subUpdates2;
                    for (const i in subUpdates2.data) {
                        databaseUpdates[i] = subUpdates2.data[i];
                    }
                } else {
                    const subUpdates2 = await this.getDbUpdatesToSubBoutFencer(id, "right", fencer2.info, false, false, meet.published);
                    if (subUpdates2.status === "fail") return subUpdates2;
                    for (const i in subUpdates2.data) {
                        databaseUpdates[i] = subUpdates2.data[i];
                    }
                }
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/forfeit`] = fencer2.forfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/medicalForfeit`] = fencer2.medicalForfeit;
            } else {
                // Preserve existing fencer info while updating forfeit status
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/forfeit`] = fencer2.forfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/medicalForfeit`] = fencer2.medicalForfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/fencerInfo`] = boutData.fencer2.fencerInfo;
            }

            let shouldAddEvent = boutData.log.length === 0;
            if (boutData.log.length > 0) {
                const lastEvent = await this.getBoutEvent(boutData.log[boutData.log.length - 1]);
                if (lastEvent.status === "fail") return lastEvent;
                if (lastEvent.data?.type !== BoutEventType.ManualUpdate) {
                    shouldAddEvent = true;
                }
            }
            if (shouldAddEvent) {
                // Add bout event for manual score update
                const eventId = this.database.ref(`${this.PREFIX}/boutEvents`).push().key;
                if (!eventId) throw new Error("Failed to generate event ID");

                const event: IBoutEvent = {
                    id: eventId,
                    description: "Bout score manually updated",
                    fencer1Cards: boutData.fencer1.cardInfo,
                    fencer2Cards: boutData.fencer2.cardInfo,
                    score1: score1,
                    score2: score2,
                    boutTime: boutData.boutTime,
                    localTime: Date.now(),
                    type: BoutEventType.ManualUpdate
                };

                // Add the event to the log array
                const log = [...(boutData.log || []), eventId];
                databaseUpdates[`${this.PREFIX}/bouts/${id}/log`] = log;
                databaseUpdates[`${this.PREFIX}/boutEvents/${eventId}`] = event;
            }

            // Update the bout's winner using the boutWinner helper function
            const updatedBout: IDualMeetBout = {
                ...boutData,
                fencer1: {
                    ...boutData.fencer1,
                    fencerInfo: { ...boutData.fencer1.fencerInfo, id: fencer1.info.id },
                    score: score1,
                    forfeit: fencer1.forfeit,
                    medicalForfeit: fencer1.medicalForfeit
                },
                fencer2: {
                    ...boutData.fencer2,
                    fencerInfo: { ...boutData.fencer2.fencerInfo, id: fencer2.info.id },
                    score: score2,
                    forfeit: fencer2.forfeit,
                    medicalForfeit: fencer2.medicalForfeit
                },
                priority,
                endedAt: Date.now()
            };
            const winner = boutWinner(updatedBout, { team: true });
            const boutIdx = meet.bouts.findIndex(l => l.id === id);
            if (boutIdx === -1) return new DBError("Bout not found");

            databaseUpdates[`${this.PREFIX}/dualMeets/${boutData.dualMeetId}/bouts/${boutIdx}/winner`] = winner;

            const autoPublish = !meet.published;
            if (autoPublish) {
                databaseUpdates[`${this.PREFIX}/dualMeets/${boutData.dualMeetId}/published`] = true;
                databaseUpdates[`${this.PREFIX}/dualMeets/${boutData.dualMeetId}/publishedAt`] = Date.now();
                databaseUpdates[`${this.PREFIX}/dualMeets/${boutData.dualMeetId}/publishedBy`] = userInfo.id;
            }

            if (meet.published || autoPublish) {
                // Add/update both fencers' records with the new winner
                // Note: We don't remove first because in a Firebase multi-path update,
                // setting a parent to null while also setting children causes the children to be deleted
                const addUpdates1 = await this.getDbUpdatesForAddBoutToFencerRecord(fencer1.info.id, updatedBout, meet);
                if (addUpdates1.status === "fail") return addUpdates1;
                for (const i in addUpdates1.data) {
                    databaseUpdates[i] = addUpdates1.data[i];
                }

                const addUpdates2 = await this.getDbUpdatesForAddBoutToFencerRecord(fencer2.info.id, updatedBout, meet);
                if (addUpdates2.status === "fail") return addUpdates2;
                for (const i in addUpdates2.data) {
                    databaseUpdates[i] = addUpdates2.data[i];
                }

                // Update college event results if this is part of a college event
                if (meet.eventID && winner !== null) {
                    // Update fencer1 results
                    const team1 = await this.getTeam(fencer1.teamID, { forceCache: true });
                    if (team1.status === "success") {
                        const gender1: "mens" | "womens" = team1.data.boysAndGirlsTeam === "boys" ? "mens" : "womens";
                        databaseUpdates[`${this.PREFIX}/eventResults/${meet.eventID}/updatedAt`] = Date.now();
                        databaseUpdates[`${this.PREFIX}/eventResults/${meet.eventID}/${gender1}/${fencer1.info.id}/name`] =
                            `${fencer1.info.firstName} ${fencer1.info.lastName}`;
                        databaseUpdates[`${this.PREFIX}/eventResults/${meet.eventID}/${gender1}/${fencer1.info.id}/teamID`] =
                            fencer1.teamID;
                        databaseUpdates[
                            `${this.PREFIX}/eventResults/${meet.eventID}/${gender1}/${fencer1.info.id}/bouts/${boutData.weapon}/${id}`
                        ] = {
                            opposingTeamID: fencer2.teamID,
                            winner: winner === BoutSide.Fencer1 ? 1 : 2,
                            ts: score1,
                            tr: score2
                        } as ICollegeEventFencerResult["bouts"][Weapon][ID];
                    }

                    // Update fencer2 results
                    const team2 = await this.getTeam(fencer2.teamID, { forceCache: true });
                    if (team2.status === "success") {
                        const gender2: "mens" | "womens" = team2.data.boysAndGirlsTeam === "boys" ? "mens" : "womens";
                        databaseUpdates[`${this.PREFIX}/eventResults/${meet.eventID}/updatedAt`] = Date.now();
                        databaseUpdates[`${this.PREFIX}/eventResults/${meet.eventID}/${gender2}/${fencer2.info.id}/name`] =
                            `${fencer2.info.firstName} ${fencer2.info.lastName}`;
                        databaseUpdates[`${this.PREFIX}/eventResults/${meet.eventID}/${gender2}/${fencer2.info.id}/teamID`] =
                            fencer2.teamID;
                        databaseUpdates[
                            `${this.PREFIX}/eventResults/${meet.eventID}/${gender2}/${fencer2.info.id}/bouts/${boutData.weapon}/${id}`
                        ] = {
                            opposingTeamID: fencer1.teamID,
                            winner: winner === BoutSide.Fencer2 ? 1 : 2,
                            ts: score2,
                            tr: score1
                        } as ICollegeEventFencerResult["bouts"][Weapon][ID];
                    }
                }
            }

            return new DBSuccess(databaseUpdates);
        } catch (e) {
            console.log(databaseUpdates);
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async addDispute(dualMeetId: ID, disputedBouts: IDualMeetBout[]): AsyncDBResult<IDualMeetDispute> {
        try {
            const result = await this.functions.httpsCallable("addDispute")({
                prefix: this.PREFIX,
                dualMeetId,
                disputedBouts
            });

            return result.data;
        } catch (error) {
            return new DBError(error.message);
        }
    }

    public async updateDispute(dualMeetId: ID, disputeId: ID, disputedBouts: IDualMeetBout[]): AsyncDBResult<boolean> {
        try {
            const result = await this.functions.httpsCallable("updateDispute")({
                prefix: this.PREFIX,
                dualMeetId,
                disputeId,
                disputedBouts
            });

            return result.data;
        } catch (error) {
            return new DBError(error.message);
        }
    }

    public async getDispute(dualMeetId: ID, id: ID): AsyncDBResult<IDualMeetDispute> {
        try {
            const result = await this.functions.httpsCallable("getDispute")({
                prefix: this.PREFIX,
                dualMeetId,
                id
            });

            return result.data;
        } catch (error) {
            return new DBError(error.message);
        }
    }

    public async getAllDisputes(dualMeetId: ID): AsyncDBResult<IDualMeetDispute[]> {
        try {
            const result = await this.functions.httpsCallable("getAllDisputes")({
                prefix: this.PREFIX,
                dualMeetId
            });

            return result.data;
        } catch (error) {
            return new DBError(error.message);
        }
    }

    public async rescindDispute(dualMeetId: ID, disputeId: ID): AsyncDBResult<boolean> {
        try {
            const result = await this.functions.httpsCallable("rescindDispute")({
                prefix: this.PREFIX,
                dualMeetId,
                disputeId
            });

            return result.data;
        } catch (error) {
            return new DBError(error.message);
        }
    }

    /**
     * Adds the dispute status to an existing dispute. Also validates the dispute for final approval and merging; if
     * approved, the dispute merges.
     * @param dualMeetId The dual meet id
     * @param disputeId The dispute id
     * @param response The response ("accepted", "changes_requested", "denied")
     * @param remarks Optional; comment to be relayed to all other team administrators
     * @returns The updated dual meet dispute, including the updated `approved` field.
     */
    public async updateDisputeStatus(
        dualMeetId: ID,
        disputeId: ID,
        response: "accepted" | "changes_requested" | "denied",
        remarks?: string
    ): AsyncDBResult<IDualMeetDispute> {
        try {
            const result = await this.functions.httpsCallable("updateDisputeStatus")({
                prefix: this.PREFIX,
                dualMeetId,
                disputeId,
                response,
                remarks
            });

            return result.data;
        } catch (error) {
            return new DBError(error.message);
        }
    }

    public async getManualBoutUpdatePoolUpdates(
        poolData: ITournamentPoolCBA,
        boutData: IDualMeetBout,
        score1: number,
        score2: number,
        fencer1: { info: IExistingFencer; forfeit: boolean; medicalForfeit: boolean; teamIdx: number },
        fencer2: { info: IExistingFencer; forfeit: boolean; medicalForfeit: boolean; teamIdx: number },
        priority: "left" | "right" | null
    ): AsyncDBResult<{ fencer1Subbed: boolean; fencer2Subbed: boolean; databaseUpdates: Record<string, unknown> }> {
        const databaseUpdates = {};
        try {
            let fencer1Subbed = false;
            let fencer2Subbed = false;

            const id = boutData.id;

            const stripStr = boutData.order === 2 ? "A" : boutData.order === 1 ? "B" : "C";

            const poolBoutPrefix = `${this.PREFIX}/tournament-pools/${boutData.poolId!}/bouts/${fencer1.teamIdx}/${fencer2.teamIdx}/${stripStr}`;

            databaseUpdates[`${this.PREFIX}/bouts/${id}/endedAt`] = Date.now();
            databaseUpdates[`${this.PREFIX}/bouts/${id}/updatedAt`] = Date.now();
            databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/score`] = score1;
            databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/score`] = score2;
            databaseUpdates[`${this.PREFIX}/bouts/${id}/priority`] = priority;
            databaseUpdates[`${poolBoutPrefix}/score1`] = score1;
            databaseUpdates[`${poolBoutPrefix}/score2`] = score2;

            // Handle fencer1 updates
            if (fencer1.info.id !== boutData.fencer1.fencerInfo.id) {
                if (fencer1.forfeit) {
                    const subUpdates1 = await this.getUpdatesToRemoveBoutFencer(id, "left", true);
                    if (subUpdates1.status === "fail") return subUpdates1;
                    for (const i in subUpdates1.data) {
                        databaseUpdates[i] = subUpdates1.data[i];
                    }
                } else {
                    const subUpdates1 = await this.getDbUpdatesToSubBoutFencer(id, "left", fencer1.info, false, false, false);
                    if (subUpdates1.status === "fail") return subUpdates1;
                    for (const i in subUpdates1.data) {
                        databaseUpdates[i] = subUpdates1.data[i];
                    }
                }
                fencer1Subbed = true;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/forfeit`] = fencer1.forfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/medicalForfeit`] = fencer1.medicalForfeit;
            } else {
                // Preserve existing fencer info while updating forfeit status
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/forfeit`] = fencer1.forfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/medicalForfeit`] = fencer1.medicalForfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer1/fencerInfo`] = boutData.fencer1.fencerInfo;
            }
            databaseUpdates[`${poolBoutPrefix}/fencer1ID`] = fencer1.forfeit && !fencer1.medicalForfeit ? null : fencer1.info.id;
            databaseUpdates[`${poolBoutPrefix}/fencer1Forfeit`] = fencer1.forfeit ? true : null;

            if (fencer1.forfeit !== boutData.fencer1.forfeit) fencer1Subbed = true;

            // Handle fencer2 updates
            if (fencer2.info.id !== boutData.fencer2.fencerInfo.id) {
                if (fencer2.forfeit) {
                    const subUpdates2 = await this.getUpdatesToRemoveBoutFencer(id, "right", true);
                    if (subUpdates2.status === "fail") return subUpdates2;
                    for (const i in subUpdates2.data) {
                        databaseUpdates[i] = subUpdates2.data[i];
                    }
                } else {
                    // TODO: check if published then update records
                    const subUpdates2 = await this.getDbUpdatesToSubBoutFencer(id, "right", fencer2.info, false, false, false);
                    if (subUpdates2.status === "fail") return subUpdates2;
                    for (const i in subUpdates2.data) {
                        databaseUpdates[i] = subUpdates2.data[i];
                    }
                }
                fencer2Subbed = true;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/forfeit`] = fencer2.forfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/medicalForfeit`] = fencer2.medicalForfeit;
            } else {
                // Preserve existing fencer info while updating forfeit status
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/forfeit`] = fencer2.forfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/medicalForfeit`] = fencer2.medicalForfeit;
                databaseUpdates[`${this.PREFIX}/bouts/${id}/fencer2/fencerInfo`] = boutData.fencer2.fencerInfo;
            }

            databaseUpdates[`${poolBoutPrefix}/fencer2ID`] = fencer2.forfeit && !fencer2.medicalForfeit ? null : fencer2.info.id;
            databaseUpdates[`${poolBoutPrefix}/fencer2Forfeit`] = fencer2.forfeit ? true : null;
            if (fencer2.forfeit !== boutData.fencer2.forfeit) fencer2Subbed = true;

            // Add bout event for manual score update
            const eventId = this.database.ref(`${this.PREFIX}/boutEvents`).push().key;
            if (!eventId) throw new Error("Failed to generate event ID");

            const event: IBoutEvent = {
                id: eventId,
                description: "Bout score manually updated",
                fencer1Cards: boutData.fencer1.cardInfo,
                fencer2Cards: boutData.fencer2.cardInfo,
                score1: score1,
                score2: score2,
                boutTime: boutData.boutTime,
                localTime: Date.now(),
                type: BoutEventType.ManualUpdate
            };

            // Add the event to the log array
            const log = boutData.log || [];
            log.push(eventId);
            databaseUpdates[`${this.PREFIX}/bouts/${id}/log`] = log;
            databaseUpdates[`${this.PREFIX}/boutEvents/${eventId}`] = event;

            // Update the bout's winner using the boutWinner helper function
            const updatedBout: IDualMeetBout = {
                ...boutData,
                fencer1: {
                    ...boutData.fencer1,
                    score: score1,
                    forfeit: fencer1.forfeit,
                    medicalForfeit: fencer1.medicalForfeit
                },
                fencer2: {
                    ...boutData.fencer2,
                    score: score2,
                    forfeit: fencer2.forfeit,
                    medicalForfeit: fencer2.medicalForfeit
                },
                priority,
                endedAt: Date.now()
            };
            const winner = boutWinner(updatedBout, { team: true });

            databaseUpdates[`${poolBoutPrefix}/winner`] = winner;

            if (poolData.variant === "cba") {
                let team1Index = -1;
                let team2Index = -1;
                let strip: "C" | "B" | "A" = "C";

                let finishedBefore = true;
                for (const i in poolData.bouts) {
                    for (const j in poolData.bouts[i]) {
                        if (!poolData.bouts[i][j]) continue;
                        if (
                            poolData.bouts[i][j].C.winner === undefined ||
                            poolData.bouts[i][j].B.winner === undefined ||
                            poolData.bouts[i][j].A.winner === undefined
                        )
                            finishedBefore = false;
                        if (
                            poolData.bouts[i][j].C.id === boutData.id ||
                            poolData.bouts[i][j].B.id === boutData.id ||
                            poolData.bouts[i][j].A.id === boutData.id
                        ) {
                            team1Index = Number(i);
                            team2Index = Number(j);
                            if (poolData.bouts[i][j].C.id === boutData.id) {
                                strip = "C";
                            } else if (poolData.bouts[i][j].B.id === boutData.id) {
                                strip = "B";
                            } else if (poolData.bouts[i][j].A.id === boutData.id) {
                                strip = "A";
                            }
                        }
                    }
                }

                if (team1Index !== -1 && team2Index !== -1) {
                    const boutPrefix = `${this.PREFIX}/tournament-pools/${poolData.id}/bouts/${team1Index}/${team2Index}/${strip}`;
                    databaseUpdates[`${boutPrefix}/winner`] = winner;
                    databaseUpdates[`${boutPrefix}/score1`] = score1;
                    databaseUpdates[`${boutPrefix}/score2`] = score2;

                    poolData.bouts[team1Index][team2Index][strip].winner = winner || 0;
                    poolData.bouts[team1Index][team2Index][strip].score1 = score1;
                    poolData.bouts[team1Index][team2Index][strip].score2 = score2;
                }

                let finished = true;
                for (const i in poolData.bouts) {
                    for (const j in poolData.bouts[i]) {
                        if (!poolData.bouts[i][j]) continue;
                        if (!poolData.bouts[i][j].C.winner || !poolData.bouts[i][j].B.winner || !poolData.bouts[i][j].A.winner)
                            finished = false;
                        // TODO: account for double forfeits (only situation where no winner methinks)
                    }
                }
                databaseUpdates[`${this.PREFIX}/tournament-rounds/${poolData.roundId}/pools/${poolData.id}/completed`] = finished;

                if (finished) {
                    const roundResult = await this.getTournamentRound(poolData.roundId, { preferCache: true });
                    if (roundResult.status === "fail") return roundResult;
                    const roundData = roundResult.data;

                    let allFinished = true;
                    for (const id in roundData.pools) {
                        if (!roundData.pools[id].completed) {
                            allFinished = false;
                            break;
                        }
                    }

                    const eventResult = await this.getTournamentEvent(poolData.eventId, { preferCache: true });
                    if (eventResult.status === "fail") return eventResult;
                    const eventData = eventResult.data;

                    const roundIdx = eventData.rounds.findIndex(l => l.id === roundData.id);

                    databaseUpdates[`${this.PREFIX}/tournament-events/${poolData.eventId}/rounds/${roundIdx}/finished`] = allFinished;
                }
            }

            const roundUpdateResult = await this.getDbUpdatesForRoundResultsFromPool(poolData.roundId, poolData);
            if (roundUpdateResult.status === "fail") return roundUpdateResult;
            Object.assign(databaseUpdates, roundUpdateResult.data);

            // TODO: update fencer records
            // // If winner changed, update both fencers' records
            // // Remove bout from both fencers' records first
            // const removeUpdates1 = await this.getUpdatesToRemoveBoutFromFencerRecord(fencer1.id, id, meet);
            // if (removeUpdates1.status === "fail") return removeUpdates1;
            // for (const i in removeUpdates1.data) {
            //     databaseUpdates[i] = removeUpdates1.data[i];
            // }

            // const removeUpdates2 = await this.getUpdatesToRemoveBoutFromFencerRecord(fencer2.id, id, meet);
            // if (removeUpdates2.status === "fail") return removeUpdates2;
            // for (const i in removeUpdates2.data) {
            //     databaseUpdates[i] = removeUpdates2.data[i];
            // }

            // if (meet.published) {
            //     // Add bout back to both fencers' records with new winner
            //     const addUpdates1 = await this.getDbUpdatesForAddBoutToFencerRecord(fencer1.id, id, meet);
            //     if (addUpdates1.status === "fail") return addUpdates1;
            //     for (const i in addUpdates1.data) {
            //         databaseUpdates[i] = addUpdates1.data[i];
            //     }

            //     const addUpdates2 = await this.getDbUpdatesForAddBoutToFencerRecord(fencer2.id, id, meet);
            //     if (addUpdates2.status === "fail") return addUpdates2;
            //     for (const i in addUpdates2.data) {
            //         databaseUpdates[i] = addUpdates2.data[i];
            //     }
            // }

            return new DBSuccess({ fencer1Subbed, fencer2Subbed, databaseUpdates });
        } catch (e) {
            console.log(databaseUpdates);
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async manualBoutUpdatePool(
        poolData: ITournamentPoolCBA,
        boutId: string,
        score1: number,
        score2: number,
        fencer1: { info: IExistingFencer; forfeit: boolean; medicalForfeit: boolean; teamIdx: number },
        fencer2: { info: IExistingFencer; forfeit: boolean; medicalForfeit: boolean; teamIdx: number },
        priority: "left" | "right" | null
    ): AsyncDBResult<boolean> {
        try {
            let databaseUpdates: Record<string, unknown> = {};
            const boutResult = await this.getBout(boutId);
            if (boutResult.status === "fail") return boutResult;

            // Handle new write-in fencers
            if (fencer1.info.id.startsWith("newWriteIn")) {
                const teamID = poolData.teams[fencer1.teamIdx].id;
                const dbResponse = await this.createNewFencerInTeamDatabaseObj(
                    teamID,
                    fencer1.info.firstName,
                    fencer1.info.lastName,
                    undefined,
                    CURRENT_SEASON_STR,
                    poolData.weapon
                );
                if (dbResponse.status === "fail") return dbResponse;
                const { databaseAdditions, newFencer } = dbResponse.data;
                fencer1.info = newFencer;
                databaseUpdates = { ...databaseUpdates, ...databaseAdditions };
            }
            if (fencer2.info.id.startsWith("newWriteIn")) {
                const teamID = poolData.teams[fencer2.teamIdx].id;
                const dbResponse = await this.createNewFencerInTeamDatabaseObj(
                    teamID,
                    fencer2.info.firstName,
                    fencer2.info.lastName,
                    undefined,
                    CURRENT_SEASON_STR,
                    poolData.weapon
                );
                if (dbResponse.status === "fail") return dbResponse;
                const { databaseAdditions, newFencer } = dbResponse.data;
                fencer2.info = newFencer;
                databaseUpdates = { ...databaseUpdates, ...databaseAdditions };
            }

            // Get the database updates for this single bout
            const updatesResult = await this.getManualBoutUpdatePoolUpdates(
                poolData,
                boutResult.data,
                score1,
                score2,
                fencer1,
                fencer2,
                priority
            );
            if (updatesResult.status === "fail") return updatesResult;
            const updates = updatesResult.data.databaseUpdates;

            // Merge the updates
            for (const i in updates) {
                databaseUpdates[i] = updates[i];
            }

            await this.database.ref().update(databaseUpdates);

            return new DBSuccess(true);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async manualBoutUpdatePoolPropagate(
        poolData: ITournamentPoolCBA,
        boutId: string,
        score1: number,
        score2: number,
        fencer1: { info: IExistingFencer; forfeit: boolean; medicalForfeit: boolean; teamIdx: number },
        fencer2: { info: IExistingFencer; forfeit: boolean; medicalForfeit: boolean; teamIdx: number },
        priority: "left" | "right" | null
    ): AsyncDBResult<Record<string, unknown>> {
        let databaseUpdates: Record<string, unknown> = {};
        const boutResult = await this.getBout(boutId);
        if (boutResult.status === "fail") return boutResult;

        // Unused for now, but maybe useful later
        // Record of newWriteIn fencer IDs to their new fencer objects
        const idReplacements: Record<string, IExistingFencer> = {};

        if (fencer1.info.id.startsWith("newWriteIn")) {
            const teamID = poolData.teams[fencer1.teamIdx].id;
            const dbResponse = await this.createNewFencerInTeamDatabaseObj(
                teamID,
                fencer1.info.firstName,
                fencer1.info.lastName,
                undefined,
                CURRENT_SEASON_STR,
                poolData.weapon
            );
            if (dbResponse.status === "fail") return dbResponse;
            const { databaseAdditions, newFencer } = dbResponse.data;
            idReplacements[fencer1.info.id] = newFencer;
            fencer1.info = newFencer;
            databaseUpdates = { ...databaseUpdates, ...databaseAdditions };
        }
        if (fencer2.info.id.startsWith("newWriteIn")) {
            const teamID = poolData.teams[fencer2.teamIdx].id;
            const dbResponse = await this.createNewFencerInTeamDatabaseObj(
                teamID,
                fencer2.info.firstName,
                fencer2.info.lastName,
                undefined,
                CURRENT_SEASON_STR,
                poolData.weapon
            );
            if (dbResponse.status === "fail") return dbResponse;
            const { databaseAdditions, newFencer } = dbResponse.data;
            idReplacements[fencer2.info.id] = newFencer;
            fencer2.info = newFencer;
            databaseUpdates = { ...databaseUpdates, ...databaseAdditions };
        }

        const updatesResult = await this.getManualBoutUpdatePoolUpdates(
            poolData,
            boutResult.data,
            score1,
            score2,
            fencer1,
            fencer2,
            priority
        );
        if (updatesResult.status === "fail") return updatesResult;
        let { fencer1Subbed, fencer2Subbed } = updatesResult.data;
        const updates = updatesResult.data.databaseUpdates;

        for (const i in updates) {
            databaseUpdates[i] = updates[i];
        }

        const stripStr = boutResult.data.order === 2 ? "A" : boutResult.data.order === 1 ? "B" : "C";

        let boutSeen = false;

        if (fencer1Subbed || fencer2Subbed) {
            let leftFencer = fencer1;
            let rightFencer = fencer2;
            const poolOrder = GenPoolOrder(poolData.teams.length);
            for (const pair of poolOrder) {
                const teamIdx1 = pair[0] - 1;
                const teamIdx2 = pair[1] - 1;
                const idx1 = Math.min(teamIdx1, teamIdx2);
                const idx2 = Math.max(teamIdx1, teamIdx2);
                const bouts = poolData.bouts[idx1]?.[idx2];
                console.log(poolData, bouts, idx1, idx2);
                if (!bouts) continue;

                if (boutSeen) {
                    console.log("lorax", boutId);
                    if (fencer1Subbed && (teamIdx1 === leftFencer.teamIdx || teamIdx2 === leftFencer.teamIdx)) {
                        const bout = bouts[stripStr];
                        const sideStr = idx1 === leftFencer.teamIdx ? "left" : "right";
                        if (leftFencer.forfeit) {
                            const boutUpdates = await this.getUpdatesToRemoveBoutFencer(bout.id, sideStr, true);
                            if (boutUpdates.status === "fail") return boutUpdates;
                            for (const i in boutUpdates.data) {
                                databaseUpdates[i] = boutUpdates.data[i];
                            }
                        } else {
                            const boutUpdates = await this.getDbUpdatesToSubBoutFencer(
                                bout.id,
                                sideStr,
                                leftFencer.info,
                                false,
                                false,
                                false
                            );
                            if (boutUpdates.status === "fail") return boutUpdates;
                            for (const i in boutUpdates.data) {
                                databaseUpdates[i] = boutUpdates.data[i];
                            }
                        }
                        databaseUpdates[`${this.PREFIX}/bouts/${bout.id}/fencer1/forfeit`] = fencer1.forfeit;
                        databaseUpdates[`${this.PREFIX}/bouts/${bout.id}/fencer1/medicalForfeit`] = fencer1.medicalForfeit;
                    }
                    if (fencer2Subbed && (teamIdx1 === rightFencer.teamIdx || teamIdx2 === rightFencer.teamIdx)) {
                        const bout = bouts[stripStr];
                        const sideStr = idx1 === rightFencer.teamIdx ? "left" : "right";
                        if (rightFencer.forfeit) {
                            const boutUpdates = await this.getUpdatesToRemoveBoutFencer(bout.id, sideStr, true);
                            if (boutUpdates.status === "fail") return boutUpdates;
                            for (const i in boutUpdates.data) {
                                databaseUpdates[i] = boutUpdates.data[i];
                            }
                        } else {
                            const boutUpdates = await this.getDbUpdatesToSubBoutFencer(
                                bout.id,
                                sideStr,
                                rightFencer.info,
                                false,
                                false,
                                false
                            );
                            if (boutUpdates.status === "fail") return boutUpdates;
                            for (const i in boutUpdates.data) {
                                databaseUpdates[i] = boutUpdates.data[i];
                            }
                        }
                        databaseUpdates[`${this.PREFIX}/bouts/${bout.id}/fencer2/forfeit`] = rightFencer.forfeit;
                        databaseUpdates[`${this.PREFIX}/bouts/${bout.id}/fencer2/medicalForfeit`] = rightFencer.medicalForfeit;
                    }
                } else if (bouts[stripStr].id === boutId) {
                    console.log("found bout", boutId);
                    [fencer1Subbed, fencer2Subbed] = [fencer2Subbed, fencer1Subbed];
                    [leftFencer, rightFencer] = [rightFencer, leftFencer];
                    boutSeen = true;
                }
            }
        }

        return new DBSuccess(databaseUpdates);
    }

    public async manualBoutUpdatePoolPropagateMultiple(
        poolData: ITournamentPoolCBA,
        boutUpdates: ManualBoutUpdateRequest[]
    ): AsyncDBResult<boolean> {
        const databaseUpdates: Record<string, unknown> = {};

        for (const boutUpdate of boutUpdates) {
            const result = await this.manualBoutUpdatePoolPropagate(
                poolData,
                boutUpdate.boutId,
                boutUpdate.score1,
                boutUpdate.score2,
                boutUpdate.fencer1,
                boutUpdate.fencer2,
                boutUpdate.priority
            );
            if (result.status === "fail") return result;
            for (const i in result.data) {
                databaseUpdates[i] = result.data[i];
            }
        }

        await this.database.ref().update(databaseUpdates);
        return new DBSuccess(true);
    }

    // #endregion

    // #region Events

    private async getDbUpdatesForRounds(
        rounds: ICollegeEventRound[],
        eventId: string,
        gender: "mens" | "womens",
        season: string,
        startDate: Date
    ) {
        const databaseUpdates: Record<string, unknown> = {};

        const teamIDs: Set<string> = new Set();

        for (const round of rounds) {
            for (const meet of round.meets) {
                teamIDs.add(meet.idA);
                teamIDs.add(meet.idB);
            }
            for (const bye of round.byes) {
                teamIDs.add(bye.id);
            }
        }

        const teamsArr = await Promise.all([...teamIDs].map(l => this.getTeam(l)));
        const teams: Record<string, ITeam> = {};

        for (const team of teamsArr) {
            if (team.status === "success") {
                teams[team.data.id] = team.data;
            }
        }

        const allDbUpdates = await Promise.all(
            rounds.flatMap(l =>
                l.meets.map(j => {
                    const id = this.database.ref(`${this.PREFIX}/dualMeets/`).push().key!;
                    j.id = id;
                    let teamA:
                        | ITeam
                        | {
                              name: string;
                              abbreviation: string;
                              fencers: IDualMeetTeam["fencers"];
                          } = teams[j.idA];
                    if (!teamA)
                        teamA = {
                            name: j.nameA,
                            abbreviation: j.abbA,
                            fencers: { Sabre: [], Foil: [], Epee: [] }
                        };
                    let teamB:
                        | ITeam
                        | {
                              name: string;
                              abbreviation: string;
                              fencers: IDualMeetTeam["fencers"];
                          } = teams[j.idB];
                    if (!teamB)
                        teamB = {
                            name: j.nameB,
                            abbreviation: j.abbB,
                            fencers: { Sabre: [], Foil: [], Epee: [] }
                        };
                    return this.getDbUpdatesForDualMeet(
                        id,
                        teamA,
                        teamB,
                        DualMeetType.CollegeEvent,
                        season,
                        startDate,
                        undefined,
                        undefined,
                        eventId
                    );
                })
            )
        );

        for (let i = 0; i < rounds.length; i++) {
            databaseUpdates[`${this.PREFIX}/events/${eventId}/${gender}Rounds/${i}`] = rounds[i];
        }

        for (const databaseAdditions of allDbUpdates) {
            for (const i in databaseAdditions) {
                if (i.startsWith(`${this.PREFIX}/teams/`) && i in databaseUpdates) {
                    const merged = new Set([...((databaseUpdates[i] as unknown[]) || []), ...(databaseAdditions[i] as unknown[])]);
                    databaseUpdates[i] = [...merged];
                } else if (i.startsWith(`${this.PREFIX}/fencers`) && i in databaseUpdates) {
                    const merged = new Set([...((databaseUpdates[i] as unknown[]) || []), ...(databaseAdditions[i] as unknown[])]);
                    databaseUpdates[i] = [...merged];
                } else {
                    databaseUpdates[i] = databaseAdditions[i];
                }
            }
        }

        return databaseUpdates;
    }

    public async createNewCollegeEvent(
        mensRounds: ICollegeEventRound[],
        womensRounds: ICollegeEventRound[],
        mensTeams: ICollegeEventTeam[],
        womensTeams: ICollegeEventTeam[],
        name: string,
        location: string,
        address: string,
        startDate: Date,
        hostName: string
    ): AsyncDBResult<string> {
        const user = this.auth.currentUser;
        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);

        if (!name) return new DBError("No name provided.");
        if (!location) return new DBError("No location provided.");
        if (!address) return new DBError("No address provided.");
        if (!startDate) return new DBError("No start date provided.");

        try {
            const result = await this.functions.httpsCallable("createCollegeEvent")({
                prefix: this.PREFIX,
                mensRounds,
                womensRounds,
                mensTeams,
                womensTeams,
                name,
                location,
                address,
                startDate: Number(startDate),
                hostName
            });

            return result.data;
        } catch (error) {
            return new DBError(error.message);
        }
    }

    public getLiveCollegeEvents(listener?: Listener<DBResult<ICollegeEvent[]>>): AsyncDBResult<ICollegeEvent[]> {
        return new Promise(res => {
            const todaysDate = new Date();
            todaysDate.setHours(0, 0, 0, 0);
            const startTimeStamp = todaysDate.getTime(); // yesterday
            const endTimeStamp = startTimeStamp + 86400000; // tomorrow
            this.database
                .ref(`${this.PREFIX}/events`)
                .orderByChild("startedAt")
                .startAt(startTimeStamp)
                .endAt(endTimeStamp)
                .on("value", async queryResults => {
                    const val: Record<string, ICollegeEvent> | null = queryResults.val();

                    const resolved = val
                        ? Object.values(val)
                              .filter(l => l.published)
                              .map(val => {
                                  const event = { ...DEFAULT_EVENT, ...val };
                                  //@ts-ignore
                                  event.mensRounds = event.mensRounds.map(l => ({ byes: [], meets: [], ...l }));
                                  //@ts-ignore
                                  event.womensRounds = event.womensRounds.map(l => ({ byes: [], meets: [], ...l }));
                                  return event;
                              })
                        : [];

                    resolved.sort((a, b) => Number(b.startedAt) - Number(a.startedAt));

                    listener && listener(new DBSuccess(resolved));
                    res(new DBSuccess(resolved));
                });
        });
    }

    public getCollegeEventsRaw(listener?: Listener<DBResult<Record<string, ICollegeEvent>>>): AsyncDBResult<Record<string, ICollegeEvent>> {
        if (listener) {
            return new Promise(res => {
                this.database.ref(`${this.PREFIX}/events`).on("value", v => {
                    const val = v.val() || {};
                    for (const id in val) {
                        const event = { ...DEFAULT_EVENT, ...val[id] };
                        event.mensRounds = event.mensRounds.map(l => ({
                            byes: [],
                            meets: [],
                            ...l
                        }));
                        event.womensRounds = event.womensRounds.map(l => ({
                            byes: [],
                            meets: [],
                            ...l
                        }));
                        val[id] = event;
                    }
                    listener(new DBSuccess(val));
                    res(new DBSuccess(val));
                });
            });
        } else {
            return new Promise(res => {
                this.database.ref(`${this.PREFIX}/events`).once("value", v => {
                    const val = v.val() || {};
                    for (const id in val) {
                        const event = { ...DEFAULT_EVENT, ...val[id] };
                        event.mensRounds = event.mensRounds.map(l => ({
                            byes: [],
                            meets: [],
                            ...l
                        }));
                        event.womensRounds = event.womensRounds.map(l => ({
                            byes: [],
                            meets: [],
                            ...l
                        }));
                        val[id] = event;
                    }
                    res(new DBSuccess(val));
                });
            });
        }
    }

    public stopListeningCollegeEventsRaw(listener?: (v: Record<string, ICollegeEvent>) => void) {
        removeRefListener(`${this.PREFIX}/events`, listener);
    }

    public async getCollegeEvent(
        id: string,
        options?: { listener?: Listener<DBResult<ICollegeEvent>>; preferCache?: boolean }
    ): AsyncDBResult<ICollegeEvent> {
        const listener = options?.listener;
        const preferCache = options?.preferCache ?? false;

        const processEvent = (event: ICollegeEvent) => {
            const val = { ...DEFAULT_EVENT, ...event };
            // @ts-ignore
            val.mensRounds = val.mensRounds.filter(Boolean).map(j => ({ byes: [], meets: [], ...j }));
            // @ts-ignore
            val.womensRounds = val.womensRounds.filter(Boolean).map(j => ({ byes: [], meets: [], ...j }));
            val.createdAt = new Date(val.createdAt);
            val.startedAt = new Date(val.startedAt);
            return val;
        };

        if (listener) {
            return new Promise(async res => {
                const l = async v => {
                    const val = v.val() as ICollegeEvent;
                    const response = new DBSuccess(processEvent(val));
                    listener && listener(response);
                    res(response);
                };

                this.database.ref(`${this.PREFIX}/events/${id}`).on("value", l);
                this.listeners.set(listener, l);
            });
        } else {
            if (preferCache && this.cache.collegeEvents[id]) {
                return new DBSuccess(this.cache.collegeEvents[id]);
            }

            const val = await refOnce<ICollegeEvent>(`${this.PREFIX}/events/${id}`);
            if (val) {
                this.cache.collegeEvents[id] = val;
                this.cache.queueSave("collegeEvents");
                return new DBSuccess(processEvent(val));
            } else {
                return new DBError(ERROR_EVENT_DOES_NOT_EXIST);
            }
        }
    }

    public stopListeningCollegeEvent(id: string, listener?: Listener<DBResult<ICollegeEvent>>) {
        removeRefListener(`${this.PREFIX}/events/${id}`, listener);
    }

    public async publishCollegeEvent(id: string): AsyncDBResult<boolean>;
    public async publishCollegeEvent(eventData: ICollegeEvent): AsyncDBResult<boolean>;
    public async publishCollegeEvent(eventDataOrId: string | ICollegeEvent): AsyncDBResult<boolean> {
        let eventData = eventDataOrId;
        if (typeof eventData === "string") {
            const eventRequest = await this.getCollegeEvent(eventData);
            if (eventRequest.status === "fail") return eventRequest;
            eventData = eventRequest.data;
        }
        const currentUser = await this.getCurrentUserInfo();
        if (currentUser.status === "fail") return currentUser;

        const date = Date.now();
        const databaseUpdates: Record<string, unknown> = {};

        const meetIDs: Set<string> = new Set();

        const allRounds = [...eventData.mensRounds, ...eventData.womensRounds];

        for (const round of allRounds) {
            for (const meet of round.meets) {
                meetIDs.add(meet.id);
            }
        }

        databaseUpdates[`${this.PREFIX}/events/${eventData.id}/published`] = true;

        for (const meet of meetIDs) {
            databaseUpdates[`${this.PREFIX}/dualMeets/${meet}/published`] = true;
            databaseUpdates[`${this.PREFIX}/dualMeets/${meet}/publishedAt`] = date;
            databaseUpdates[`${this.PREFIX}/dualMeets/${meet}/publishedBy`] = currentUser.data.id;
        }

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async deleteCollegeEvent(id: string): AsyncDBResult<boolean>;
    public async deleteCollegeEvent(eventData: ICollegeEvent): AsyncDBResult<boolean>;
    public async deleteCollegeEvent(eventDataOrId: string | ICollegeEvent): AsyncDBResult<boolean> {
        let eventData = eventDataOrId;
        if (typeof eventData === "string") {
            const eventRequest = await this.getCollegeEvent(eventData);
            if (eventRequest.status === "fail") return eventRequest;
            eventData = eventRequest.data;
        }

        const allRounds = [...eventData.mensRounds, ...eventData.womensRounds];

        const databaseUpdates: Record<string, unknown> = {};

        let teamIDs: Set<string> = new Set();

        for (const round of allRounds) {
            for (const meet of round.meets) {
                if (!teamIDs.has(meet.idA)) teamIDs.add(meet.idA);
                if (!teamIDs.has(meet.idB)) teamIDs.add(meet.idB);
            }
            for (const bye of round.byes) {
                teamIDs.add(bye.id);
            }
        }

        teamIDs = new Set([...teamIDs].filter(l => !l.startsWith("writeIn")));

        const teamsData = await Promise.all([...teamIDs].map(l => this.getTeam(l)));
        const teams: Record<string, ITeam> = {};
        for (const team of teamsData) {
            if (team.status === "fail") continue;
            teams[team.data.id] = team.data;
        }

        const meetIDs = allRounds.flatMap(l => l.meets.map(j => j.id));
        const allMeetUpdatesPromises = await Promise.allResolved(meetIDs.map(l => this.getDbUpdatesForDeletingDualMeet(l)));
        const allMeetUpdates = allMeetUpdatesPromises
            .filter(l => l.status === "success")
            .map(l => (l as DBSuccess<Record<string, unknown>>).data);
        for (const meetUpdates of allMeetUpdates) {
            for (const ref in meetUpdates) {
                if (ref.startsWith(`${this.PREFIX}/fencers`)) {
                    const fencerArr = databaseUpdates[ref] as string[];
                    if (fencerArr) {
                        databaseUpdates[ref] = fencerArr.filter(l => (meetUpdates[ref] as string[]).includes(l));
                        continue;
                    }
                }
                if (ref.startsWith(`${this.PREFIX}/teams`)) {
                    const teamArr = databaseUpdates[ref] as string[];
                    if (teamArr) {
                        databaseUpdates[ref] = teamArr.filter(l => (meetUpdates[ref] as string[]).includes(l));
                        continue;
                    }
                }
                if (ref.endsWith("/byes") || ref.endsWith("/meets")) {
                    continue;
                }
                databaseUpdates[ref] = meetUpdates[ref];
            }
        }

        databaseUpdates[`${this.PREFIX}/events/${eventData.id}`] = null;

        for (const meetId of meetIDs) {
            databaseUpdates[`${this.PREFIX}/disputes/${meetId}`] = null;
        }

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async addMeetToCollegeEvent(eventId: string, meetId: string, gender: "mens" | "womens", round: number): AsyncDBResult<boolean> {
        const event = await this.getCollegeEvent(eventId);
        if (event.status === "fail") return event;

        const meetRequest = await this.getDualMeet(meetId);
        if (meetRequest.status === "fail") return meetRequest;
        const meet = meetRequest.data;
        if (!meet.team1.id || !meet.team2.id) {
            return new DBError(ERROR_UNKNOWN);
        }

        const [team1Request, team2Request] = await Promise.all([this.getTeam(meet.team1.id), this.getTeam(meet.team2.id)]);
        if (team1Request.status === "fail") return team1Request;
        if (team2Request.status === "fail") return team2Request;
        const team1 = team1Request.data;
        const team2 = team2Request.data;

        const newMeet: ICollegeEventRoundMeet = {
            abbA: team1.abbreviation || team1.name,
            abbB: team2.abbreviation || team2.name,
            id: meet.id,
            idA: team1.id,
            idB: team2.id,
            nameA: team1.name,
            nameB: team2.name,
            regionA: team1.region,
            regionB: team1.region
        };

        const existingMeetCount = event.data[gender === "mens" ? "mensRounds" : "womensRounds"]?.[round]?.meets?.length || 0;
        event.data[gender === "mens" ? "mensRounds" : "womensRounds"]?.[round]?.meets?.push(newMeet);

        const byeUpdatesResult = await this.getCollegeEventByeUpdates(event.data, gender);
        if (byeUpdatesResult.status === "fail") return byeUpdatesResult;
        const databaseUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/events/${eventId}/${gender}Rounds/${round}/meets/${existingMeetCount}`]: newMeet,
            [`${this.PREFIX}/dualMeets/${meetId}/eventID`]: eventId,
            ...byeUpdatesResult.data
        };

        // If the event is published, also publish the new meet
        if (event.data.published) {
            databaseUpdates[`${this.PREFIX}/dualMeets/${meetId}/published`] = true;
            databaseUpdates[`${this.PREFIX}/dualMeets/${meetId}/publishedAt`] = Date.now();
            databaseUpdates[`${this.PREFIX}/dualMeets/${meetId}/publishedBy`] = meet.createdBy;
        }

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async removeMeetFromCollegeEvent(eventId: string, meetId: string, gender: "mens" | "womens"): AsyncDBResult<boolean> {
        const event = await this.getCollegeEvent(eventId);
        if (event.status === "fail") return event;

        const rounds = gender === "mens" ? event.data.mensRounds : event.data.womensRounds;
        let meetFound = false;
        let roundIndex = -1;
        let meetIndex = -1;

        // Find the meet in the rounds
        for (let i = 0; i < rounds.length; i++) {
            const round = rounds[i];
            for (let j = 0; j < round.meets.length; j++) {
                if (round.meets[j].id === meetId) {
                    meetFound = true;
                    roundIndex = i;
                    meetIndex = j;
                    break;
                }
            }
            if (meetFound) break;
        }

        if (!meetFound) {
            return new DBError("Meet not found in event schedule");
        }

        // Remove the meet from the round
        rounds[roundIndex].meets.splice(meetIndex, 1);

        // Update the database
        const databaseUpdates: Record<string, unknown> = {};
        databaseUpdates[`${this.PREFIX}/events/${eventId}/${gender}Rounds/${roundIndex}/meets`] = rounds[roundIndex].meets;

        // Get bye updates after removing the meet
        const byeUpdatesResult = await this.getCollegeEventByeUpdates(event.data, gender);
        if (byeUpdatesResult.status === "fail") return byeUpdatesResult;

        // Merge bye updates with database updates
        Object.assign(databaseUpdates, byeUpdatesResult.data);

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async addRoundToCollegeEvent(eventId: string, gender: "mens" | "womens", roundName?: string): AsyncDBResult<boolean> {
        const event = await this.getCollegeEvent(eventId);
        if (event.status === "fail") return event;

        const rounds = gender === "mens" ? event.data.mensRounds : event.data.womensRounds;
        const roundNumber = rounds.length + 1;
        const name = roundName || `Round ${roundNumber}`;

        const newRound: ICollegeEventRound = {
            name,
            meets: [],
            byes: []
        };

        const databaseUpdates: Record<string, unknown> = {};
        databaseUpdates[`${this.PREFIX}/events/${eventId}/${gender}Rounds/${rounds.length}`] = newRound;

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async removeRoundFromCollegeEvent(eventId: string, gender: "mens" | "womens", roundIndex: number): AsyncDBResult<boolean> {
        const event = await this.getCollegeEvent(eventId);
        if (event.status === "fail") return event;

        const rounds = gender === "mens" ? event.data.mensRounds : event.data.womensRounds;

        if (roundIndex < 0 || roundIndex >= rounds.length) {
            return new DBError("Round index out of bounds");
        }

        // Delete all meets in this round
        const round = rounds[roundIndex];
        for (const meet of round.meets) {
            const deleteResult = await this.deleteDualMeet(meet.id);
            if (deleteResult.status === "fail") {
                // Continue deleting other meets even if one fails
                console.error(`Failed to delete meet ${meet.id}:`, deleteResult.data);
            }
        }

        const originalLength = rounds.length;

        // Remove the round from the array
        rounds.splice(roundIndex, 1);

        const databaseUpdates: Record<string, unknown> = {};

        // Set the removed round to null
        databaseUpdates[`${this.PREFIX}/events/${eventId}/${gender}Rounds/${roundIndex}`] = null;

        // Shift all subsequent rounds down by one index
        for (let i = roundIndex; i < rounds.length; i++) {
            databaseUpdates[`${this.PREFIX}/events/${eventId}/${gender}Rounds/${i}`] = rounds[i];
        }

        // Remove the last element if we had more rounds before (to handle the gap left by the removed round)
        if (roundIndex < originalLength - 1) {
            databaseUpdates[`${this.PREFIX}/events/${eventId}/${gender}Rounds/${rounds.length}`] = null;
        }

        // Get bye updates after removing the round
        const byeUpdatesResult = await this.getCollegeEventByeUpdates(event.data, gender);
        if (byeUpdatesResult.status === "fail") return byeUpdatesResult;

        // Merge bye updates with database updates
        Object.assign(databaseUpdates, byeUpdatesResult.data);

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async getCollegeEventByeUpdates(
        eventId: string | ICollegeEvent,
        gender: "mens" | "womens"
    ): AsyncDBResult<Record<string, unknown>> {
        let event = eventId;
        if (typeof event === "string") {
            const eventRequest = await this.getCollegeEvent(event);
            if (eventRequest.status === "fail") return eventRequest;
            event = eventRequest.data;
        }

        const rounds = gender === "mens" ? event.mensRounds : event.womensRounds;
        const relevantTeams = gender === "mens" ? event.mensTeams : event.womensTeams;

        const changesNeeded: number[] = [];

        for (let i = 0; i < rounds.length; i++) {
            const round = rounds[i];

            const oldByes = round.byes.map(l => l.id);

            const teamsInRound = round.meets.flatMap(l => (l ? [l.idA, l.idB] : []));
            const noDuplicates = [...new Set(teamsInRound)];
            const otherTeams = relevantTeams.filter(l => !noDuplicates.includes(l.id));

            if (oldByes.every(l => otherTeams.some(j => j.id === l)) && otherTeams.every(l => oldByes.includes(l.id))) {
                continue;
            }

            changesNeeded.push(i);

            round.byes = await Promise.all(
                otherTeams.map(async l => ({
                    id: l.id,
                    abb: l.abbreviation,
                    name: l.name,
                    // TODO: make this type safe but fr
                    region: ((await this.getTeam(l.id)) as DBSuccess<ITeam>)!.data.region
                }))
            );
        }

        const databaseUpdates: Record<string, unknown> = {};

        for (const change of changesNeeded) {
            databaseUpdates[`${this.PREFIX}/events/${event.id}/${gender}Rounds/${change}/byes`] = rounds[change].byes;
        }

        return new DBSuccess(databaseUpdates);
    }

    public async applyCollegeEventByeUpdates(eventId: string | ICollegeEvent, gender: "mens" | "womens"): AsyncDBResult<boolean> {
        const updates = await this.getCollegeEventByeUpdates(eventId, gender);
        if (updates.status === "fail") return updates;
        await this.database.ref().update(updates.data);
        return new DBSuccess(true);
    }

    public async moveCollegeEventMeetUp(
        eventId: string | ICollegeEvent,
        gender: "mens" | "womens",
        meet: ICollegeEventRoundMeet
    ): AsyncDBResult<boolean> {
        let event = eventId;
        if (typeof event === "string") {
            const eventRequest = await this.getCollegeEvent(event);
            if (eventRequest.status === "fail") return eventRequest;
            event = eventRequest.data;
        }

        const rounds = gender === "mens" ? event.mensRounds : event.womensRounds;
        const roundIdx = rounds.findIndex(l => l.meets.some(j => j.id === meet.id));
        const meetIdx = rounds[roundIdx].meets.findIndex(j => j.id === meet.id);

        if (roundIdx <= 0) return new DBError(ERROR_CANNOT_MOVE_MEET);

        const aboveMeet = rounds[roundIdx - 1].meets[meetIdx];

        rounds[roundIdx].meets[meetIdx] = aboveMeet;
        rounds[roundIdx - 1].meets[meetIdx] = meet;

        rounds[roundIdx].meets = rounds[roundIdx].meets.filter(l => Boolean(l));
        rounds[roundIdx - 1].meets = rounds[roundIdx - 1].meets.filter(l => Boolean(l));

        let databaseUpdates: Record<string, unknown> = {
            [`${this.PREFIX}/events/${event.id}/${gender}Rounds/${roundIdx}/meets`]: rounds[roundIdx].meets,
            [`${this.PREFIX}/events/${event.id}/${gender}Rounds/${roundIdx - 1}/meets`]: rounds[roundIdx - 1].meets
        };

        const byeUpdates = await this.getCollegeEventByeUpdates(event, gender);
        if (byeUpdates.status === "fail") return byeUpdates;
        databaseUpdates = { ...databaseUpdates, ...byeUpdates.data };
        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async moveCollegeEventMeetDown(
        eventId: string | ICollegeEvent,
        gender: "mens" | "womens",
        meet: ICollegeEventRoundMeet
    ): AsyncDBResult<boolean> {
        let event = eventId;
        if (typeof event === "string") {
            const eventRequest = await this.getCollegeEvent(event);
            if (eventRequest.status === "fail") return eventRequest;
            event = eventRequest.data;
        }

        const rounds = gender === "mens" ? event.mensRounds : event.womensRounds;
        const roundIdx = rounds.findIndex(l => l.meets.some(j => j.id === meet.id));
        const meetIdx = rounds[roundIdx].meets.findIndex(j => j.id === meet.id);

        if (roundIdx === -1 || roundIdx >= rounds.length - 1) return new DBError(ERROR_CANNOT_MOVE_MEET);

        const belowMeet = rounds[roundIdx + 1].meets[meetIdx];

        rounds[roundIdx].meets[meetIdx] = belowMeet;
        rounds[roundIdx + 1].meets[meetIdx] = meet;

        rounds[roundIdx].meets = rounds[roundIdx].meets.filter(l => Boolean(l));
        rounds[roundIdx + 1].meets = rounds[roundIdx + 1].meets.filter(l => Boolean(l));

        let databaseUpdates: Record<string, unknown> = {};

        databaseUpdates[`${this.PREFIX}/events/${event.id}/${gender}Rounds/${roundIdx}/meets`] = rounds[roundIdx].meets;
        databaseUpdates[`${this.PREFIX}/events/${event.id}/${gender}Rounds/${roundIdx + 1}/meets`] = rounds[roundIdx + 1].meets;

        const byeUpdates = await this.getCollegeEventByeUpdates(event, gender);
        if (byeUpdates.status === "fail") return byeUpdates;

        databaseUpdates = { ...databaseUpdates, ...byeUpdates.data };

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async moveCollegeEventMeetLeft(
        eventId: string | ICollegeEvent,
        gender: "mens" | "womens",
        meet: ICollegeEventRoundMeet
    ): AsyncDBResult<boolean> {
        let event = eventId;
        if (typeof event === "string") {
            const eventRequest = await this.getCollegeEvent(event);
            if (eventRequest.status === "fail") return eventRequest;
            event = eventRequest.data;
        }

        const rounds = gender === "mens" ? event.mensRounds : event.womensRounds;
        const roundIdx = rounds.findIndex(l => l.meets.some(j => j.id === meet.id));
        const round = rounds[roundIdx];
        const meetIdx = rounds[roundIdx].meets.findIndex(j => j.id === meet.id);

        if (meetIdx <= 0) return new DBError(ERROR_CANNOT_MOVE_MEET);

        const leftMeet = round.meets[meetIdx - 1];

        round.meets[meetIdx] = leftMeet;
        round.meets[meetIdx - 1] = meet;

        let databaseUpdates: Record<string, unknown> = {};

        databaseUpdates[`${this.PREFIX}/events/${event.id}/${gender}Rounds/${roundIdx}/meets`] = rounds[roundIdx].meets;

        const byeUpdates = await this.getCollegeEventByeUpdates(event, gender);
        if (byeUpdates.status === "fail") return byeUpdates;

        databaseUpdates = { ...databaseUpdates, ...byeUpdates.data };

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async moveCollegeEventMeetRight(
        eventId: string | ICollegeEvent,
        gender: "mens" | "womens",
        meet: ICollegeEventRoundMeet
    ): AsyncDBResult<boolean> {
        let event = eventId;
        if (typeof event === "string") {
            const eventRequest = await this.getCollegeEvent(event);
            if (eventRequest.status === "fail") return eventRequest;
            event = eventRequest.data;
        }

        const rounds = gender === "mens" ? event.mensRounds : event.womensRounds;
        const roundIdx = rounds.findIndex(l => l.meets.some(j => j.id === meet.id));
        const round = rounds[roundIdx];
        const meetIdx = rounds[roundIdx].meets.findIndex(j => j.id === meet.id);

        if (meetIdx >= round.meets.length - 1) return new DBError(ERROR_CANNOT_MOVE_MEET);

        const rightMeet = round.meets[meetIdx + 1];

        round.meets[meetIdx] = rightMeet;
        round.meets[meetIdx + 1] = meet;

        let databaseUpdates: Record<string, unknown> = {};

        databaseUpdates[`${this.PREFIX}/events/${event.id}/${gender}Rounds/${roundIdx}/meets`] = rounds[roundIdx].meets;

        const byeUpdates = await this.getCollegeEventByeUpdates(event, gender);
        if (byeUpdates.status === "fail") return byeUpdates;

        databaseUpdates = { ...databaseUpdates, ...byeUpdates.data };

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async switchSidesOfEventMeet(eventId: string | ICollegeEvent, eventMeet: ICollegeEventRoundMeet): AsyncDBResult<boolean> {
        let event = eventId;
        if (typeof event === "string") {
            const eventRequest = await this.getCollegeEvent(event);
            if (eventRequest.status === "fail") return eventRequest;
            event = eventRequest.data;
        }

        let gender: "mens" | "womens" | null = null;
        let roundIdx = -1;
        let meetIdx = -1;
        {
            roundIdx = event.mensRounds.findIndex(l => l.meets.some(j => j.id === eventMeet.id));
            if (roundIdx !== -1) {
                meetIdx = event.mensRounds[roundIdx].meets.findIndex(j => j.id === eventMeet.id);
                gender = "mens";
            }
        }
        if (!gender) {
            roundIdx = event.womensRounds.findIndex(l => l.meets.some(j => j.id === eventMeet.id));
            if (roundIdx !== -1) {
                meetIdx = event.womensRounds[roundIdx].meets.findIndex(j => j.id === eventMeet.id);
                gender = "womens";
            }
        }

        if (!gender) {
            return new DBError(ERROR_MEET_NOT_IN_SCHEDULE);
        }

        const databaseUpdates = {};

        const swappedEventMeet: ICollegeEventRoundMeet = {
            id: eventMeet.id,
            abbA: eventMeet.abbB,
            abbB: eventMeet.abbA,
            idA: eventMeet.idB,
            idB: eventMeet.idA,
            nameA: eventMeet.nameB,
            nameB: eventMeet.nameA,
            regionA: eventMeet.regionB,
            regionB: eventMeet.regionA
        };

        databaseUpdates[`${this.PREFIX}/events/${event.id}/${gender}Rounds/${roundIdx}/meets/${meetIdx}`] = swappedEventMeet;

        const meetRequest = await this.getDualMeetRaw(eventMeet.id);
        if (meetRequest.status === "fail") return meetRequest;
        const meet = meetRequest.data;

        const splitName = meet.name.split(" vs. ");
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/name`] = `${splitName[1]} vs. ${splitName[0]}`;
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/team1ID`] = meet.team2ID || null;
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/team2ID`] = meet.team1ID || null;
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/team1WriteInData`] = meet.team2WriteInData || null;
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/team2WriteInData`] = meet.team1WriteInData || null;
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/team1Lineup`] = null;
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/team2Lineup`] = null;
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/lineupsVisibleSabre`] = false;
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/lineupsVisibleFoil`] = false;
        databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/lineupsVisibleEpee`] = false;

        const boutRequests = await Promise.all(meet.bouts.map(l => this.getBout(l.id)));
        const bouts = boutRequests.filter(isSuccess).map(l => l.data);
        const boutUpdates = await Promise.all([
            ...bouts.map(l => this.getUpdatesToRemoveBoutFencer(l, "left", false)),
            ...bouts.map(l => this.getUpdatesToRemoveBoutFencer(l, "right", false))
        ]);

        for (const updatesResult of boutUpdates) {
            if (updatesResult.status === "fail") return updatesResult;
            const updates = updatesResult.data;
            for (const ref in updates) {
                if (ref in databaseUpdates && ref.endsWith("bouts")) {
                    const prev = databaseUpdates[ref] as string[];
                    const intersection = prev.filter(l => (updates[ref] as string[]).includes(l));
                    databaseUpdates[ref] = intersection;
                } else {
                    databaseUpdates[ref] = updates[ref];
                }
            }
        }

        // Swap team info on each bout's fencers (fencer1 gets old team2 info, fencer2 gets old team1 info)
        // Also reset the winner on the meet's bouts array since fencers are being cleared
        for (let boutIdx = 0; boutIdx < bouts.length; boutIdx++) {
            const bout = bouts[boutIdx];
            // Get the team info that should now be on fencer1 (was team2, now is new team1)
            const newTeam1Name = eventMeet.nameB; // old team2 name becomes new team1
            const newTeam1Abb = eventMeet.abbB;
            // Get the team info that should now be on fencer2 (was team1, now is new team2)
            const newTeam2Name = eventMeet.nameA; // old team1 name becomes new team2
            const newTeam2Abb = eventMeet.abbA;

            databaseUpdates[`${this.PREFIX}/bouts/${bout.id}/fencer1/fencerInfo/teamName`] = newTeam1Name;
            databaseUpdates[`${this.PREFIX}/bouts/${bout.id}/fencer1/fencerInfo/teamAbbreviation`] = newTeam1Abb;
            databaseUpdates[`${this.PREFIX}/bouts/${bout.id}/fencer2/fencerInfo/teamName`] = newTeam2Name;
            databaseUpdates[`${this.PREFIX}/bouts/${bout.id}/fencer2/fencerInfo/teamAbbreviation`] = newTeam2Abb;

            // Reset winner on meet's bouts array entry (score display uses this)
            databaseUpdates[`${this.PREFIX}/dualMeets/${meet.id}/bouts/${boutIdx}/winner`] = 0;
        }

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async processLineups(meetId: string): AsyncDBResult<boolean> {
        const lineupsObj = {
            1: [1, 9, 18],
            2: [2, 11, 19],
            3: [0, 10, 20],
            4: [2, 10, 18],
            5: [1, 11, 20],
            6: [0, 9, 19]
        };

        const weaponOffsets = {
            Sabre: 0,
            Foil: 3,
            Epee: 6
        };

        const meetData = await this.getDualMeet(meetId);
        if (meetData.status === "fail") return meetData;
        const meet = meetData.data;

        if (!meet.team1Lineup || !meet.team2Lineup) return new DBError("No lineups were provided.");

        const databaseUpdates: Record<string, unknown> = {};

        const bouts = meet.bouts.map(l => l.id);

        const processWeapon = (weapon: Weapon) => {
            const lineup1 = meet.team1Lineup?.[weapon as Weapon];
            const lineup2 = meet.team2Lineup?.[weapon as Weapon];
            if (!lineup1 && !lineup2) return;

            const forfeits1 = new Set<number>();
            const forfeits2 = new Set<number>();
            const doubleForfeits = new Set<number>();

            for (const id in lineup1) {
                const fencer = lineup1[Number(id) as 1 | 2 | 3 | 4 | 5 | 6];
                if (!fencer) continue;
                if ("type" in fencer) {
                    for (let i = 0; i < 3; i++) {
                        const properIdx = lineupsObj[Number(id)][i] + weaponOffsets[weapon];
                        this.updateBout(bouts[properIdx], {
                            [`fencer1/forfeit`]: true
                        });
                        forfeits1.add(properIdx);
                    }
                } else if ("id" in fencer) {
                    for (let i = 0; i < 3; i++) {
                        const properIdx = lineupsObj[Number(id)][i] + weaponOffsets[weapon];
                        this.subBoutFencer(bouts[properIdx], "left", fencer.id!, false, false);
                    }
                } else {
                    const indices = [0, 1, 2].map(i => lineupsObj[Number(id)][i] + weaponOffsets[weapon]);
                    const boutsToSub = indices.map(l => bouts[l]);
                    this.subBoutWriteIn(boutsToSub, meet.team1.id!, meet.season, weapon, "left", fencer, false);
                }
            }
            for (const id in lineup2) {
                const fencer = lineup2[Number(id) as 1 | 2 | 3 | 4 | 5 | 6];
                if (!fencer) continue;
                if ("type" in fencer) {
                    for (let i = 0; i < 3; i++) {
                        const properIdx = lineupsObj[Number(id)][i] + weaponOffsets[weapon];
                        this.updateBout(bouts[properIdx], {
                            [`fencer2/forfeit`]: true
                        });
                        if (forfeits1.has(properIdx)) {
                            forfeits1.delete(properIdx);
                            doubleForfeits.add(properIdx);
                        } else {
                            forfeits2.add(properIdx);
                        }
                    }
                } else if ("id" in fencer) {
                    for (let i = 0; i < 3; i++) {
                        const properIdx = lineupsObj[Number(id)][i] + weaponOffsets[weapon];
                        this.subBoutFencer(bouts[properIdx], "right", fencer.id!, false, false);
                    }
                } else {
                    const indices = [0, 1, 2].map(i => lineupsObj[Number(id)][i] + weaponOffsets[weapon]);
                    const boutsToSub = indices.map(l => bouts[l]);
                    this.subBoutWriteIn(boutsToSub, meet.team2.id!, meet.season, weapon, "right", fencer, false);
                }
            }

            for (const id of forfeits1) {
                this.endBout(bouts[id], BoutSide.Fencer2, meetId);
            }
            for (const id of forfeits2) {
                this.endBout(bouts[id], BoutSide.Fencer1, meetId);
            }
            for (const id of doubleForfeits) {
                this.endBout(bouts[id], 0, meetId);
            }

            databaseUpdates[`${this.PREFIX}/dualMeets/${meetId}/lineupsVisible${weapon}`] = true;
        };

        if (meet.team1Lineup.Sabre && meet.team2Lineup.Sabre && !meet.lineupsVisibleSabre) processWeapon("Sabre");
        if (meet.team1Lineup.Foil && meet.team2Lineup.Foil && !meet.lineupsVisibleFoil) processWeapon("Foil");
        if (meet.team1Lineup.Epee && meet.team2Lineup.Epee && !meet.lineupsVisibleEpee) processWeapon("Epee");

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async addLineupToMeet(meetId: string, side: 1 | 2, weapon: Weapon, lineup: Lineup): AsyncDBResult<boolean> {
        try {
            const databaseUpdates: Record<string, unknown> = {};

            for (const i in lineup[weapon]) {
                if (lineup[weapon][i] && !lineup[weapon][i].gradYear) {
                    // In case of undefined
                    delete lineup[weapon][i].gradYear;
                }
                databaseUpdates[`${this.PREFIX}/dualMeets/${meetId}/team${side}Lineup/${weapon}/${i}`] = lineup[weapon][i];
            }

            await this.database.ref().update(databaseUpdates);
            await this.processLineups(meetId);
            return new DBSuccess(true);
        } catch (e: unknown) {
            console.error("Error adding lineups", e);
            return new DBError(ERROR_ADDING_LINEUPS);
        }
    }

    public async unlockCollegeEvent(eventID: string): AsyncDBResult<boolean> {
        const databaseUpdates: Record<string, unknown> = {};

        const eventResult = await this.getCollegeEvent(eventID);
        if (eventResult.status === "fail") return eventResult;
        const event = eventResult.data;

        const now = Date.now();

        databaseUpdates[`${this.PREFIX}/events/${eventID}/unlockedAt`] = now;
        databaseUpdates[`${this.PREFIX}/events/${eventID}/unlockedDuration`] = 24 * 60 * 60 * 1000;

        const meets = getMeetsFromEvent(event);

        for (const id of meets) {
            databaseUpdates[`${this.PREFIX}/dualMeets/${id}/unlockedAt`] = now;
            databaseUpdates[`${this.PREFIX}/dualMeets/${id}/unlockedDuration`] = 24 * 60 * 60 * 1000;
        }

        await this.database.ref().update(databaseUpdates);

        return new DBSuccess(true);
    }

    public async getCollegeEventResults(
        eventID: string,
        options?: { listener?: Listener<DBResult<ICollegeEventResults | null>> }
    ): AsyncDBResult<ICollegeEventResults | null> {
        const listener = options?.listener;
        if (listener) {
            return new Promise(res => {
                if (eventID in this.collegeEventResults) {
                    listener(new DBSuccess(this.collegeEventResults[eventID]));
                }
                const l = v => {
                    const val = v.val();
                    if (!val) {
                        delete this.collegeEventResults[eventID];
                        listener(new DBSuccess(null));
                        res(new DBSuccess(null));
                    } else {
                        for (const i in val.mens) {
                            if (!("bouts" in val.mens[i])) val.mens[i].bouts = {};
                        }
                        for (const i in val.womens) {
                            if (!("bouts" in val.womens[i])) val.womens[i].bouts = {};
                        }
                        this.collegeEventResults[eventID] = val;
                        listener(new DBSuccess(val));
                        res(new DBSuccess(val));
                    }
                };
                this.listeners.set(listener, l, eventID);
                this.database.ref(`${this.PREFIX}/eventResults/${eventID}`).on("value", l);
            });
        } else {
            if (eventID in this.collegeEventResults) {
                const dbUpdatedAt = await this.database.ref(`${this.PREFIX}/eventResults/${eventID}/updatedAt`).once("value");
                if ((dbUpdatedAt.val() || Infinity) <= this.collegeEventResults[eventID].updatedAt) {
                    return new DBSuccess(this.collegeEventResults[eventID]);
                }
            }
            const v = await this.database.ref(`${this.PREFIX}/eventResults/${eventID}`).once("value");
            const val = v.val();
            if (!val) {
                delete this.collegeEventResults[eventID];
                return new DBSuccess(null);
            }
            for (const i in val.mens) {
                if (!("bouts" in val.mens[i])) val.mens[i].bouts = {};
            }
            for (const i in val.womens) {
                if (!("bouts" in val.womens[i])) val.womens[i].bouts = {};
            }
            this.collegeEventResults[eventID] = val;
            return new DBSuccess(val);
        }
    }

    public stopListeningCollegeEventResults(eventID: string, listener: Listener<DBResult<ICollegeEventResults>>) {
        const l = this.listeners.get(listener, eventID);
        this.database.ref(`${this.PREFIX}/eventResults/${eventID}`).off("value", l);
        this.listeners.delete(listener, eventID);
    }

    // #endregion

    // #region Tournaments

    public async getTournaments(): AsyncDBResult<Record<string, ITournament>> {
        try {
            const v = await this.database.ref(`${this.PREFIX}/tournaments`).once("value");
            const val = v.val();

            const results: Record<string, ITournament> = {};
            if (!val) return new DBSuccess(results);

            for (const id in val) {
                results[id] = { ...DEFAULT_TOURNAMENT, ...val[id] };
            }

            return new DBSuccess({ ...results });
        } catch (e: unknown) {
            return new DBError((e as Error).message);
        }
    }

    public async getTournament(tournamentID: string, options?: { listener?: Listener<DBResult<ITournament>> }): AsyncDBResult<ITournament> {
        const listener = options?.listener;
        if (listener) {
            if (this.tournaments[tournamentID]) {
                listener(new DBSuccess(this.tournaments[tournamentID]));
            }
            return new Promise(res => {
                const l = v => {
                    const val = v.val();
                    if (!val) return new DBError("The requested tournament could not be found");
                    const data: ITournament = { ...DEFAULT_TOURNAMENT, ...val };
                    this.tournaments[tournamentID] = data;
                    const result = new DBSuccess(data);
                    listener(result);
                    return res(result);
                };
                this.database.ref(`${this.PREFIX}/tournaments/${tournamentID}`).on("value", l);
                this.listeners.set(listener, l, tournamentID);
            });
        } else {
            try {
                const v = await this.database.ref(`${this.PREFIX}/tournaments/${tournamentID}`).once("value");
                const val = v.val();

                if (val) {
                    const data = { ...DEFAULT_TOURNAMENT, ...val };
                    this.tournaments[tournamentID] = data;
                    return new DBSuccess(data);
                } else {
                    return new DBError("The requested tournament could not be found");
                }
            } catch (e: unknown) {
                return new DBError((e as Error).message);
            }
        }
    }

    public stopListeningTournament(tournamentID: string, listener: Listener<DBResult<ITournament>>) {
        const l = this.listeners.get(listener, tournamentID);
        this.database.ref(`${this.PREFIX}/tournaments/${tournamentID}`).off("value", l);
        this.listeners.delete(listener, tournamentID);
    }

    public async getTournamentEvent(
        eventID: string,
        options?: { listener?: Listener<DBResult<ITournamentEvent>>; preferCache?: boolean }
    ): AsyncDBResult<ITournamentEvent> {
        const listener = options?.listener;
        if (listener) {
            if (this.tournamentEvents[eventID]) {
                listener(new DBSuccess(this.tournamentEvents[eventID]));
            }
            return new Promise(res => {
                const l = v => {
                    const val = v.val();
                    if (val) {
                        if (!("rounds" in val)) val.rounds = [];
                        if (val.type === "cba" && !("teams" in val)) val.teams = [];
                        if (val.type === "individual" && !("fencers" in val)) val.fencers = [];
                        this.tournamentEvents[eventID] = val;
                        const result = new DBSuccess(val);
                        listener(result);
                        res(result);
                    } else {
                        const result = new DBError("The requested tournament event could not be found");
                        listener(result);
                        res(result);
                    }
                };
                this.database.ref(`${this.PREFIX}/tournament-events/${eventID}`).on("value", l);
                this.listeners.set(listener, l, eventID);
            });
        } else {
            try {
                if (options?.preferCache && this.tournamentEvents[eventID]) {
                    return new DBSuccess(this.tournamentEvents[eventID]);
                }

                const v = await this.database.ref(`${this.PREFIX}/tournament-events/${eventID}`).once("value");
                const val = v.val();

                if (val) {
                    if (!("rounds" in val)) val.rounds = [];
                    if (val.type === "cba" && !("teams" in val)) val.teams = [];
                    if (val.type === "individual" && !("fencers" in val)) val.fencers = [];
                    this.tournamentEvents[eventID] = val;
                    this.cache.queueSave("tournamentEvents");
                    return new DBSuccess(val);
                } else {
                    return new DBError("The requested tournament event could not be found");
                }
            } catch (e: unknown) {
                return new DBError((e as Error).message);
            }
        }
    }

    public async getTournamentEventAddRoundTeamsBySeeding(
        eventID: string
    ): AsyncDBResult<ITournamentEventTeam[] | ITournamentEventFencer[]> {
        const eventResult = await this.getTournamentEvent(eventID, { preferCache: true });
        if (eventResult.status === "fail") return eventResult;
        const eventData = eventResult.data;

        try {
            if (eventData.rounds.length === 0) {
                // Use team count if no rounds created
                if ("teams" in eventData) {
                    return new DBSuccess(eventData.teams.sort((a, b) => a.seed - b.seed));
                } else {
                    return new DBSuccess(eventData.fencers.sort((a, b) => a.seed - b.seed));
                }
            } else {
                const round = await this.getTournamentRound(eventData.rounds[eventData.rounds.length - 1].id, { preferCache: true });
                if (round.status === "fail") return round;
                if ("teams" in round.data) {
                    const pairs = Object.entries(round.data.teams).map(([id, team]) => ({ team, results: round.data.results[id] }));
                    pairs.sort((a, b) => sortPoolResults(a.results, b.results));
                    return new DBSuccess(pairs.map(l => l.team));
                } else {
                    const pairs = Object.entries(round.data.fencers).map(([id, fencer]) => ({ fencer, results: round.data.results[id] }));
                    pairs.sort((a, b) => sortPoolResults(a.results, b.results));
                    return new DBSuccess(pairs.map(l => l.fencer));
                }
            }
        } catch (e: unknown) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public stopListeningTournamentEvent(eventID: string, listener: Listener<DBResult<ITournamentEvent>>) {
        const l = this.listeners.get(listener, eventID);
        this.database.ref(`${this.PREFIX}/tournament-events/${eventID}`).off("value", l);
        this.listeners.delete(listener, eventID);
    }

    public async getTournamentRound(
        roundID: string,
        options?: { listener?: Listener<DBResult<ITournamentRound>>; preferCache?: boolean }
    ): AsyncDBResult<ITournamentRound> {
        const listener = options?.listener;
        if (listener) {
            if (this.tournamentRounds[roundID]) {
                listener(new DBSuccess(this.tournamentRounds[roundID]));
            }
            return new Promise(res => {
                const l = v => {
                    const val = v.val();
                    if (val) {
                        if (val.type === "pool" && !("pools" in val)) val.pools = {};
                        if (val.variant === "cba" && !("teams" in val)) val.teams = [];
                        if (val.variant === "individual" && !("fencers" in val)) val.fencers = [];
                        this.tournamentRounds[roundID] = val;
                        const result = new DBSuccess(val);
                        listener(result);
                        res(result);
                    } else {
                        const result = new DBError("The requested tournament round could not be found");
                        listener(result);
                        res(result);
                    }
                };
                this.database.ref(`${this.PREFIX}/tournament-rounds/${roundID}`).on("value", l);
                this.listeners.set(listener, l, roundID);
            });
        } else {
            try {
                if (options?.preferCache && this.tournamentRounds[roundID]) {
                    return new DBSuccess(this.tournamentRounds[roundID]);
                }
                const v = await this.database.ref(`${this.PREFIX}/tournament-rounds/${roundID}`).once("value");
                const val = v.val();

                if (val) {
                    if (val.type === "pool" && !("pools" in val)) val.pools = {};
                    if (val.variant === "cba" && !("teams" in val)) val.teams = [];
                    if (val.variant === "individual" && !("fencers" in val)) val.fencers = [];
                    this.tournamentRounds[roundID] = val;
                    this.cache.queueSave("tournamentRounds");
                    return new DBSuccess(val);
                } else {
                    return new DBError("The requested tournament round could not be found");
                }
            } catch (e: unknown) {
                return new DBError((e as Error).message);
            }
        }
    }

    public stopListeningTournamentRound(eventID: string, listener: Listener<DBResult<ITournamentRound>>) {
        const l = this.listeners.get(listener, eventID);
        this.database.ref(`${this.PREFIX}/tournament-rounds/${eventID}`).off("value", l);
        this.listeners.delete(listener, eventID);
    }

    public async getTournamentPool(
        poolID: string,
        options?: { listener?: Listener<DBResult<ITournamentPool>>; preferCache?: boolean; checkUpdated?: boolean }
    ): AsyncDBResult<ITournamentPool> {
        try {
            if (options?.preferCache && poolID in this.tournamentPools) {
                return new DBSuccess(this.tournamentPools[poolID]);
            }

            const v = await this.database.ref(`${this.PREFIX}/tournament-pools/${poolID}`).once("value");
            const val = v.val();

            if (val === null) return new DBError("Cannot find pool");

            if (!("bouts" in val)) val.bouts = {};
            if (val.variant === "cba" && !("stripRestrictions" in val)) val.stripRestrictions = {};

            this.tournamentPools[val.id] = val;
            this.cache.queueSave("tournamentPools");

            return new DBSuccess(val);
        } catch (e: unknown) {
            return new DBError((e as Error).message);
        }
    }

    public stopListeningTournamentPool(poolID: string, listener: Listener<DBResult<ITournamentPool>>) {
        const l = this.listeners.get(listener, poolID);
        this.database.ref(`${this.PREFIX}/tournament-pools/${poolID}`).off("value", l);
        this.listeners.delete(listener, poolID);
    }

    public async createNewEmptyTournament(
        name: string,
        location: { name: string; city: string; state: string },
        startedAt: number
    ): AsyncDBResult<ID> {
        const user = this.auth.currentUser;
        if (!user) return new DBError(ERROR_NOT_LOGGED_IN);

        if (!name) return new DBError("No name provided.");
        if (!location) return new DBError("No location provided.");

        const id = this.database.ref(`${this.PREFIX}/tournaments`).push().key!;

        const seasonBase = new Date().getMonth() >= 7 ? new Date().getFullYear() : new Date().getFullYear() - 1;
        const season = `${seasonBase}-${seasonBase + 1}`;

        const d = Date.now();

        const databaseUpdates: Record<string, unknown> = {};
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/id`] = id;
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/updatedAt`] = d;
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/createdAt`] = d;
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/createdBy`] = user.uid;
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/name`] = name;
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/location/name`] = location.name;
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/location/city`] = location.city;
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/location/state`] = location.state;
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/startedAt`] = startedAt;
        databaseUpdates[`${this.PREFIX}/tournaments/${id}/season`] = season;

        await this.database.ref().update(databaseUpdates);
        return new DBSuccess(id);
    }

    public async getUpdatesToAddEventToTournament(
        tournamentID: string,
        startedAt: number,
        name: string,
        type: "individual" | "cba",
        gender: "mens" | "womens" | "both",
        weapon: Weapon
    ): AsyncDBResult<Record<string, unknown>> {
        const eventID = this.database.ref(`${this.PREFIX}/tournament-events`).push().key!;

        const databaseUpdates: Record<string, unknown> = {};
        databaseUpdates[`${this.PREFIX}/tournaments/${tournamentID}/events/${eventID}/id`] = eventID;
        databaseUpdates[`${this.PREFIX}/tournaments/${tournamentID}/events/${eventID}/startedAt`] = startedAt;
        databaseUpdates[`${this.PREFIX}/tournaments/${tournamentID}/events/${eventID}/name`] = name;
        databaseUpdates[`${this.PREFIX}/tournaments/${tournamentID}/updatedAt`] = Date.now();

        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/id`] = eventID;
        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/updatedAt`] = Date.now();
        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/startedAt`] = startedAt;
        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/name`] = name;
        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/type`] = type;
        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/gender`] = gender;
        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/weapon`] = weapon;
        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/tournamentID`] = tournamentID;

        return new DBSuccess(databaseUpdates);
    }

    public async addEventToTournament(
        tournamentID: string,
        startedAt: number,
        type: "individual" | "cba",
        gender: "mens" | "womens" | "both",
        weapon: Weapon
    ): AsyncDBResult<boolean> {
        try {
            const typeText = type == "cba" ? "Team" : "";
            const genderText = gender == "mens" ? "Men's" : gender == "womens" ? "Women's" : "";
            const name = `Senior ${typeText} ${genderText} ${weapon}`;
            const databaseUpdatesResult = await this.getUpdatesToAddEventToTournament(tournamentID, startedAt, name, type, gender, weapon);
            if (databaseUpdatesResult.status === "fail") return databaseUpdatesResult;
            await this.database.ref().update(databaseUpdatesResult.data);
            return new DBSuccess(true);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async updateTournamentEvent(eventID: string, eventData: ITournamentEvent): AsyncDBResult<boolean> {
        try {
            const databaseUpdates: Record<string, unknown> = {};
            eventData.updatedAt = Date.now();
            databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}`] = eventData;
            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async getUpdatesToSetTeamsOfEvent(eventID: string, teams: Record<string, number>): AsyncDBResult<Record<string, unknown>> {
        const teamResults: Record<string, AsyncDBResult<ITeam>> = {};
        for (const team in teams) {
            teamResults[team] = this.getTeam(team);
        }
        await Promise.all(Object.values(teamResults));
        const teamData: Record<string, ITeam> = {};
        for (const team in teamResults) {
            const result = (await teamResults[team]) as DBResult<ITeam>;
            if (result.status === "fail") return result;
            teamData[team] = result.data;
        }

        const databaseUpdates: Record<string, unknown> = {};
        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/updatedAt`] = Date.now();
        for (const teamID in teamData) {
            const team = teamData[teamID];
            const prefix = `${this.PREFIX}/tournament-events/${eventID}/teams/${teamID}`;
            databaseUpdates[`${prefix}/id`] = teamID;
            databaseUpdates[`${prefix}/name`] = team.name;
            databaseUpdates[`${prefix}/abbreviation`] = team.abbreviation;
            databaseUpdates[`${prefix}/orgID`] = team.orgID;
            databaseUpdates[`${prefix}/seed`] = teams[teamID];
            databaseUpdates[`${prefix}/seedStatus`] = SeedStatus.Advanced;
        }

        return new DBSuccess(databaseUpdates);
    }

    public async getUpdatesToAddPoolRoundToEvent(
        eventID: string,
        variant: "cba" | "individual",
        poolOptions: { pools: number[][]; eliminated: ID[]; exempt: ID[] }
    ): AsyncDBResult<Record<string, unknown>> {
        const eventResult = await this.getTournamentEvent(eventID);
        if (eventResult.status === "fail") return eventResult;
        const eventData = eventResult.data;
        const roundID = this.database.ref(`${this.PREFIX}/tournament-rounds`).push().key!;

        const teamsBySeedingResult = await this.getTournamentEventAddRoundTeamsBySeeding(eventID);
        if (teamsBySeedingResult.status === "fail") return teamsBySeedingResult;
        const teamsBySeeding = teamsBySeedingResult.data;

        for (const team of teamsBySeeding) {
            if (poolOptions.exempt.includes(team.id)) {
                team.seedStatus = SeedStatus.Exempt;
            } else if (poolOptions.eliminated.includes(team.id)) {
                team.seedStatus = SeedStatus.Eliminated;
            } else {
                team.seedStatus = SeedStatus.Advanced;
            }
        }

        eventData.rounds.push({ id: roundID, type: "pool", finished: false });

        const databaseUpdates: Record<string, unknown> = {};
        databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/updatedAt`] = Date.now();
        for (let i = 0; i < 10; i++) {
            const prefix = `${this.PREFIX}/tournament-events/${eventID}/rounds/${i}`;
            if (i < eventData.rounds.length) {
                databaseUpdates[`${prefix}/id`] = eventData.rounds[i].id;
                databaseUpdates[`${prefix}/type`] = eventData.rounds[i].type;
                databaseUpdates[`${prefix}/finished`] = eventData.rounds[i].finished;
            } else {
                databaseUpdates[`${prefix}/id`] = null;
                databaseUpdates[`${prefix}/type`] = null;
                databaseUpdates[`${prefix}/finished`] = null;
            }
        }
        const prefix = `${this.PREFIX}/tournament-rounds/${roundID}`;
        databaseUpdates[`${prefix}/id`] = roundID;
        databaseUpdates[`${prefix}/type`] = "pool";
        databaseUpdates[`${prefix}/variant`] = variant;
        databaseUpdates[`${prefix}/updatedAt`] = Date.now();
        if (variant === "cba") {
            const teams: Record<ID, ITournamentEventTeam> = {};
            const results: Record<ID, PoolResults> = {};
            for (let i = 0; i < teamsBySeeding.length; i++) {
                const team = teamsBySeeding[i];
                teams[team.id] = team as ITournamentEventTeam;
                const status = poolOptions.exempt.includes(team.id)
                    ? SeedStatus.Exempt
                    : poolOptions.eliminated.includes(team.id)
                      ? SeedStatus.Eliminated
                      : SeedStatus.Advanced;
                results[team.id] = { v: 0, m: 0, ts: 0, tr: 0, status };
                if (status !== SeedStatus.Advanced) {
                    results[team.id].place = i + 1;
                }
            }
            databaseUpdates[`${prefix}/teams`] = teams;
            databaseUpdates[`${prefix}/results`] = results;
        } else if (variant === "individual") {
            const fencers: Record<ID, ITournamentEventFencer> = {};
            const results: Record<ID, PoolResults> = {};
            for (let i = 0; i < teamsBySeeding.length; i++) {
                const fencer = teamsBySeeding[i];
                const status = poolOptions.exempt.includes(fencer.id)
                    ? SeedStatus.Exempt
                    : poolOptions.eliminated.includes(fencer.id)
                      ? SeedStatus.Eliminated
                      : SeedStatus.Advanced;
                fencers[fencer.id] = fencer as ITournamentEventFencer;
                results[fencer.id] = { v: 0, m: 0, ts: 0, tr: 0, status };
                if (status !== SeedStatus.Advanced) {
                    results[fencer.id].place = i + 1;
                }
            }
            databaseUpdates[`${prefix}/fencers`] = fencers;
            databaseUpdates[`${prefix}/results`] = results;
        }

        for (let i = 0; i < poolOptions.pools.length; i++) {
            const indicesInPool = poolOptions.pools[i];
            const poolID = this.database.ref(`${this.PREFIX}/tournament-pools`).push().key!;
            const poolPrefix = `${this.PREFIX}/tournament-pools/${poolID}`;
            databaseUpdates[`${poolPrefix}/id`] = poolID;
            databaseUpdates[`${poolPrefix}/updatedAt`] = Date.now();
            databaseUpdates[`${poolPrefix}/variant`] = variant;
            databaseUpdates[`${poolPrefix}/weapon`] = eventData.weapon;
            databaseUpdates[`${poolPrefix}/number`] = i + 1;
            databaseUpdates[`${poolPrefix}/roundId`] = roundID;
            databaseUpdates[`${poolPrefix}/eventId`] = eventID;
            databaseUpdates[`${prefix}/pools/${poolID}/id`] = poolID;
            databaseUpdates[`${prefix}/pools/${poolID}/completed`] = false;
            if (variant === "cba") {
                const teams: ITournamentEventTeam[] = [];
                const results: Record<ID, PoolResults> = {};
                for (const team of indicesInPool) {
                    const t = teamsBySeeding[team] as ITournamentEventTeam;
                    teams.push(t);
                    const status = poolOptions.exempt.includes(t.id)
                        ? SeedStatus.Exempt
                        : poolOptions.eliminated.includes(t.id)
                          ? SeedStatus.Eliminated
                          : SeedStatus.Advanced;
                    results[t.id] = { v: 0, m: 0, ts: 0, tr: 0, status };
                }
                databaseUpdates[`${poolPrefix}/teams`] = teams;

                const boutObj: ITournamentPoolCBA["bouts"] = {};
                for (let j = 0; j < indicesInPool.length; j++) {
                    for (let k = j + 1; k < indicesInPool.length; k++) {
                        if (!(j in boutObj)) boutObj[j] = {};
                        if (!(k in boutObj[j]))
                            boutObj[j][k] = {
                                C: {
                                    id: "",
                                    score1: 0,
                                    score2: 0,
                                    fencer1ID: "",
                                    fencer2ID: "",
                                    fencer1Name: "Unknown fencer",
                                    fencer2Name: "Unknown fencer"
                                },
                                B: {
                                    id: "",
                                    score1: 0,
                                    score2: 0,
                                    fencer1ID: "",
                                    fencer2ID: "",
                                    fencer1Name: "Unknown fencer",
                                    fencer2Name: "Unknown fencer"
                                },
                                A: {
                                    id: "",
                                    score1: 0,
                                    score2: 0,
                                    fencer1ID: "",
                                    fencer2ID: "",
                                    fencer1Name: "Unknown fencer",
                                    fencer2Name: "Unknown fencer"
                                }
                            };

                        const team1 = teamsBySeeding[indicesInPool[j]];
                        const team2 = teamsBySeeding[indicesInPool[k]];

                        const cUpdatesResult = await this.getDBUpdatesForBout(
                            true,
                            poolID,
                            eventData.weapon,
                            team1.id,
                            team2.id,
                            null,
                            null,
                            0
                        );
                        if (cUpdatesResult.status === "fail") return cUpdatesResult;
                        boutObj[j][k].C = {
                            id: cUpdatesResult.data.id,
                            score1: 0,
                            score2: 0,
                            fencer1ID: cUpdatesResult.data.fencer1ID,
                            fencer2ID: cUpdatesResult.data.fencer2ID,
                            fencer1Name: cUpdatesResult.data.fencer1Name,
                            fencer2Name: cUpdatesResult.data.fencer2Name
                        };
                        Object.assign(databaseUpdates, cUpdatesResult.data.updates);

                        const bUpdatesResult = await this.getDBUpdatesForBout(
                            true,
                            poolID,
                            eventData.weapon,
                            team1.id,
                            team2.id,
                            null,
                            null,
                            1
                        );
                        if (bUpdatesResult.status === "fail") return bUpdatesResult;
                        boutObj[j][k].B = {
                            id: bUpdatesResult.data.id,
                            score1: 0,
                            score2: 0,
                            fencer1ID: bUpdatesResult.data.fencer1ID,
                            fencer2ID: bUpdatesResult.data.fencer2ID,
                            fencer1Name: bUpdatesResult.data.fencer1Name,
                            fencer2Name: bUpdatesResult.data.fencer2Name
                        };
                        Object.assign(databaseUpdates, bUpdatesResult.data.updates);

                        const aUpdatesResult = await this.getDBUpdatesForBout(
                            true,
                            poolID,
                            eventData.weapon,
                            team1.id,
                            team2.id,
                            null,
                            null,
                            2
                        );
                        if (aUpdatesResult.status === "fail") return aUpdatesResult;
                        boutObj[j][k].A = {
                            id: aUpdatesResult.data.id,
                            score1: 0,
                            score2: 0,
                            fencer1ID: aUpdatesResult.data.fencer1ID,
                            fencer2ID: aUpdatesResult.data.fencer2ID,
                            fencer1Name: aUpdatesResult.data.fencer1Name,
                            fencer2Name: aUpdatesResult.data.fencer2Name
                        };
                        Object.assign(databaseUpdates, aUpdatesResult.data.updates);
                    }
                }

                databaseUpdates[`${poolPrefix}/bouts`] = boutObj;
            }
        }

        return new DBSuccess(databaseUpdates);
    }

    public async addPoolRoundToEvent(
        eventID: string,
        variant: "cba" | "individual",
        poolOptions: { pools: number[][]; eliminated: ID[]; exempt: ID[] }
    ): AsyncDBResult<boolean> {
        try {
            const databaseUpdatesResult = await this.getUpdatesToAddPoolRoundToEvent(eventID, variant, poolOptions);
            if (databaseUpdatesResult.status === "fail") return databaseUpdatesResult;
            await this.database.ref().update(databaseUpdatesResult.data);
            return new DBSuccess(true);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async getDbUpdatesToDeleteBout(boutID: string): AsyncDBResult<Record<string, unknown>> {
        try {
            const databaseUpdates: Record<string, unknown> = {};

            const boutResult = await this.getBout(boutID);
            if (boutResult.status === "fail") return boutResult;
            const boutData = boutResult.data;

            databaseUpdates[`${this.PREFIX}/bouts/${boutID}`] = null;

            // Remove all the events
            for (const event of boutData.log || []) {
                if (!event.trim()) continue;
                databaseUpdates[`${this.PREFIX}/boutEvents/${event}`] = null;
            }

            // Remove fencer records
            const fencer1ID = boutData.fencer1.fencerInfo.id;
            const fencer2ID = boutData.fencer2.fencerInfo.id;
            if (fencer1ID) {
                databaseUpdates[`${this.PREFIX}/fencerRecords/${fencer1ID}/bouts/${boutData.id}`] = null;
                if (!fencer1ID.startsWith("writeIn")) {
                    const fencerResult = await this.getFencer(fencer1ID);
                    if (fencerResult.status === "fail") return fencerResult;
                    const fencerData = fencerResult.data;

                    const modifiedBouts = (fencerData.bouts || []).filter(l => l !== boutID);
                    databaseUpdates[`${this.PREFIX}/fencers/${fencerData.id}/bouts`] = modifiedBouts;
                }
            }
            if (fencer2ID) {
                databaseUpdates[`${this.PREFIX}/fencerRecords/${fencer2ID}/bouts/${boutData.id}`] = null;
                if (!fencer2ID.startsWith("writeIn")) {
                    const fencerResult = await this.getFencer(fencer2ID);
                    if (fencerResult.status === "fail") return fencerResult;
                    const fencerData = fencerResult.data;

                    const modifiedBouts = (fencerData.bouts || []).filter(l => l !== boutID);
                    databaseUpdates[`${this.PREFIX}/fencers/${fencerData.id}/bouts`] = modifiedBouts;
                }
            }

            // Remove from college event results if this bout is part of a college event
            if (boutData.dualMeetId) {
                const meetResult = await this.getDualMeetRaw(boutData.dualMeetId, undefined, true);
                if (meetResult.status === "success" && meetResult.data.eventID) {
                    const meet = meetResult.data;

                    // Remove fencer1 from college event results
                    if (fencer1ID && !fencer1ID.startsWith("writeIn")) {
                        const team1 = await this.getTeam(meet.team1ID!, { forceCache: true });
                        if (team1.status === "success") {
                            const gender1: "mens" | "womens" = team1.data.boysAndGirlsTeam === "boys" ? "mens" : "womens";
                            databaseUpdates[`${this.PREFIX}/eventResults/${meet.eventID}/updatedAt`] = Date.now();
                            databaseUpdates[
                                `${this.PREFIX}/eventResults/${meet.eventID}/${gender1}/${fencer1ID}/bouts/${boutData.weapon}/${boutID}`
                            ] = null;
                        }
                    }

                    // Remove fencer2 from college event results
                    if (fencer2ID && !fencer2ID.startsWith("writeIn")) {
                        const team2 = await this.getTeam(meet.team2ID!, { forceCache: true });
                        if (team2.status === "success") {
                            const gender2: "mens" | "womens" = team2.data.boysAndGirlsTeam === "boys" ? "mens" : "womens";
                            databaseUpdates[`${this.PREFIX}/eventResults/${meet.eventID}/updatedAt`] = Date.now();
                            databaseUpdates[
                                `${this.PREFIX}/eventResults/${meet.eventID}/${gender2}/${fencer2ID}/bouts/${boutData.weapon}/${boutID}`
                            ] = null;
                        }
                    }
                }
            }

            return new DBSuccess(databaseUpdates);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async getDbUpdatesToDeletePoolFromRound(roundID: string, poolID: string): AsyncDBResult<Record<string, unknown>> {
        try {
            const poolResult = await this.getTournamentPool(poolID);
            if (poolResult.status === "fail") return poolResult;
            const poolData = poolResult.data;

            const databaseUpdates: Record<string, unknown> = {};
            databaseUpdates[`${this.PREFIX}/tournament-pools/${poolID}`] = null;
            databaseUpdates[`${this.PREFIX}/tournament-rounds/${roundID}/pools/${poolID}`] = null;
            databaseUpdates[`${this.PREFIX}/tournament-rounds/${roundID}/updatedAt`] = Date.now();
            const boutIDs = Object.values(poolData.bouts)
                .flatMap(l => Object.values(l))
                .flatMap((l: ITournamentPool["bouts"][number][number]) =>
                    "id" in l ? [l.id] : Object.values(l).map(l => l.id)
                ) as string[];

            const boutDatabaseUpdates = await Promise.all(boutIDs.map(l => this.getDbUpdatesToDeleteBout(l)));
            for (const result of boutDatabaseUpdates) {
                if (result.status === "fail") return result;
                Object.assign(databaseUpdates, result.data);
            }

            return new DBSuccess(databaseUpdates);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async deletePoolFromRound(roundID: string, poolID: string): AsyncDBResult<boolean> {
        try {
            const databaseUpdates = await this.getDbUpdatesToDeletePoolFromRound(roundID, poolID);
            if (databaseUpdates.status === "fail") return databaseUpdates;
            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async getDbUpdatesToDeleteRoundFromEvent(eventID: string, roundID: string): AsyncDBResult<Record<string, unknown>> {
        try {
            const eventResult = await this.getTournamentEvent(eventID, { preferCache: true });
            if (eventResult.status === "fail") return eventResult;
            const eventData = eventResult.data;

            const roundResult = await this.getTournamentRound(roundID);
            if (roundResult.status === "fail") return roundResult;
            const roundData = roundResult.data;
            const roundIdx = eventData.rounds.findIndex(l => l.id === roundID);

            const databaseUpdates: Record<string, unknown> = {};
            databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/updatedAt`] = Date.now();
            databaseUpdates[`${this.PREFIX}/tournament-events/${eventID}/rounds/${roundIdx}`] = null;
            databaseUpdates[`${this.PREFIX}/tournament-rounds/${roundID}`] = null;
            const poolIDs = Object.keys(roundData.pools);

            const roundDatabaseUpdates = await Promise.all(poolIDs.map(l => this.getDbUpdatesToDeletePoolFromRound(roundID, l)));
            for (const result of roundDatabaseUpdates) {
                if (result.status === "fail") return result;
                Object.assign(databaseUpdates, result.data);
            }

            const keys = Object.keys(databaseUpdates);
            for (const key of keys) {
                if (key.startsWith(`${this.PREFIX}/tournament-rounds/${roundID}/`)) {
                    delete databaseUpdates[key];
                }
            }

            await this.database.ref().update(databaseUpdates);

            return new DBSuccess(databaseUpdates);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async deleteRoundFromEvent(eventID: string, roundID: string): AsyncDBResult<boolean> {
        try {
            const databaseUpdates = await this.getDbUpdatesToDeleteRoundFromEvent(eventID, roundID);
            if (databaseUpdates.status === "fail") return databaseUpdates;
            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async deleteEventFromTournament(tournamentID: string | ITournament, eventID: string | ITournamentEvent): AsyncDBResult<boolean> {
        try {
            let tournament = tournamentID;
            if (typeof tournament === "string") {
                const tournamentResult = await this.getTournament(tournament);
                if (tournamentResult.status === "fail") return tournamentResult;
                tournament = tournamentResult.data;
            }

            let event = eventID;
            if (typeof event === "string") {
                const eventResult = await this.getTournamentEvent(event);
                if (eventResult.status === "fail") return eventResult;
                event = eventResult.data;
            }

            const databaseUpdates: Record<string, unknown> = {};
            databaseUpdates[`${this.PREFIX}/tournaments/${tournament.id}/updatedAt`] = Date.now();
            databaseUpdates[`${this.PREFIX}/tournaments/${tournament.id}/events/${event.id}`] = null;

            for (const round of event.rounds) {
                const deleteRoundUpdates = await this.getDbUpdatesToDeleteRoundFromEvent(event.id, round.id);
                if (deleteRoundUpdates.status === "fail") return deleteRoundUpdates;
                for (const i in deleteRoundUpdates) {
                    if (!i.startsWith(`${this.PREFIX}/tournament-events/${event.id}/`)) {
                        databaseUpdates[i] = deleteRoundUpdates[i];
                    }
                }
            }

            databaseUpdates[`${this.PREFIX}/tournament-events/${event.id}`] = null;

            await this.database.ref().update(databaseUpdates);
            delete this.tournamentEvents[event.id];
            delete this.tournaments[tournament.id].events[event.id];
            this.cache.queueSave("tournaments");
            this.cache.queueSave("tournamentEvents");

            return new DBSuccess(true);
        } catch (e: unknown) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async deleteTournament(tournamentID: string): AsyncDBResult<boolean> {
        try {
            console.time("Delete tournament");
            const databaseUpdates: Record<string, unknown> = {};

            const tournamentResult = await this.getTournament(tournamentID);
            if (tournamentResult.status === "fail") return tournamentResult;
            const tournamentData = tournamentResult.data;
            databaseUpdates[`${this.PREFIX}/tournaments/${tournamentID}`] = null;

            for (const eventID in tournamentData.events) {
                const eventResult = await this.getTournamentEvent(eventID);
                if (eventResult.status === "fail") continue;
                const eventData = eventResult.data;
                databaseUpdates[`${this.PREFIX}/tournament-events/${eventData.id}`] = null;

                const roundPromises: Promise<unknown>[] = eventData.rounds.map(async (round: ITournamentEvent["rounds"][number]) => {
                    const roundID = round.id;
                    const roundResult = await this.getTournamentRound(roundID);
                    if (roundResult.status === "fail") return;
                    const roundData = roundResult.data;
                    databaseUpdates[`${this.PREFIX}/tournament-rounds/${roundID}`] = null;

                    for (const poolID in roundData.pools) {
                        const poolResult = await this.getTournamentPool(poolID);
                        if (poolResult.status === "fail") continue;
                        const poolData = poolResult.data;
                        databaseUpdates[`${this.PREFIX}/tournament-pools/${poolID}`] = null;

                        const boutIDs = Object.values(poolData.bouts)
                            .flatMap(l => Object.values(l))
                            .flatMap((l: ITournamentPool["bouts"][number][number]) =>
                                "id" in l ? [l.id] : Object.values(l).map(l => l.id)
                            ) as string[];

                        const boutDatabaseUpdates = await Promise.all(boutIDs.map(l => this.getDbUpdatesToDeleteBout(l)));
                        for (const result of boutDatabaseUpdates) {
                            if (result.status === "fail") return result;
                            Object.assign(databaseUpdates, result.data);
                        }
                    }
                    return;
                });
                await Promise.all(roundPromises);
            }
            console.timeEnd("Delete tournament");
            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async publishTournament(tournamentID: string): AsyncDBResult<boolean> {
        try {
            const userInfo = await this.getCurrentUserInfo();
            if (userInfo.status === "fail") return userInfo;
            const user = userInfo.data;

            const databaseUpdates: Record<string, unknown> = {};
            databaseUpdates[`${this.PREFIX}/tournaments/${tournamentID}/publishedAt`] = Date.now();
            databaseUpdates[`${this.PREFIX}/tournaments/${tournamentID}/publishedBy`] = user.id;

            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e: unknown) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async unpublishTournament(tournamentID: string): AsyncDBResult<boolean> {
        try {
            const userInfo = await this.getCurrentUserInfo();
            if (userInfo.status === "fail") return userInfo;
            const user = userInfo.data;

            const databaseUpdates: Record<string, unknown> = {};
            databaseUpdates[`${this.PREFIX}/tournaments/${tournamentID}/publishedAt`] = null;
            databaseUpdates[`${this.PREFIX}/tournaments/${tournamentID}/publishedBy`] = user.id;

            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e: unknown) {
            console.error(e);
            return new DBError((e as Error).message);
        }
    }

    public async getDbUpdatesForRoundResultsFromPool(roundID: string, poolData: ITournamentPool) {
        const databaseUpdates: Record<string, unknown> = {};

        const roundResult = await this.getTournamentRound(roundID, { preferCache: true });
        if (roundResult.status === "fail") return roundResult;
        const roundData = roundResult.data;

        if (roundData.variant === "individual" || poolData.variant === "individual")
            return new DBError("Individual tournaments currently unsupported");

        databaseUpdates[`${this.PREFIX}/tournament-rounds/${roundID}/updatedAt`] = Date.now();

        const results = computePoolResults(poolData);

        for (let i = 0; i < poolData.teams.length; i++) {
            databaseUpdates[`${this.PREFIX}/tournament-rounds/${roundID}/results/${poolData.teams[i].id}`] = results[i];
        }

        return new DBSuccess(databaseUpdates);
    }

    // #endregion

    // #region Repair methods

    public async repairAllFencerRecords() {
        const fencers = (await this.database.ref(`${this.PREFIX}/fencers`).once("value")).val() as Record<string, IExistingFencer>;
        await Promise.all(Object.values(fencers).map(l => this.migrateFencerRecords(l)));
    }

    public async repairTeamFencersList(teamIDOrData: string | ITeam) {
        let team = teamIDOrData;
        if (typeof team === "string") {
            const teamResult = await this.getTeam(team);
            if (teamResult.status === "fail") return teamResult;
            team = teamResult.data;
        }
        const existingFencers = team.fencers;
        const rosterFencers = Object.values(team.roster)
            .flatMap(l => Object.values(l))
            .flatMap(l => Object.keys(l));
        const totalFencers = [...new Set([...existingFencers, ...rosterFencers])].filter(l => Boolean(l));
        await this.database.ref(`${this.PREFIX}/teams/${team.id}/fencers`).set(totalFencers);
        return new DBSuccess(true);
    }

    public async repairAllTeams() {
        const teams = await this.getTeamList();
        if (teams.status === "fail") return teams;
        for (const team of Object.values(teams.data)) {
            await this.repairTeamFencersList(team);
        }
        return new DBSuccess(true);
    }

    public async repairTeamNameFieldsInDualMeetUpdates(dualMeetIDOrData: string | IDualMeet_DB) {
        let meet = dualMeetIDOrData;
        if (typeof meet === "string") {
            const meetResult = await this.getDualMeet(meet);
            if (meetResult.status === "fail") return meetResult;
            meet = meetResult.data;
        }
        const team1Res = meet.team1ID ? await this.getTeam(meet.team1ID, { forceCache: true }) : null;
        const team2Res = meet.team2ID ? await this.getTeam(meet.team2ID, { forceCache: true }) : null;
        if (team1Res?.status !== "success") return team1Res;
        if (team2Res?.status !== "success") return team2Res;
        const updates = {
            [`${this.PREFIX}/dualMeets/${meet.id}/team1ID`]: team1Res.data.id,
            [`${this.PREFIX}/dualMeets/${meet.id}/team2ID`]: team2Res.data.id,
            [`${this.PREFIX}/dualMeets/${meet.id}/team1Name`]: team1Res.data.name,
            [`${this.PREFIX}/dualMeets/${meet.id}/team2Name`]: team2Res.data.name
        };
        return updates;
    }

    public async repairTeamNameFieldsInDualMeets() {
        const v = await this.database.ref(`${this.PREFIX}/dualMeets`).once("value");
        const meets = (v.val() || {}) as Record<string, IDualMeet_DB>;
        for (const meetID in meets) {
            this.dualMeets[meetID] = meets[meetID];
        }
        const updates: Record<string, unknown> = {};
        for (const meet of Object.values(meets)) {
            const newUpdates = await this.repairTeamNameFieldsInDualMeetUpdates(meet);
            for (const i in newUpdates) {
                updates[i] = newUpdates[i];
            }
        }
        await this.database.ref().update(updates);
        return new DBSuccess(true);
    }

    public async repairFencerGenders(): AsyncDBResult<boolean> {
        const fencersResult = await this.getAllFencers();
        if (fencersResult.status === "fail") return fencersResult;
        const fencers = fencersResult.data;

        const databaseUpdates: Record<string, unknown> = {};
        const fencersWithTeams = Object.values(fencers).filter(l => l.teams.length);
        const teams = await Promise.all(fencersWithTeams.map(f => this.getTeam(f.teams[0])));
        for (let i = 0; i < fencersWithTeams.length; i++) {
            const team = teams[i];
            if (team.status === "fail") continue;
            databaseUpdates[`${this.PREFIX}/fencers/${fencersWithTeams[i].id}/gender`] = GROWN_UP_STR[team.data.boysAndGirlsTeam];
        }
        await this.database.ref().update(databaseUpdates);
        return new DBSuccess(true);
    }

    public async repairFencerGender(fencerID: string): AsyncDBResult<"man" | "woman" | "both"> {
        const fencerResult = await this.getFencer(fencerID);
        if (fencerResult.status === "fail") return fencerResult;
        const fencer = fencerResult.data;
        if (!fencer.teams.length) return new DBError("Fencer has no teams");

        const teamResult = await this.getTeam(fencer.teams[0]);
        if (teamResult.status === "fail") return teamResult;
        const team = teamResult.data;

        await this.database.ref(`${this.PREFIX}/fencers/${fencerID}/gender`).set(GROWN_UP_STR[team.boysAndGirlsTeam]);
        return new DBSuccess(GROWN_UP_STR[team.boysAndGirlsTeam]);
    }

    public async repairDualMeetsDirectory() {
        const dualMeets: Record<string, IDualMeet_DB> = await refOnce(`${this.PREFIX}/dualMeets`);
        const obj: Record<string, IDirectoryDualMeet> = {};
        for (const id in dualMeets) {
            obj[id] = { name: dualMeets[id].name, startedAt: dualMeets[id].startedAt };
        }
        await this.database.ref(`${this.PREFIX}/dualMeetsDirectory`).set(obj);
    }

    public async repairTeamDirectory() {
        const teams: Record<string, ITeam> = await refOnce(`${this.PREFIX}/teams`);
        const obj: Record<string, IDirectoryTeam> = {};
        for (const id in teams) {
            const team = teams[id];
            if (!team.orgID) {
                console.log(`Team ${team.name} (${id}) has no orgID, skipping...`);
                continue;
            }
            obj[id] = {
                name: team.name,
                abbreviation: team.abbreviation!,
                orgID: team.orgID,
                published: team.published,
                gender: team.boysAndGirlsTeam
            };
        }

        await this.database.ref(`${this.PREFIX}/teamDirectory`).set(obj);
        await this.database.ref(`${this.PREFIX}/teamDirectoryUpdatedAt`).set(Date.now());
        return new DBSuccess(true);
    }

    public async repairFencerInvites() {
        const repairFencerInvites = this.functions.httpsCallable("repairFencerInvites");
        const result = await repairFencerInvites({ prefix: this.PREFIX });
        return result.data as DBResult<boolean>;
    }

    public async repairTeamScorekeepers(): AsyncDBResult<boolean> {
        const usersResult = await this.getUserList();
        if (usersResult.status === "fail") return usersResult;
        const users = usersResult.data;

        const teamsResult = await this.getTeamList();
        if (teamsResult.status === "fail") return teamsResult;
        const teams = teamsResult.data;

        const databaseUpdates: Record<string, unknown> = {};

        for (const team of Object.values(teams)) {
            const updatedScorekeepers: Record<string, LinkedTeamType> = {};

            // For each user, check if they have linked fencers that are in this team
            for (const user of Object.values(users)) {
                for (const linkedFencerId of user.linkedFencerIds || []) {
                    // Check if this fencer is in the current team
                    if (team.fencers.includes(linkedFencerId)) {
                        const currentScorekeeperValue = team.linkedUsers[user.id] || 0;
                        const newValue = currentScorekeeperValue | LinkedTeamType.Scorekeeper;
                        updatedScorekeepers[user.id] = newValue;
                        databaseUpdates[`${this.COMMON_PREFIX}/users/${user.id}/linkedTeams/${team.id}`] = newValue;
                    }
                }
            }

            // Only update if there are changes
            if (Object.keys(updatedScorekeepers).length > 0) {
                databaseUpdates[`${this.PREFIX}/teams/${team.id}/linkedUsers`] = updatedScorekeepers;
            }
        }

        if (Object.keys(databaseUpdates).length > 0) {
            console.log(databaseUpdates);
            await this.database.ref().update(databaseUpdates);
        }

        return new DBSuccess(true);
    }

    public async repairCollegeEventResults(eventID: string): AsyncDBResult<boolean> {
        try {
            const eventResult = await this.getCollegeEvent(eventID, { preferCache: true });
            if (eventResult.status === "fail") return eventResult;
            const event = eventResult.data;

            const mensMeets = await Promise.all(
                event.mensRounds.flatMap(l => l.meets).map(l => this.getDualMeetRaw(l.id, undefined, true))
            );
            const womensMeets = await Promise.all(
                event.womensRounds.flatMap(l => l.meets).map(l => this.getDualMeetRaw(l.id, undefined, true))
            );
            const mensTeamsResults = await Promise.all(event.mensTeams.map(l => this.getTeam(l.id, { forceCache: true })));
            const womensTeamsResults = await Promise.all(event.womensTeams.map(l => this.getTeam(l.id, { forceCache: true })));
            const mensTeams = mensTeamsResults.filter(l => l.status === "success").map(l => l.data);
            const womensTeams = womensTeamsResults.filter(l => l.status === "success").map(l => l.data);

            // Cache for fencer lookups to avoid repeated database calls
            const fencerCache = new Map<string, IExistingFencer>();

            const results: ICollegeEventResults = {
                updatedAt: Date.now(),
                mens: {},
                womens: {},
                mensTeams: {},
                womensTeams: {}
            };

            const getFencerName = async (fencerID: string, boutFencerInfo: { firstName?: string; lastName?: string }): Promise<string> => {
                // Try to get name from bout first
                const firstName = boutFencerInfo.firstName;
                const lastName = boutFencerInfo.lastName;
                if (firstName && lastName) {
                    return `${firstName} ${lastName}`;
                }
                // Fall back to fencer record
                if (!fencerCache.has(fencerID)) {
                    const fencerResult = await this.getFencer(fencerID, { forceCache: true });
                    if (fencerResult.status === "success") {
                        fencerCache.set(fencerID, fencerResult.data);
                    }
                }
                const fencerRecord = fencerCache.get(fencerID);
                if (fencerRecord) {
                    const name = `${fencerRecord.firstName || ""} ${fencerRecord.lastName || ""}`.trim();
                    return name || fencerID;
                }
                // Last resort: use ID
                return fencerID;
            };

            for (const gender of ["mens", "womens"]) {
                const meets = gender === "mens" ? mensMeets : womensMeets;
                const teams = gender === "mens" ? mensTeams : womensTeams;
                const otherTeams = gender === "mens" ? womensTeams : mensTeams;
                
                for (const meet of meets) {
                    if (meet.status === "fail") continue;
                    const meetData = meet.data;
                    
                    const team1 = teams.find(l => l.id === meetData.team1ID) || otherTeams.find(l => l.id === meetData.team1ID);
                    const team2 = teams.find(l => l.id === meetData.team2ID) || otherTeams.find(l => l.id === meetData.team2ID);

                    if (!team1 || !team2) continue;

                    const bouts = await Promise.all(meetData.bouts.map(l => this.getBout(l.id, undefined, true)));
                    const boutsData = bouts.filter(l => l.status === "success").map(l => l.data);

                    for (const bout of boutsData) {
                        // Use { team: true } to include all bouts with winners (including forfeits) for team results
                        // Individual results will only be recorded for non-write-in fencers, but team results need all bouts
                        const winner = boutWinner(bout, { team: true });
                        if (!winner) continue;

                        const fencer1 = bout.fencer1.fencerInfo.id;
                        const fencer2 = bout.fencer2.fencerInfo.id;

                        // Record individual results for non-write-in fencers
                        // This ensures team results can be calculated even when opponents are write-ins
                        if (fencer1 && !fencer1.startsWith("writeIn")) {
                            if (!results[gender][fencer1]) {
                                results[gender][fencer1] = {
                                    teamID: team1.id,
                                    name: await getFencerName(fencer1, bout.fencer1.fencerInfo),
                                    bouts: {
                                        Sabre: {},
                                        Epee: {},
                                        Foil: {}
                                    }
                                };
                            }
                            results[gender as "mens" | "womens"][fencer1].bouts[bout.weapon][bout.id] = {
                                opposingTeamID: team2.id,
                                winner: winner === BoutSide.Fencer1 ? 1 : 2,
                                ts: bout.fencer1.score,
                                tr: bout.fencer2.score
                            };
                        }

                        if (fencer2 && !fencer2.startsWith("writeIn")) {
                            if (!results[gender][fencer2]) {
                                results[gender][fencer2] = {
                                    teamID: team2.id,
                                    name: await getFencerName(fencer2, bout.fencer2.fencerInfo),
                                    bouts: {
                                        Sabre: {},
                                        Epee: {},
                                        Foil: {}
                                    }
                                };
                            }
                            results[gender as "mens" | "womens"][fencer2].bouts[bout.weapon][bout.id] = {
                                opposingTeamID: team1.id,
                                winner: winner === BoutSide.Fencer2 ? 1 : 2,
                                ts: bout.fencer2.score,
                                tr: bout.fencer1.score
                            };
                        }

                        // Record team results
                        const teamResultsKey = gender === "mens" ? "mensTeams" : "womensTeams";
                        
                        // Team 1 results
                        if (!results[teamResultsKey][team1.id]) {
                            results[teamResultsKey][team1.id] = {
                                teamID: team1.id,
                                teamName: team1.name,
                                bouts: {
                                    Sabre: {},
                                    Epee: {},
                                    Foil: {}
                                }
                            };
                        }
                        results[teamResultsKey][team1.id].bouts[bout.weapon][bout.id] = {
                            opposingTeamID: team2.id,
                            winner: winner === BoutSide.Fencer1 ? 1 : 2,
                            ts: bout.fencer1.score,
                            tr: bout.fencer2.score
                        };

                        // Team 2 results
                        if (!results[teamResultsKey][team2.id]) {
                            results[teamResultsKey][team2.id] = {
                                teamID: team2.id,
                                teamName: team2.name,
                                bouts: {
                                    Sabre: {},
                                    Epee: {},
                                    Foil: {}
                                }
                            };
                        }
                        results[teamResultsKey][team2.id].bouts[bout.weapon][bout.id] = {
                            opposingTeamID: team1.id,
                            winner: winner === BoutSide.Fencer2 ? 1 : 2,
                            ts: bout.fencer2.score,
                            tr: bout.fencer1.score
                        };
                    }
                }
            }

            const databaseUpdates: Record<string, unknown> = {};
            databaseUpdates[`${this.PREFIX}/eventResults/${eventID}`] = results;
            await this.database.ref().update(databaseUpdates);
            return new DBSuccess(true);
        } catch (e) {
            return new DBError((e as Error).message);
        }
    }

    public async repairTeamRosters() {
        const repairTeamRosters = this.functions.httpsCallable("repairTeamRosters");
        const result = await repairTeamRosters({ prefix: this.PREFIX });
        return result.data as DBResult<boolean>;
    }

    public async repairTeamSeasons(id: string) {
        const team = await this.getTeam(id);
        if (team.status === "fail") return team;
        const teamData = team.data;
        const seasons: Set<string> = new Set(teamData.seasons);
        for (const season of teamData.seasons) {
            seasons.add(season);
        }
        for (const season in teamData.roster) {
            seasons.add(season);
        }
        console.log(teamData);
        const seasonsArr = [...seasons];
        seasonsArr.sort((a, b) => a.localeCompare(b));
        const databaseUpdates: Record<string, unknown> = {};
        databaseUpdates[`${this.PREFIX}/teams/${id}/seasons`] = seasonsArr;
        console.log(databaseUpdates);
        await this.database.ref().update(databaseUpdates);
        return new DBSuccess(true);
    }

    // #endregion

    // #region Misc

    public getOnline(listener?: Listener<boolean>): Promise<boolean> {
        return new Promise(async res => {
            if (listener) {
                const l = v => {
                    const val = v.val();
                    listener && listener(val);
                    res(val);
                };
                this.listeners.set(listener, l);
                this.database.ref(".info/connected").on("value", l);
            } else {
                this.database.ref(".info/connected").once("value", v => void res(v.val()));
            }
        });
    }

    public stopListeningOnline(listener: Listener<boolean>) {
        if (this.listeners.has(listener)) {
            this.database.ref(".info/connected").off("value", this.listeners.get(listener));
        }
    }

    // #endregion

    // #region Notifications

    public async getNotificationsForTeam(teamId: string): AsyncDBResult<INotification[]> {
        try {
            const result = await refOnce(`${this.PREFIX}/notifications/teams/${teamId}`);
            if (!result) return new DBSuccess([]);
            return new DBSuccess(Object.values(result));
        } catch (e) {
            return new DBError((e as Error).message);
        }
    }

    public async acceptNotification(scope: "teams" | "users", teamId: string, id: string): AsyncDBResult<Record<string, unknown>> {
        try {
            const result = await this.functions.httpsCallable("acceptNotification")({ prefix: this.PREFIX, scope, teamId, id });
            return result.data;
        } catch (e) {
            return new DBError((e as Error).message);
        }
    }

    public async rejectNotification(scope: "teams" | "users", teamId: string, id: string): AsyncDBResult<Record<string, unknown>> {
        try {
            const result = await this.functions.httpsCallable("rejectNotification")({ prefix: this.PREFIX, scope, teamId, id });
            return result.data;
        } catch (e) {
            return new DBError((e as Error).message);
        }
    }

    public async requestCountMeet(dualMeetId: string, count: boolean): AsyncDBResult<boolean> {
        try {
            const meet = await this.getDualMeet(dualMeetId);
            if (meet.status === "fail") return meet;

            // Get current user's team
            const userInfo = await this.getCurrentUserInfo();
            if (userInfo.status === "fail") return userInfo;

            // Determine which team is the other team
            const otherTeamId = userInfo.data.teams.includes(meet.data.team1.id || "asdf") ? meet.data.team2.id : meet.data.team1.id;

            const name = count ? "requestCountDualMeet" : "requestUncountDualMeet";
            const result = await this.functions.httpsCallable(name)({
                prefix: this.PREFIX,
                userId: this.auth.currentUser?.uid,
                otherTeamId,
                dualMeetId,
                dualMeetName: meet.data.name,
                dualMeetDate: meet.data.startedAt
            });
            return new DBSuccess(result.data);
        } catch (e) {
            return new DBError((e as Error).message);
        }
    }

    // #endregion
}

class COMMON_DB_BASE {
    public PREFIX = "/common";
    private database = fire.database();
    private auth = fire.auth();

    public async createUser(uid: string, firstName: string, lastName: string, email: string): AsyncDBResult<IUser> {
        try {
            const now = Date.now();
            const newUser: IUser = {
                id: uid,
                firstName,
                lastName,
                email,
                createdAt: now,
                updatedAt: now,
                verificationPin: getRandomInt(10000, 99999),
                flags: 1,
                teams: [],
                managingTeams: [],
                linkedFencerIds: []
            };
            await this.database.ref(`${this.PREFIX}/users/${uid}`).set(newUser);
            return new DBSuccess(newUser);
        } catch (e: unknown) {
            return new DBError(ERROR_UNKNOWN);
        }
    }

    public async getCurrentUserInfo(listener?: Listener<DBResult<IUser>>): AsyncDBResult<IUser> {
        if (listener) {
            return new Promise(async res => {
                if (this.auth.currentUser?.uid) {
                    this.database.ref(`${this.PREFIX}/users/${this.auth.currentUser!.uid}`).on("value", queryResults => {
                        const val = queryResults.val();
                        const defaultedVal: IUser = { ...DEFAULT_USER, ...val };
                        defaultedVal.managingTeams = defaultedVal.managingTeams.filter(Boolean);
                        defaultedVal.teams = defaultedVal.teams.filter(Boolean);
                        const response = new DBSuccess(defaultedVal);
                        listener && listener(response);
                        res(response);
                    });
                } else {
                    const err = new DBError(ERROR_NOT_LOGGED_IN);
                    listener && listener(err);
                    res(err);
                }
            });
        } else {
            if (!this.auth.currentUser?.uid) return new DBError("You are not logged in!");
            const v = await this.database.ref(`${this.PREFIX}/users/${this.auth.currentUser!.uid}`).once("value");
            const val = v.val();
            const defaultedVal: IUser = { ...DEFAULT_USER, ...val };
            defaultedVal.managingTeams = defaultedVal.managingTeams.filter(Boolean);
            defaultedVal.teams = defaultedVal.teams.filter(Boolean);
            return new DBSuccess(defaultedVal);
        }
    }

    public getUserInfo(userId: string): AsyncDBResult<IUser> {
        return new Promise(async res => {
            this.database.ref(`${this.PREFIX}/users/${userId}`).once("value", queryResults => {
                const dbVal = queryResults.val();
                if (!dbVal) {
                    return res(new DBError(ERROR_USER_DOES_NOT_EXIST));
                }
                const val: IUser = { ...DEFAULT_USER, ...dbVal };
                val.teams = val.teams.filter(Boolean);
                res(new DBSuccess(val));
            });
        });
    }

    public getUserList(listener?: Listener<DBResult<Record<string, IUser>>>): AsyncDBResult<Record<string, IUser>> {
        return new Promise(async res => {
            try {
                this.database
                    .ref(`${this.PREFIX}/users`)
                    .orderByChild("lastName")
                    .once("value", queryResults => {
                        const val = queryResults.val();
                        for (const i in val) {
                            val[i] = { ...DEFAULT_USER, ...val[i] };
                        }
                        const response = new DBSuccess(val);
                        res(response);
                        listener && listener(response);
                    });
            } catch {
                res(new DBError(ERROR_UNKNOWN));
            }
        });
    }

    public stopListeningUserList() {
        removeRefListener(`${this.PREFIX}/users`);
    }
}

const COMMON_DB = new COMMON_DB_BASE();
